import npmApiTranslations from './de.api.npm.ts';
import pt9800ApiTranslations from './de.api.pt9800.ts';
import NonTranslatable from './non-translatable.ts';

import {
  generalTerm,
} from '@/constants/locales/de.shared.ts';
import {
  TValidateExpressionRouteResponse,
} from '@/generated/qmulus/axios/signal-builder.ts';
import {
  components,
} from '@/generated/qmulus/openapi.ts';
import {
  EAppGroupSemanticDepartment, EAppGroupSemanticTopic, EAppId,
} from '@/types/app-type.ts';
import {
  EAssetProfileCombinator,
} from '@/types/asset-profile-combinator.ts';
import {
  EAssetProfileStatus,
} from '@/types/asset-profile-status.ts';
import {
  EHierarchy,
} from '@/types/asset-tree.ts';
import {
  EAuditResultType,
} from '@/types/audit-result.ts';
import {
  ECalendarEventType,
  ECalendarPeriod,
  EMaintenancePriority, EMaintenanceRecurrenceEndCondition,
  EMaintenanceRecurrenceMonthDayDimension,
  EMaintenanceRecurrenceMonthDayValue,
  EMaintenanceState,
} from '@/types/calendar.ts';
import {
  EChamberType, EHeatTreatmentProcessType,
} from '@/types/chamber.ts';
import {
  TFunctionCategory,
  TFunctionDefinition,
  TOperatorDefinition,
} from '@/types/function.ts';
import {
  TJobStatus,
} from '@/types/job-status.ts';
import {
  EBlobMeaning,
  EConsumptionMeaningMap, EJobFeatureMeaningMap,
  EPhysicalPropertyMeaningMap,
} from '@/types/meaning-type.ts';
import {
  EDataFormat,
} from '@/types/metadata.ts';
import {
  EModelLifecycleState,
} from '@/types/model-builder.ts';
import {
  ENotificationChannelType,
} from '@/types/notification-channel-type.ts';
import {
  ESubscriptionTreeLabel,
} from '@/types/notification.ts';
import {
  EObservationType,
} from '@/types/observation-type.ts';
import {
  ETimeseriesMeaning,
  TComponentKeys,
  TComponentValue,
  TComponentsSchema,
} from '@/types/openapi-type.ts';
import {
  TOrderStatus,
} from '@/types/order-status.ts';
import {
  EPhysicalDimension,
} from '@/types/physical-dimension.ts';
import {
  TQmulusDashboardWidgetSharedProps, TQmulusDashboardWidgetType,
} from '@/types/qmulus-dashboard.ts';
import {
  TReportBlockId,
} from '@/types/report-block.ts';
import {
  EScaleOption,
} from '@/types/scale.ts';
import {
  ESimulatorAMS275910CompoundLayerClass,
  ESimulatorAMS275910SteelClass,
  ESimulatorAMS275912PorosityClass,
  ESimulatorAMS275912SteelClass,
  ESimulatorPreTreatmentType,
  ESimulatorRecipeSimulationValuesType,
  ESimulatorRecipeType,
  ESimulatorSimulationPartShape,
} from '@/types/simulator.ts';
import {
  EUnitOfMeasurement,
} from '@/types/unit-of-measurement.ts';
import {
  sinkExpression,
  sinkVariable,
} from '@/utils/expression.ts';

const SparePart: Partial<TComponentKeys<'SparePart'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  manufacturerId: generalTerm.manufacturer,
  manufacturerDisplayName: generalTerm.manufacturer,
};

const HeatTreatmentCapability: Partial<TComponentKeys<'HeatTreatmentCapability'>> = {
  heatTreatmentProcess: generalTerm.heatTreatmentProcess,
  preference: generalTerm.preference,
};

const UnitOfMeasurementDisplayName: Record<EUnitOfMeasurement, string> = {
  [EUnitOfMeasurement.AMPERE]: 'Ampere',
  [EUnitOfMeasurement.ATMOSPHERE]: 'Atmosphäre',
  [EUnitOfMeasurement.BAR]: 'Bar',
  [EUnitOfMeasurement.BAR_PER_MINUTE]: 'Bar pro Minute',
  [EUnitOfMeasurement.BRINELL_HARDNESS]: 'Brinellhärte',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_1500_10]: 'Brinellhärte 10/1500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_1500_15]: 'Brinellhärte 10/1500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_3000_10]: 'Brinellhärte 10/3000/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_3000_15]: 'Brinellhärte 10/3000/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_500_10]: 'Brinellhärte 10/500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_10_500_15]: 'Brinellhärte 10/500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_1500_10]: 'Brinellhärte 6/1500/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_1500_15]: 'Brinellhärte 6/1500/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_3000_10]: 'Brinellhärte 6/3000/10',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_3000_15]: 'Brinellhärte 6/3000/15',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_500_10]: 'Brinellhärte 6/500/10/',
  [EUnitOfMeasurement.BRINELL_HARDNESS_6_500_15]: 'Brinellhärte 6/500/15',
  [EUnitOfMeasurement.BTU]: 'Britische Wärmeeinheit',
  [EUnitOfMeasurement.BTU_PER_HOUR]: 'Britische Wärmeeinheit pro Stunde',
  [EUnitOfMeasurement.BTU_PER_MINUTE]: 'Britische Wärmeeinheit pro Minute',
  [EUnitOfMeasurement.CENTIMETRE_PER_MINUTE]: 'Zentimeter pro Minute',
  [EUnitOfMeasurement.CENTIMETRE_PER_SECOND]: 'Zentimeter pro Sekunde',
  [EUnitOfMeasurement.CUBIC_CENTIMETRE_PER_MINUTE]: 'Kubikzentimeter pro Minute',
  [EUnitOfMeasurement.CUBIC_FEET]: 'Kubikfuß',
  [EUnitOfMeasurement.CUBIC_FEET_PER_HOUR]: 'Kubikfuß pro Stunde',
  [EUnitOfMeasurement.CUBIC_FEET_PER_MINUTE]: 'Kubikfuß pro Minute',
  [EUnitOfMeasurement.CUBIC_METRES]: 'Kubikmeter',
  [EUnitOfMeasurement.CUBIC_METRE_PER_HOUR]: 'Kubikmeter pro Stunde',
  [EUnitOfMeasurement.CUBIC_METRE_PER_MINUTE]: 'Kubikmeter pro Minute',
  [EUnitOfMeasurement.CUBIC_METRE_PER_SECOND]: 'Kubikmeter pro Sekunde',
  [EUnitOfMeasurement.DAY]: 'Tag',
  [EUnitOfMeasurement.DEGREES_CELSIUS]: 'Grad Celsius',
  [EUnitOfMeasurement.DEGREES_CELSIUS_PER_MINUTE]: 'Grad Celsius pro Minute',
  [EUnitOfMeasurement.DEGREES_FAHRENHEIT]: 'Grad Fahrenheit',
  [EUnitOfMeasurement.DEGREES_FAHRENHEIT_PER_MINUTE]: 'Grad Fahrenheit pro Minute',
  [EUnitOfMeasurement.DEGREES_KELVIN]: 'Grad Kelvin',
  [EUnitOfMeasurement.DELTA_DEGREES_CELSIUS]: 'Delta Grad Celsius',
  [EUnitOfMeasurement.DELTA_DEGREES_FAHRENHEIT]: 'Delta Grad Fahrenheit',
  [EUnitOfMeasurement.DELTA_DEGREES_KELVIN]: 'Delta Grad Kelvin',
  [EUnitOfMeasurement.FEET_OF_WATER_COLUMN]: 'Fuß der Wassersäule',
  [EUnitOfMeasurement.FEET_PER_MINUTE]: 'Fuß pro Minute',
  [EUnitOfMeasurement.FEET_PER_SECOND]: 'Fuß pro Sekunde',
  [EUnitOfMeasurement.GALLONS_PER_HOUR]: 'Gallonen pro Stunde',
  [EUnitOfMeasurement.GALLONS_PER_MINUTE]: 'Gallonen pro Minute',
  [EUnitOfMeasurement.GRAM]: 'Gramm',
  [EUnitOfMeasurement.GRAM_PER_SECOND]: 'Gram pro Sekunde',
  [EUnitOfMeasurement.HECTO_PASCAL]: 'Hektopascal',
  [EUnitOfMeasurement.HERTZ]: 'Hertz',
  [EUnitOfMeasurement.HORSEPOWER]: 'Pferdestärken',
  [EUnitOfMeasurement.HOUR]: 'Stunde',
  [EUnitOfMeasurement.INCH]: 'Zoll',
  [EUnitOfMeasurement.INCH_OF_MERCURY]: 'Zoll Quecksilber',
  [EUnitOfMeasurement.INCH_OF_WATER_COLUMN]: 'Zoll der Wassersäule',
  [EUnitOfMeasurement.INCH_PER_MINUTE]: 'Zoll pro Minute',
  [EUnitOfMeasurement.INCH_PER_SECOND]: 'Zoll pro Sekunde',
  [EUnitOfMeasurement.KILOGRAM]: 'Kilogramm',
  [EUnitOfMeasurement.KILOGRAM_PER_HOUR]: 'Kilogram pro Stunde',
  [EUnitOfMeasurement.KILOGRAM_PER_MINUTE]: 'Kilogram pro Minute',
  [EUnitOfMeasurement.KILOGRAM_PER_SECOND]: 'Kilogram pro Sekunde',
  [EUnitOfMeasurement.KILOMETER]: 'Kilometer',
  [EUnitOfMeasurement.KILOVOLT]: 'Kilovolt',
  [EUnitOfMeasurement.KILOVOLT_AMPERE]: 'Kilovolt-Ampere',
  [EUnitOfMeasurement.KILOWATT]: 'Kilowatt',
  [EUnitOfMeasurement.KILOWATT_HOUR]: 'Kilowattstunde',
  [EUnitOfMeasurement.KILO_PASCAL]: 'Kilo-Pascal',
  [EUnitOfMeasurement.KNOOP_HARDNESS]: 'Knoophärte',
  [EUnitOfMeasurement.KNOOP_HARDNESS_100]: 'Knoophärte 100',
  [EUnitOfMeasurement.KNOOP_HARDNESS_200]: 'Knoophärte 200',
  [EUnitOfMeasurement.KNOOP_HARDNESS_500]: 'Knoophärte 500',
  [EUnitOfMeasurement.KNOOP_HARDNESS_50]: 'Knoophärte 05',
  [EUnitOfMeasurement.LITRE]: 'Liter',
  [EUnitOfMeasurement.LITRE_PER_HOUR]: 'Liter pro Stunde',
  [EUnitOfMeasurement.LITRE_PER_MINUTE]: 'Liter pro Mitute',
  [EUnitOfMeasurement.MEGAWATT]: 'Megawatt',
  [EUnitOfMeasurement.METRES_PER_MINUTE]: 'Meter pro Minute',
  [EUnitOfMeasurement.METRES_PER_SECOND]: 'Meter pro Sekunde',
  [EUnitOfMeasurement.METRE]: 'Meter',
  [EUnitOfMeasurement.MICROBAR]: 'Mikrobar',
  [EUnitOfMeasurement.MICROBAR_PER_MINUTE]: 'Mikrobar pro Minute',
  [EUnitOfMeasurement.MICRON]: 'Mikron',
  [EUnitOfMeasurement.MICRON_HG_PER_HOUR]: 'Mikron HG pro Stunde',
  [EUnitOfMeasurement.MICRON_HG_PER_MINUTE]: 'Mikron HG pro Minute',
  [EUnitOfMeasurement.MICRON_OF_MERCURY]: 'Mikron Quecksilber',
  [EUnitOfMeasurement.MILLIBAR]: 'Millibar',
  [EUnitOfMeasurement.MILLIBAR_PER_MINUTE]: 'Millibar pro Minute',
  [EUnitOfMeasurement.MILLIGRAM]: 'Milligramm',
  [EUnitOfMeasurement.MILLILITRE]: 'Milliliter',
  [EUnitOfMeasurement.MILLILITRE_PER_MINUTE]: 'Milliliter pro Minute',
  [EUnitOfMeasurement.MILLIMETRE]: 'Millimeter',
  [EUnitOfMeasurement.MILLIMETRE_OF_MERCURY]: 'Millimeter Quecksilber',
  [EUnitOfMeasurement.MILLIMETRE_OF_WATER_COLUMN]: 'Millimeter der Wassersäule',
  [EUnitOfMeasurement.MILLIMETRE_PER_SECOND]: 'Millimeter pro Sekunde',
  [EUnitOfMeasurement.MILLITORR_PER_HOUR]: 'Millitorr pro Stunde',
  [EUnitOfMeasurement.MILLIVOLT]: 'Millivolt',
  [EUnitOfMeasurement.MILLI_AMPERE]: 'Milli Ampere',
  [EUnitOfMeasurement.MINUTE]: 'Minute',
  [EUnitOfMeasurement.MONTH]: 'Monat',
  [EUnitOfMeasurement.NEWTON_PER_SQUARE_MILLIMETRE]: 'Newton pro Quadratmillimeter',
  [EUnitOfMeasurement.NORMAL_LITRE_PER_SQUARE_METERS_PER_HOUR]: 'Normliter pro Quadratmeter pro Stunde',
  [EUnitOfMeasurement.NUMBER]: 'Anzahl/Nummer',
  [EUnitOfMeasurement.PARTS_PER_MILLION]: 'Teile pro Million',
  [EUnitOfMeasurement.PASCAL]: 'Pascal',
  [EUnitOfMeasurement.PERCENT]: 'Prozentsatz',
  [EUnitOfMeasurement.PERCENT_CARBON]: 'Prozentsatz Kohlenstoff',
  [EUnitOfMeasurement.PERCENT_HYDROGEN]: 'Wasserstoff in Prozent',
  [EUnitOfMeasurement.PERCENT_NITROGEN]: 'Stickstoff in Prozent',
  [EUnitOfMeasurement.POUND]: 'Pfund',
  [EUnitOfMeasurement.PSI]: 'PSI',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS]: 'Rockwellhärte',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_N]: 'Rockwellhärte 15N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_T]: 'Rockwellhärte 15T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_W]: 'Rockwellhärte 15W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_X]: 'Rockwellhärte 15X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_15_Y]: 'Rockwellhärte 15Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_N]: 'Rockwellhärte 30N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_T]: 'Rockwellhärte 30T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_W]: 'Rockwellhärte 30W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_X]: 'Rockwellhärte 30X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_30_Y]: 'Rockwellhärte 30Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_N]: 'Rockwellhärte 45N',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_T]: 'Rockwellhärte 45T',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_W]: 'Rockwellhärte 45W',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_X]: 'Rockwellhärte 45X',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_45_Y]: 'Rockwellhärte 45Y',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_A]: 'Rockwellhärte A',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_B]: 'Rockwellhärte B',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_C]: 'Rockwellhärte C',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_D]: 'Rockwellhärte D',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_E]: 'Rockwellhärte E',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_F]: 'Rockwellhärte F',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_G]: 'Rockwellhärte G',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_H]: 'Rockwellhärte H',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_K]: 'Rockwellhärte K',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_L]: 'Rockwellhärte L',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_M]: 'Rockwellhärte M',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_P]: 'Rockwellhärte P',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_R]: 'Rockwellhärte R',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_S]: 'Rockwellhärte S',
  [EUnitOfMeasurement.ROCKWELL_HARDNESS_V]: 'Rockwellhärte V',
  [EUnitOfMeasurement.RPM]: 'Umdrehungen',
  [EUnitOfMeasurement.SECOND]: 'Sekunde',
  [EUnitOfMeasurement.TONNE]: 'Tonne',
  [EUnitOfMeasurement.TORR]: 'Torr',
  [EUnitOfMeasurement.TORR_PER_MINUTE]: 'Torr pro Minute',
  [EUnitOfMeasurement.UNIT_LESS]: 'Einheitenlos',
  [EUnitOfMeasurement.THOUSANDTHS_OF_AN_INCH]: 'Tausendstel Zoll',
  [EUnitOfMeasurement.THOUSANDTHS_OF_WATER_COLUMN]: 'Tausendstel Zoll Wassersäule',
  [EUnitOfMeasurement.THOUSANDTHS_OF_MERCURY_COLUMN]: 'Tausendstel Zoll Quecksilbersäule',
  [EUnitOfMeasurement.VICKERS_HARDNESS]: 'Vickershärte',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_1]: 'Vickershärte 0.1',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_2]: 'Vickershärte 0.2',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_3]: 'Vickershärte 0.3',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_4]: 'Vickershärte 0.4',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_5]: 'Vickershärte 0.5',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_6]: 'Vickershärte 0.6',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_7]: 'Vickershärte 0.7',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_8]: 'Vickershärte 0.8',
  [EUnitOfMeasurement.VICKERS_HARDNESS_0_9]: 'Vickershärte 0.9',
  [EUnitOfMeasurement.VICKERS_HARDNESS_100]: 'Vickershärte 100',
  [EUnitOfMeasurement.VICKERS_HARDNESS_10]: 'Vickershärte 10',
  [EUnitOfMeasurement.VICKERS_HARDNESS_1]: 'Vickershärte 1',
  [EUnitOfMeasurement.VICKERS_HARDNESS_20]: 'Vickershärte 20',
  [EUnitOfMeasurement.VICKERS_HARDNESS_2]: 'Vickershärte 2',
  [EUnitOfMeasurement.VICKERS_HARDNESS_30]: 'Vickershärte 30',
  [EUnitOfMeasurement.VICKERS_HARDNESS_40]: 'Vickershärte 40',
  [EUnitOfMeasurement.VICKERS_HARDNESS_50]: 'Vickershärte 50',
  [EUnitOfMeasurement.VICKERS_HARDNESS_5]: 'Vickershärte 5',
  [EUnitOfMeasurement.VICKERS_HARDNESS_60]: 'Vickershärte 60',
  [EUnitOfMeasurement.VICKERS_HARDNESS_70]: 'Vickershärte 70',
  [EUnitOfMeasurement.VICKERS_HARDNESS_80]: 'Vickershärte 80',
  [EUnitOfMeasurement.VICKERS_HARDNESS_90]: 'Vickershärte 90',
  [EUnitOfMeasurement.VOLT]: 'Volt',
  [EUnitOfMeasurement.VOLT_AMPERE]: 'Volt Ampere',
  [EUnitOfMeasurement.WATT]: 'Watt',
  [EUnitOfMeasurement.WEEK]: 'Woche',
  [EUnitOfMeasurement.WEIGHT_PERCENT]: 'Gewicht in Prozent',
  [EUnitOfMeasurement.YEAR]: 'Jahr',
};

const UnitOfMeasurementSymbol: Record<EUnitOfMeasurement, string> = {
  ...NonTranslatable.UnitOfMeasurementSymbol,
  [EUnitOfMeasurement.MINUTE]: 'Minute | Minuten',
  [EUnitOfMeasurement.HOUR]: 'Stunde | Stunden',
  [EUnitOfMeasurement.DAY]: 'Tag | Tage',
  [EUnitOfMeasurement.WEEK]: 'Woche | Wochen',
  [EUnitOfMeasurement.MONTH]: 'Monate | Monate',
  [EUnitOfMeasurement.YEAR]: 'Jahr | Jahre',
};

const AuditResultType: Record<EAuditResultType, string> = {
  [EAuditResultType.FAILED]: 'Fehlgeschlagen',
  [EAuditResultType.INCONCLUSIVE]: 'Unschlüssig',
  [EAuditResultType.PASSED]: 'Bestanden',
};

const PhysicalPropertyMeaning: Record<EPhysicalPropertyMeaningMap, string> = {
  [EPhysicalPropertyMeaningMap.LOAD_MASS]: 'Chargenmasse',
};

const ConsumptionMeaning: Record<EConsumptionMeaningMap, string> = {
  [EConsumptionMeaningMap.DEPRECATED_AMMONIA_CONSUMPTION]: 'Ammoniaverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_AMMONIA_COST]: 'Ammoniakosten',
  [EConsumptionMeaningMap.DEPRECATED_CARBON_DIOXIDE_CONSUMPTION]: 'Kohlendioxidverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_CARBON_DIOXIDE_COST]: 'Kohlendioxidkosten',
  [EConsumptionMeaningMap.DEPRECATED_CO_GAS_CONSUMPTION]: 'CO-Gasverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_CO_GAS_COST]: 'CO-Gaskosten',
  [EConsumptionMeaningMap.DEPRECATED_DISSOCIATED_AMMONIA_CONSUMPTION]: 'Verbrauch von Dissoziiertem Ammoniak',
  [EConsumptionMeaningMap.DEPRECATED_DISSOCIATED_AMMONIA_COST]: 'Kosten für Dissoziiertem Ammoniak',
  [EConsumptionMeaningMap.DEPRECATED_ELECTRICITY_CONSUMPTION]: 'Stromverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_ELECTRICITY_COST]: 'Stromkosten',
  [EConsumptionMeaningMap.DEPRECATED_ENDO_GAS_CONSUMPTION]: 'Endo-Gasverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_ENDO_GAS_COST]: 'Endo-Gaskosten',
  [EConsumptionMeaningMap.DEPRECATED_HYDROGEN_CONSUMPTION]: 'Wasserstoffverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_HYDROGEN_COST]: 'Wasserstoffkosten',
  [EConsumptionMeaningMap.DEPRECATED_NITROGEN_CONSUMPTION]: 'Stickstoffverbrauch',
  [EConsumptionMeaningMap.DEPRECATED_NITROGEN_COST]: 'Stickstoffkosten',
  [EConsumptionMeaningMap.ACETYLENE_C2H2_CONSUMPTION]: 'Acetylen (C2H2) Verbrauch',
  [EConsumptionMeaningMap.AIR_CONSUMPTION]: 'Luftverbrauch',
  [EConsumptionMeaningMap.AMMONIA_NH3_CONSUMPTION]: 'Ammoniak (NH3) Verbrauch',
  [EConsumptionMeaningMap.ARGON_CONSUMPTION]: 'Argonverbrauch',
  [EConsumptionMeaningMap.CARBON_DIOXIDE_CO2_CONSUMPTION]: 'Kohlendioxid (CO2) Verbrauch',
  [EConsumptionMeaningMap.CARBON_MONOXIDE_CO_CONSUMPTION]: 'Kohlenmonoxid (CO) Verbrauch',
  [EConsumptionMeaningMap.DISSOCIATED_AMMONIA_NH3_CONSUMPTION]: 'Dissoziierter Ammoniak (NH3) Verbrauch',
  [EConsumptionMeaningMap.ENDO_GAS_NATURAL_CONSUMPTION]: 'Endo-Gas (Erdgas) Verbrauch',
  [EConsumptionMeaningMap.ENDO_GAS_PROPANE_CONSUMPTION]: 'Endo-Gas (Propan) Verbrauch',
  [EConsumptionMeaningMap.ETHENE_C2H4_CONSUMPTION]: 'Ethen (C2H4) Verbrauch',
  [EConsumptionMeaningMap.HELIUM_CONSUMPTION]: 'Heliumverbrauch',
  [EConsumptionMeaningMap.HYDROGEN_H2_CONSUMPTION]: 'Wasserstoff (H2) Verbrauch',
  [EConsumptionMeaningMap.METHANOL_CH3OH_CONSUMPTION]: 'Methanol (CH3OH) Verbrauch',
  [EConsumptionMeaningMap.NATURAL_GAS_CONSUMPTION]: 'Erdgasverbrauch',
  [EConsumptionMeaningMap.NITROGEN_CONSUMPTION]: 'Stickstoffverbrauch',
  [EConsumptionMeaningMap.NITROUS_OXIDE_N2O_CONSUMPTION]: 'Distickstoffoxid (N2O) Verbrauch',
  [EConsumptionMeaningMap.PROPANE_C3H8_CONSUMPTION]: 'Propan (C3H8) Verbrauch',
  [EConsumptionMeaningMap.WATER_CONSUMPTION]: 'Wasserverbrauch',
  [EConsumptionMeaningMap.ETHANE_C2H6_CONSUMPTION]: 'Ethan (C2H6) Verbrauch',
  [EConsumptionMeaningMap.METHANE_CH4_CONSUMPTION]: 'Methan (CH4) Verbrauch',
  [EConsumptionMeaningMap.ELECTRICAL_POWER_CONSUMPTION]: 'Stromverbauch',
  [EConsumptionMeaningMap.USER_DEFINED_001]: 'Benutzerdefinierter Verbrauch 001',
  [EConsumptionMeaningMap.USER_DEFINED_002]: 'Benutzerdefinierter Verbrauch 002',
  [EConsumptionMeaningMap.USER_DEFINED_003]: 'Benutzerdefinierter Verbrauch 003',
  [EConsumptionMeaningMap.USER_DEFINED_004]: 'Benutzerdefinierter Verbrauch 004',
  [EConsumptionMeaningMap.USER_DEFINED_005]: 'Benutzerdefinierter Verbrauch 005',
  [EConsumptionMeaningMap.USER_DEFINED_006]: 'Benutzerdefinierter Verbrauch 006',
  [EConsumptionMeaningMap.USER_DEFINED_007]: 'Benutzerdefinierter Verbrauch 007',
  [EConsumptionMeaningMap.USER_DEFINED_008]: 'Benutzerdefinierter Verbrauch 008',
  [EConsumptionMeaningMap.USER_DEFINED_009]: 'Benutzerdefinierter Verbrauch 009',
  [EConsumptionMeaningMap.USER_DEFINED_010]: 'Benutzerdefinierter Verbrauch 010',
  [EConsumptionMeaningMap.USER_DEFINED_011]: 'Benutzerdefinierter Verbrauch 011',
  [EConsumptionMeaningMap.USER_DEFINED_012]: 'Benutzerdefinierter Verbrauch 012',
  [EConsumptionMeaningMap.USER_DEFINED_013]: 'Benutzerdefinierter Verbrauch 013',
  [EConsumptionMeaningMap.USER_DEFINED_014]: 'Benutzerdefinierter Verbrauch 014',
  [EConsumptionMeaningMap.USER_DEFINED_015]: 'Benutzerdefinierter Verbrauch 015',
  [EConsumptionMeaningMap.USER_DEFINED_016]: 'Benutzerdefinierter Verbrauch 016',
  [EConsumptionMeaningMap.USER_DEFINED_017]: 'Benutzerdefinierter Verbrauch 017',
  [EConsumptionMeaningMap.USER_DEFINED_018]: 'Benutzerdefinierter Verbrauch 018',
  [EConsumptionMeaningMap.USER_DEFINED_019]: 'Benutzerdefinierter Verbrauch 019',
  [EConsumptionMeaningMap.USER_DEFINED_020]: 'Benutzerdefinierter Verbrauch 020',
};

const JobFeatureMeaning: Record<EJobFeatureMeaningMap, string> = {
  [EJobFeatureMeaningMap.HEAT_LOSS_MIN_POWER]: '(NICHT BENUTZEN) Wärmeverlust minimale Leistung',
  [EJobFeatureMeaningMap.HEAT_LOSS_MAX_TEMPERATURE]: '(NICHT BENUTZEN) Wärmeverlust maximale Temperatur',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_ACCURACY]: 'Temperatur Regelgenauigkeit',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_PRECISION]: 'Temperatur Regelpräzision',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_ACCURACY]: 'Heizleistung Regelgenauigkeit',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_PRECISION]: 'Heizleistung Regelpräzision',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_ACCURACY]: 'Elektrische Leistungs Regelgenauigkeit',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_PRECISION]: 'Elektrische Leistungs Regelpräzision',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_ACCURACY]: 'Kühlleistung Regelgenauigkeit',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_PRECISION]: 'Kühlleistung Regelpräzision',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_ACCURACY]: 'Relative Druck Regelgenauigkeit',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_PRECISION]: 'Relative Druck Regelpräzision',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_ACCURACY]: 'Kammertemperatur Regelgenauigkeit',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_PRECISION]: 'Kammertemperatur Regelpräzision',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_ACCURACY]: 'Absolute Druck Regelgenauigkeit',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_PRECISION]: 'Absolute Druck Regelpräzision',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Vakuumdruck Regelgenauigkeit',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_PRECISION]: 'Vakuumdruck Regelpräzision',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_ACCURACY]: 'Atmosphärendruck Regelgenauigkeit',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_PRECISION]: 'Atmosphärendruck Regelpräzision',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_ACCURACY]: 'Raumtemperatur Regelgenauigkeit',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_PRECISION]: 'Raumtemperatur Regelpräzision',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_ACCURACY]: 'Aktivität aH Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_PRECISION]: 'Aktivität aH Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_ACCURACY]: 'Potential HP Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_PRECISION]: 'Potential HP Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_ACCURACY]: 'Potential KH- Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_PRECISION]: 'Potential KH- Regelpräzision',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_ACCURACY]: 'Aktivität aO Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_PRECISION]: 'Aktivität aO Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_ACCURACY]: 'Potential OP Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_PRECISION]: 'Potential OP Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_ACCURACY]: 'Potential KO Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_PRECISION]: 'Potential KO Regelpräzision',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_ACCURACY]: 'Aktivität aC Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_PRECISION]: 'Aktivität aC Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_ACCURACY]: 'Potential CP Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_PRECISION]: 'Potential CP Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_ACCURACY]: 'Potential KC Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_PRECISION]: 'Potential KC Regelpräzision',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_ACCURACY]: 'Aktivität aN Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_PRECISION]: 'Aktivität aN Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_ACCURACY]: 'Potential NP Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_PRECISION]: 'Potential NP Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_ACCURACY]: 'Potential KN Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_PRECISION]: 'Potential KN Regelpräzision',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_ACCURACY]: 'Aktivität aB Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_PRECISION]: 'Aktivität aB Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_ACCURACY]: 'Potential BP Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_PRECISION]: 'Potential BP Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_ACCURACY]: 'Potential kB Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_PRECISION]: 'Potential kB Regelpräzision',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_ACCURACY]: 'Aktivität aS Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_PRECISION]: 'Aktivität aS Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_ACCURACY]: 'Potential SP Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_PRECISION]: 'Potential SP Regelpräzision',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_ACCURACY]: 'Potential kS Regelgenauigkeit',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_PRECISION]: 'Potential kS Regelpräzision',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_ACCURACY]: 'Acetylen C2H2 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_PRECISION]: 'Acetylen C2H2 Flow Regelpräzision',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_ACCURACY]: 'Luftstrom Regelgenauigkeit',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_PRECISION]: 'Luftstrom Regelpräzision',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_ACCURACY]: 'Ammoniak NH3 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_PRECISION]: 'Ammoniak NH3 Flow Regelpräzision',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_ACCURACY]: 'Argon Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_PRECISION]: 'Argon Fluss Regelpräzision',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_ACCURACY]: 'Kohlendioxid CO2 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_PRECISION]: 'Kohlendioxid CO2 Flow Regelpräzision',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_ACCURACY]: 'Kohlenmonoxid CO Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_PRECISION]: 'Kohlenmonoxid CO Flow Regelpräzision',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_ACCURACY]: 'Dissoziiertes Ammoniak NH3 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_PRECISION]: 'Dissoziiertes Ammoniak NH3 Fluss Regelpräzision',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_ACCURACY]: 'Endogas natürliche Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_PRECISION]: 'Endogas natürliche Fluss Regelpräzision',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_ACCURACY]: 'Endogas Propan Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_PRECISION]: 'Endogas Propan Fluss Regelpräzision',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_ACCURACY]: 'Äthylen C2H4 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_PRECISION]: 'Äthylen C2H4 Flow Regelpräzision',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_ACCURACY]: 'Helium Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_PRECISION]: 'Helium Fluss Regelpräzision',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_ACCURACY]: 'Wasserstoff H2 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_PRECISION]: 'Wasserstoff H2 Flow Regelpräzision',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_ACCURACY]: 'Methanol CH3OH Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_PRECISION]: 'Methanol CH3OH Flow Regelpräzision',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_ACCURACY]: 'Erdgas Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_PRECISION]: 'Erdgas Fluss Regelpräzision',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_ACCURACY]: 'Stickstoff Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_PRECISION]: 'Stickstoff Fluss Regelpräzision',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_ACCURACY]: 'Propan C3H8 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_PRECISION]: 'Propan C3H8 Flow Regelpräzision',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_ACCURACY]: 'Wasser Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_PRECISION]: 'Wasser Fluss Regelpräzision',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_ACCURACY]: 'Lachgas N2O Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_PRECISION]: 'Lachgas N2O Fluss Regelpräzision',
  [EJobFeatureMeaningMap.TEMPERATURE_CONTROL_AGILITY]: 'Temperatur Regelagilität',
  [EJobFeatureMeaningMap.HEATING_POWER_OUTPUT_CONTROL_AGILITY]: 'Heizleistung Regelagilität',
  [EJobFeatureMeaningMap.ELECTRICAL_POWER_CONTROL_AGILITY]: 'Elektrische Leistungs Regelagilität',
  [EJobFeatureMeaningMap.COOLING_POWER_OUTPUT_CONTROL_AGILITY]: 'Kühlleistung Regelagilität',
  [EJobFeatureMeaningMap.RELATIVE_PRESSURE_CONTROL_AGILITY]: 'Relative Druck Regelagilität',
  [EJobFeatureMeaningMap.CHAMBER_TEMPERATURE_CONTROL_AGILITY]: 'Kammertemperatur Regelagilität',
  [EJobFeatureMeaningMap.ABSOLUTE_PRESSURE_CONTROL_AGILITY]: 'Absolute Druck Regelagilität',
  [EJobFeatureMeaningMap.VACUUM_PRESSURE_CONTROL_AGILITY]: 'Vakuumdruck Regelagilität',
  [EJobFeatureMeaningMap.ATMOSPHERIC_PRESSURE_CONTROL_AGILITY]: 'Atmosphärendruck Regelagilität',
  [EJobFeatureMeaningMap.ROOM_TEMPERATURE_CONTROL_AGILITY]: 'Raumtemperatur Regelagilität',
  [EJobFeatureMeaningMap.ACTIVITY_AH_CONTROL_AGILITY]: 'Aktivität aH Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_HP_CONTROL_AGILITY]: 'Potential HP Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_KH_CONTROL_AGILITY]: 'Potential kH Regelagilität',
  [EJobFeatureMeaningMap.ACTIVITY_AO_CONTROL_AGILITY]: 'Aktivität aO Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_OP_CONTROL_AGILITY]: 'Potential OP Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_KO_CONTROL_AGILITY]: 'Potential kO Regelagilität',
  [EJobFeatureMeaningMap.ACTIVITY_AC_CONTROL_AGILITY]: 'Aktivität aC Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_CP_CONTROL_AGILITY]: 'Potential CP Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_KC_CONTROL_AGILITY]: 'Potential kC Regelagilität',
  [EJobFeatureMeaningMap.ACTIVITY_AN_CONTROL_AGILITY]: 'Aktivität aN Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_NP_CONTROL_AGILITY]: 'Potential NP Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_KN_CONTROL_AGILITY]: 'Potential kN Regelagilität',
  [EJobFeatureMeaningMap.ACTIVITY_AB_CONTROL_AGILITY]: 'Aktivität aB Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_BP_CONTROL_AGILITY]: 'Potential BP Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_KB_CONTROL_AGILITY]: 'Potential kB Regelagilität',
  [EJobFeatureMeaningMap.ACTIVITY_AS_CONTROL_AGILITY]: 'Aktivität aS Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_SP_CONTROL_AGILITY]: 'Potential SP Regelagilität',
  [EJobFeatureMeaningMap.POTENTIAL_KS_CONTROL_AGILITY]: 'Potential kS Regelagilität',
  [EJobFeatureMeaningMap.ACETYLENE_C2H2_FLOW_CONTROL_AGILITY]: 'Acetylen C2H2 Fluss Regelagilität',
  [EJobFeatureMeaningMap.AIR_FLOW_CONTROL_AGILITY]: 'Luftstrom Regelagilität',
  [EJobFeatureMeaningMap.AMMONIA_NH3_FLOW_CONTROL_AGILITY]: 'Ammoniak NH3 Fluss Regelagilität',
  [EJobFeatureMeaningMap.ARGON_FLOW_CONTROL_AGILITY]: 'Argon Fluss Regelagilität',
  [EJobFeatureMeaningMap.CARBON_DIOXIDE_CO2_FLOW_CONTROL_AGILITY]: 'Kohlendioxid CO2 Fluss Regelagilität',
  [EJobFeatureMeaningMap.CARBON_MONOXIDE_CO_FLOW_CONTROL_AGILITY]: 'Kohlenmonoxid CO Fluss Regelagilität',
  [EJobFeatureMeaningMap.DISSOCIATED_AMMONIA_NH3_FLOW_CONTROL_AGILITY]: 'Dissoziiertes Ammoniak NH3 Fluss Regelagilität',
  [EJobFeatureMeaningMap.ENDO_GAS_NATURAL_FLOW_CONTROL_AGILITY]: 'Endogas natürliche Fluss Regelagilität',
  [EJobFeatureMeaningMap.ENDO_GAS_PROPANE_FLOW_CONTROL_AGILITY]: 'Endogas Propan Fluss Regelagilität',
  [EJobFeatureMeaningMap.ETHENE_C2H4_FLOW_CONTROL_AGILITY]: 'Äthylen C2H4 Fluss Regelagilität',
  [EJobFeatureMeaningMap.HELIUM_FLOW_CONTROL_AGILITY]: 'Helium Fluss Regelagilität',
  [EJobFeatureMeaningMap.HYDROGEN_H2_FLOW_CONTROL_AGILITY]: 'Wasserstoff H2 Fluss Regelagilität',
  [EJobFeatureMeaningMap.METHANOL_CH3OH_FLOW_CONTROL_AGILITY]: 'Methanol CH3OH Fluss Regelagilität',
  [EJobFeatureMeaningMap.NATURAL_GAS_FLOW_CONTROL_AGILITY]: 'Erdgas Fluss Regelagilität',
  [EJobFeatureMeaningMap.NITROGEN_FLOW_CONTROL_AGILITY]: 'Stickstoff Fluss Regelagilität',
  [EJobFeatureMeaningMap.PROPANE_C3H8_FLOW_CONTROL_AGILITY]: 'Propan- C3H8 Fluss Regelagilität',
  [EJobFeatureMeaningMap.WATER_FLOW_CONTROL_AGILITY]: 'Wasser Fluss Regelagilität',
  [EJobFeatureMeaningMap.NITROUS_OXIDE_N2O_FLOW_CONTROL_AGILITY]: 'Lachgas N2O Fluss Regelagilität',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_ACCURACY]: 'Sauerstoff O2 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_PRECISION]: 'Sauerstoff O2 Flow Regelpräzision',
  [EJobFeatureMeaningMap.OXYGEN_O2_FLOW_CONTROL_AGILITY]: 'Sauerstoff O2 Fluss Regelagilität',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_ACCURACY]: 'Methan CH4 Fluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_PRECISION]: 'Methan CH4 Flow Regelpräzision',
  [EJobFeatureMeaningMap.METHANE_CH4_FLOW_CONTROL_AGILITY]: 'Methan CH4 Fluss Regelagilität',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_ACCURACY]: 'Dissoziations Regelgenauigkeit',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_PRECISION]: 'Dissoziations Regelpräzision',
  [EJobFeatureMeaningMap.DISSOCIATION_CONTROL_AGILITY]: 'Dissoziations Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_ACCURACY]: 'Partialdruck Acetylen C2H2 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_PRECISION]: 'Partialdruck Acetylen C2H2 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_CONTROL_AGILITY]: 'Partialdruck Acetylen C2H2 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_ACCURACY]: 'Partialdruck Luft Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_PRECISION]: 'Partialdruck Luft Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_CONTROL_AGILITY]: 'Partialdruck Luft Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_ACCURACY]: 'Partialdruck Ammoniak NH3 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_PRECISION]: 'Partialdruck Ammoniak NH3 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_CONTROL_AGILITY]: 'Partialdruck Ammoniak NH3 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_ACCURACY]: 'Partialdruck Argon Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_PRECISION]: 'Partialdruck Argon Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_CONTROL_AGILITY]: 'Partialdruck Argon Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_ACCURACY]: 'Partialdruck Kohlendioxid CO2 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_PRECISION]: 'Partialdruck Kohlendioxid CO2 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_CONTROL_AGILITY]: 'Partialdruck Kohlendioxid CO2 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_ACCURACY]: 'Partialdruck Kohlenmonoxid CO Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_PRECISION]: 'Partialdruck Kohlenmonoxid CO Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_CONTROL_AGILITY]: 'Partialdruck Kohlenmonoxid CO Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_ACCURACY]: 'Partialdruck dissoziiertes Ammoniak NH3 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_PRECISION]: 'Partialdruck dissoziiertes Ammoniak NH3 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_CONTROL_AGILITY]: 'Partialdruck dissoziiertes Ammoniak NH3 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_ACCURACY]: 'Partialdruck Endogas natürlich Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_PRECISION]: 'Partialdruck Endogas natürlich Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_CONTROL_AGILITY]: 'Partialdruck Endogas natürlich Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_ACCURACY]: 'Partialdruck Endogas Propan Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_PRECISION]: 'Partialdruck Endogas Propan Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_CONTROL_AGILITY]: 'Partialdruck Endogas Propan Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_ACCURACY]: 'Partialdruck Ethylen C2H4 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_PRECISION]: 'Partialdruck Ethylen C2H4 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_CONTROL_AGILITY]: 'Partialdruck Ethylen C2H4 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_ACCURACY]: 'Partialdruck Helium Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_PRECISION]: 'Partialdruck Helium Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_CONTROL_AGILITY]: 'Partialdruck Helium Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_ACCURACY]: 'Partialdruck Wasserstoff H2 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_PRECISION]: 'Partialdruck Wasserstoff H2 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_CONTROL_AGILITY]: 'Partialdruck Wasserstoff H2 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_ACCURACY]: 'Partialdruck Methanol CH3OH Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_PRECISION]: 'Partialdruck Methanol CH3OH Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_CONTROL_AGILITY]: 'Partialdruck Methanol CH3OH Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_ACCURACY]: 'Partialdruck Erdgas Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_PRECISION]: 'Partialdruck Erdgas Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_CONTROL_AGILITY]: 'Partialdruck Erdgas Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_ACCURACY]: 'Partialdruck Stickstoff Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_PRECISION]: 'Partialdruck Stickstoff Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_CONTROL_AGILITY]: 'Partialdruck Stickstoff Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_ACCURACY]: 'Partialdruck Lachgas N2O Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_PRECISION]: 'Partialdruck Lachgas N2O Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_CONTROL_AGILITY]: 'Partialdruck Lachgas N2O Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_ACCURACY]: 'Partialdruck Propan C3H8 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_PRECISION]: 'Partialdruck Propan C3H8 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_CONTROL_AGILITY]: 'Partialdruck Propan C3H8 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_ACCURACY]: 'Partialdruck Wasser Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_PRECISION]: 'Partialdruck Wasser Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_CONTROL_AGILITY]: 'Partialdruck Wasser Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Acetylen C2H2 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_PRECISION]: 'Partialdruck Acetylen C2H2 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_CONTROL_AGILITY]: 'Partialdruck Acetylen C2H2 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Luft Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_PRECISION]: 'Partialdruck Luft Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AIR_PERCENT_CONTROL_AGILITY]: 'Partialdruck Luft Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Ammoniak NH3 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_PRECISION]: 'Partialdruck Ammoniak NH3 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_CONTROL_AGILITY]: 'Partialdruck Ammoniak NH3 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Argon Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_PRECISION]: 'Partialdruck Argon Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ARGON_PERCENT_CONTROL_AGILITY]: 'Partialdruck Argon Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Kohlendioxid CO2 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_PRECISION]: 'Partialdruck Kohlendioxid CO2 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_CONTROL_AGILITY]: 'Partialdruck Kohlendioxid CO2 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Kohlenmonoxid CO Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_PRECISION]: 'Partialdruck Kohlenmonoxid CO Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_CONTROL_AGILITY]: 'Partialdruck Kohlenmonoxid CO Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_ACCURACY]: 'Partialdruck dissoziiertes Ammoniak NH3 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_PRECISION]: 'Partialdruck dissoziiertes Ammoniak NH3 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_CONTROL_AGILITY]: 'Partialdruck dissoziiertes Ammoniak NH3 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Endogas natürlich Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_PRECISION]: 'Partialdruck Endogas natürlich Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_CONTROL_AGILITY]: 'Partialdruck Endogas natürlich Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Endogas Propan Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_PRECISION]: 'Partialdruck Endogas Propan Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_CONTROL_AGILITY]: 'Partialdruck Endogas Propan Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Ethylen C2H4 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_PRECISION]: 'Partialdruck Ethylen C2H4 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_CONTROL_AGILITY]: 'Partialdruck Ethylen C2H4 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Helium Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_PRECISION]: 'Partialdruck Helium Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HELIUM_PERCENT_CONTROL_AGILITY]: 'Partialdruck Helium Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Wasserstoff H2 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_PRECISION]: 'Partialdruck Wasserstoff H2 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_CONTROL_AGILITY]: 'Partialdruck Wasserstoff H2 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Methanol CH3OH Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_PRECISION]: 'Partialdruck Methanol CH3OH Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_CONTROL_AGILITY]: 'Partialdruck Methanol CH3OH Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Erdgas Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_PRECISION]: 'Partialdruck Erdgas Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_CONTROL_AGILITY]: 'Partialdruck Erdgas Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Stickstoff Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_PRECISION]: 'Partialdruck Stickstoff Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROGEN_PERCENT_CONTROL_AGILITY]: 'Partialdruck Stickstoff Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Lachgas N2O Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_PRECISION]: 'Partialdruck Lachgas N2O Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_CONTROL_AGILITY]: 'Partialdruck Lachgas N2O Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Propan C3H8 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_PRECISION]: 'Partialdruck Propan C3H8 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_CONTROL_AGILITY]: 'Partialdruck Propan C3H8 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Wasser Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_PRECISION]: 'Partialdruck Wasser Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_WATER_PERCENT_CONTROL_AGILITY]: 'Partialdruck Wasser Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_ACCURACY]: 'Partialdruck Sauerstoff Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_PRECISION]: 'Partialdruck Sauerstoff Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_CONTROL_AGILITY]: 'Partialdruck Sauerstoff Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_ACCURACY]: 'Partialdruck Kohlenstoff Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_PRECISION]: 'Partialdruck Kohlenstoff Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_CONTROL_AGILITY]: 'Partialdruck Kohlenstoff Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_ACCURACY]: 'Partialdruck Bor Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_PRECISION]: 'Partialdruck Bor Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_CONTROL_AGILITY]: 'Partialdruck Bor Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_ACCURACY]: 'Partialdruck Schwefel Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_PRECISION]: 'Partialdruck Schwefel Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_CONTROL_AGILITY]: 'Partialdruck Schwefel Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Sauerstoff Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_PRECISION]: 'Partialdruck Sauerstoff Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_OXYGEN_PERCENT_CONTROL_AGILITY]: 'Partialdruck Sauerstoff Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Kohlenstoff Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_PRECISION]: 'Partialdruck Kohlenstoff Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_CARBON_PERCENT_CONTROL_AGILITY]: 'Partialdruck Kohlenstoff Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Bor Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_PRECISION]: 'Partialdruck Bor Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_BORON_PERCENT_CONTROL_AGILITY]: 'Partialdruck Bor Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Schwefel Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_PRECISION]: 'Partialdruck Schwefel Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_SULFUR_PERCENT_CONTROL_AGILITY]: 'Partialdruck Schwefel Prozent Regelagilität',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_ACCURACY]: 'Ethan C2H6 Durchfluss Regelgenauigkeit',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_AGILITY]: 'Ethan C2H6 Durchfluss Regelagilität',
  [EJobFeatureMeaningMap.ETHANE_C2H6_FLOW_CONTROL_PRECISION]: 'Ethan C2H6 Durchfluss Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_ACCURACY]: 'Partialdruck Ethan C2H6 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_AGILITY]: 'Partialdruck Ethan C2H6 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_CONTROL_PRECISION]: 'Partialdruck Ethan C2H6 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Ethan C2H6 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_AGILITY]: 'Partialdruck Ethan C2H6 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_CONTROL_PRECISION]: 'Partialdruck Ethan C2H6 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_ACCURACY]: 'Partialdruck Methan CH4 Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_AGILITY]: 'Partialdruck Methan CH4 Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_CONTROL_PRECISION]: 'Partialdruck Methan CH4 Regelpräzision',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_ACCURACY]: 'Partialdruck Methan CH4 Prozent Regelgenauigkeit',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_AGILITY]: 'Partialdruck Methan CH4 Prozent Regelagilität',
  [EJobFeatureMeaningMap.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_CONTROL_PRECISION]: 'Partialdruck Methan CH4 Prozent Regelpräzision',
  [EJobFeatureMeaningMap.HEAT_LOSS_AT_500C]: 'Heizverlust',
  [EJobFeatureMeaningMap.ASSET_DWELLING_TIME]: 'Anlage Verweildauer pro Job',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Hochvakuumdruck Regelgenauigkeit',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_AGILITY]: 'Hochvakuumdruck Regelagilität',
  [EJobFeatureMeaningMap.HIGH_VACUUM_PRESSURE_CONTROL_PRECISION]: 'Hochvakuumdruck Regelpräzision',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_ACCURACY]: 'Niderigvakuumdruck Regelgenauigkeit',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_AGILITY]: 'Niderigvakuumdruck Regelagilität',
  [EJobFeatureMeaningMap.LOW_VACUUM_PRESSURE_CONTROL_PRECISION]: 'Niderigvakuumdruck Regelpräzision',
  [EJobFeatureMeaningMap.VACUUM_TIME_TO_CROSS_OVER]: 'Zeit bis Vakuum cross over',
  [EJobFeatureMeaningMap.VACUUM_MAX_LEAK_UP_RATE]: 'Maximale leak up Zeit',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_ACCURACY]: 'Durchlaufzeit Regelgenauigkeit',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_AGILITY]: 'Durchlaufzeit Regelagilität',
  [EJobFeatureMeaningMap.LEAD_TIME_CONTROL_PRECISION]: 'Durchlaufzeit Regelpräzision',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_ACCURACY]: 'Bandgeschwindigkeit Regelgenauigkeit',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_AGILITY]: 'Bandgeschwindigkeit Regelagilität',
  [EJobFeatureMeaningMap.BELT_SPEED_CONTROL_PRECISION]: 'Bandgeschwindigkeit Regelpräzision',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_ACCURACY]: 'Chargentemperatur Regelgenauigkeit',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_AGILITY]: 'Chargentemperatur Regelagilität',
  [EJobFeatureMeaningMap.LOAD_TEMPERATURE_CONTROL_PRECISION]: 'Chargentemperatur Regelpräzision',
  [EJobFeatureMeaningMap.PEARSON_CORRELATION]: 'Pearson Korrelation',
  [EJobFeatureMeaningMap.SPEARMAN_CORRELATION]: 'Spearman Korrelation',
  [EJobFeatureMeaningMap.USER_DEFINED_001]: 'Benutzerdefinierter Jobwert 001',
  [EJobFeatureMeaningMap.USER_DEFINED_002]: 'Benutzerdefinierter Jobwert 002',
  [EJobFeatureMeaningMap.USER_DEFINED_003]: 'Benutzerdefinierter Jobwert 003',
  [EJobFeatureMeaningMap.USER_DEFINED_004]: 'Benutzerdefinierter Jobwert 004',
  [EJobFeatureMeaningMap.USER_DEFINED_005]: 'Benutzerdefinierter Jobwert 005',
  [EJobFeatureMeaningMap.USER_DEFINED_006]: 'Benutzerdefinierter Jobwert 006',
  [EJobFeatureMeaningMap.USER_DEFINED_007]: 'Benutzerdefinierter Jobwert 007',
  [EJobFeatureMeaningMap.USER_DEFINED_008]: 'Benutzerdefinierter Jobwert 008',
  [EJobFeatureMeaningMap.USER_DEFINED_009]: 'Benutzerdefinierter Jobwert 009',
  [EJobFeatureMeaningMap.USER_DEFINED_010]: 'Benutzerdefinierter Jobwert 010',
  [EJobFeatureMeaningMap.USER_DEFINED_011]: 'Benutzerdefinierter Jobwert 011',
  [EJobFeatureMeaningMap.USER_DEFINED_012]: 'Benutzerdefinierter Jobwert 012',
  [EJobFeatureMeaningMap.USER_DEFINED_013]: 'Benutzerdefinierter Jobwert 013',
  [EJobFeatureMeaningMap.USER_DEFINED_014]: 'Benutzerdefinierter Jobwert 014',
  [EJobFeatureMeaningMap.USER_DEFINED_015]: 'Benutzerdefinierter Jobwert 015',
  [EJobFeatureMeaningMap.USER_DEFINED_016]: 'Benutzerdefinierter Jobwert 016',
  [EJobFeatureMeaningMap.USER_DEFINED_017]: 'Benutzerdefinierter Jobwert 017',
  [EJobFeatureMeaningMap.USER_DEFINED_018]: 'Benutzerdefinierter Jobwert 018',
  [EJobFeatureMeaningMap.USER_DEFINED_019]: 'Benutzerdefinierter Jobwert 019',
  [EJobFeatureMeaningMap.USER_DEFINED_020]: 'Benutzerdefinierter Jobwert 020',
  [EJobFeatureMeaningMap.USER_DEFINED_021]: 'Benutzerdefinierter Jobwert 021',
  [EJobFeatureMeaningMap.USER_DEFINED_022]: 'Benutzerdefinierter Jobwert 022',
  [EJobFeatureMeaningMap.USER_DEFINED_023]: 'Benutzerdefinierter Jobwert 023',
  [EJobFeatureMeaningMap.USER_DEFINED_024]: 'Benutzerdefinierter Jobwert 024',
  [EJobFeatureMeaningMap.USER_DEFINED_025]: 'Benutzerdefinierter Jobwert 025',
  [EJobFeatureMeaningMap.USER_DEFINED_026]: 'Benutzerdefinierter Jobwert 026',
  [EJobFeatureMeaningMap.USER_DEFINED_027]: 'Benutzerdefinierter Jobwert 027',
  [EJobFeatureMeaningMap.USER_DEFINED_028]: 'Benutzerdefinierter Jobwert 028',
  [EJobFeatureMeaningMap.USER_DEFINED_029]: 'Benutzerdefinierter Jobwert 029',
  [EJobFeatureMeaningMap.USER_DEFINED_030]: 'Benutzerdefinierter Jobwert 030',
  [EJobFeatureMeaningMap.USER_DEFINED_031]: 'Benutzerdefinierter Jobwert 031',
  [EJobFeatureMeaningMap.USER_DEFINED_032]: 'Benutzerdefinierter Jobwert 032',
  [EJobFeatureMeaningMap.USER_DEFINED_033]: 'Benutzerdefinierter Jobwert 033',
  [EJobFeatureMeaningMap.USER_DEFINED_034]: 'Benutzerdefinierter Jobwert 034',
  [EJobFeatureMeaningMap.USER_DEFINED_035]: 'Benutzerdefinierter Jobwert 035',
  [EJobFeatureMeaningMap.USER_DEFINED_036]: 'Benutzerdefinierter Jobwert 036',
  [EJobFeatureMeaningMap.USER_DEFINED_037]: 'Benutzerdefinierter Jobwert 037',
  [EJobFeatureMeaningMap.USER_DEFINED_038]: 'Benutzerdefinierter Jobwert 038',
  [EJobFeatureMeaningMap.USER_DEFINED_039]: 'Benutzerdefinierter Jobwert 039',
  [EJobFeatureMeaningMap.USER_DEFINED_040]: 'Benutzerdefinierter Jobwert 040',
  [EJobFeatureMeaningMap.USER_DEFINED_041]: 'Benutzerdefinierter Jobwert 041',
  [EJobFeatureMeaningMap.USER_DEFINED_042]: 'Benutzerdefinierter Jobwert 042',
  [EJobFeatureMeaningMap.USER_DEFINED_043]: 'Benutzerdefinierter Jobwert 043',
  [EJobFeatureMeaningMap.USER_DEFINED_044]: 'Benutzerdefinierter Jobwert 044',
  [EJobFeatureMeaningMap.USER_DEFINED_045]: 'Benutzerdefinierter Jobwert 045',
  [EJobFeatureMeaningMap.USER_DEFINED_046]: 'Benutzerdefinierter Jobwert 046',
  [EJobFeatureMeaningMap.USER_DEFINED_047]: 'Benutzerdefinierter Jobwert 047',
  [EJobFeatureMeaningMap.USER_DEFINED_048]: 'Benutzerdefinierter Jobwert 048',
  [EJobFeatureMeaningMap.USER_DEFINED_049]: 'Benutzerdefinierter Jobwert 049',
  [EJobFeatureMeaningMap.USER_DEFINED_050]: 'Benutzerdefinierter Jobwert 050',
  [EJobFeatureMeaningMap.USER_DEFINED_051]: 'Benutzerdefinierter Jobwert 051',
  [EJobFeatureMeaningMap.USER_DEFINED_052]: 'Benutzerdefinierter Jobwert 052',
  [EJobFeatureMeaningMap.USER_DEFINED_053]: 'Benutzerdefinierter Jobwert 053',
  [EJobFeatureMeaningMap.USER_DEFINED_054]: 'Benutzerdefinierter Jobwert 054',
  [EJobFeatureMeaningMap.USER_DEFINED_055]: 'Benutzerdefinierter Jobwert 055',
  [EJobFeatureMeaningMap.USER_DEFINED_056]: 'Benutzerdefinierter Jobwert 056',
  [EJobFeatureMeaningMap.USER_DEFINED_057]: 'Benutzerdefinierter Jobwert 057',
  [EJobFeatureMeaningMap.USER_DEFINED_058]: 'Benutzerdefinierter Jobwert 058',
  [EJobFeatureMeaningMap.USER_DEFINED_059]: 'Benutzerdefinierter Jobwert 059',
  [EJobFeatureMeaningMap.USER_DEFINED_060]: 'Benutzerdefinierter Jobwert 060',
  [EJobFeatureMeaningMap.USER_DEFINED_061]: 'Benutzerdefinierter Jobwert 061',
  [EJobFeatureMeaningMap.USER_DEFINED_062]: 'Benutzerdefinierter Jobwert 062',
  [EJobFeatureMeaningMap.USER_DEFINED_063]: 'Benutzerdefinierter Jobwert 063',
  [EJobFeatureMeaningMap.USER_DEFINED_064]: 'Benutzerdefinierter Jobwert 064',
  [EJobFeatureMeaningMap.USER_DEFINED_065]: 'Benutzerdefinierter Jobwert 065',
  [EJobFeatureMeaningMap.USER_DEFINED_066]: 'Benutzerdefinierter Jobwert 066',
  [EJobFeatureMeaningMap.USER_DEFINED_067]: 'Benutzerdefinierter Jobwert 067',
  [EJobFeatureMeaningMap.USER_DEFINED_068]: 'Benutzerdefinierter Jobwert 068',
  [EJobFeatureMeaningMap.USER_DEFINED_069]: 'Benutzerdefinierter Jobwert 069',
  [EJobFeatureMeaningMap.USER_DEFINED_070]: 'Benutzerdefinierter Jobwert 070',
  [EJobFeatureMeaningMap.USER_DEFINED_071]: 'Benutzerdefinierter Jobwert 071',
  [EJobFeatureMeaningMap.USER_DEFINED_072]: 'Benutzerdefinierter Jobwert 072',
  [EJobFeatureMeaningMap.USER_DEFINED_073]: 'Benutzerdefinierter Jobwert 073',
  [EJobFeatureMeaningMap.USER_DEFINED_074]: 'Benutzerdefinierter Jobwert 074',
  [EJobFeatureMeaningMap.USER_DEFINED_075]: 'Benutzerdefinierter Jobwert 075',
  [EJobFeatureMeaningMap.USER_DEFINED_076]: 'Benutzerdefinierter Jobwert 076',
  [EJobFeatureMeaningMap.USER_DEFINED_077]: 'Benutzerdefinierter Jobwert 077',
  [EJobFeatureMeaningMap.USER_DEFINED_078]: 'Benutzerdefinierter Jobwert 078',
  [EJobFeatureMeaningMap.USER_DEFINED_079]: 'Benutzerdefinierter Jobwert 079',
  [EJobFeatureMeaningMap.USER_DEFINED_080]: 'Benutzerdefinierter Jobwert 080',
};

const TimeseriesMeaning: Record<ETimeseriesMeaning, string> = {
  [ETimeseriesMeaning.TEMPERATURE_SETPOINT]: 'Temperatur Sollwert',
  [ETimeseriesMeaning.TEMPERATURE_READING]: 'Temperatur Istwert',
  [ETimeseriesMeaning.HEATING_POWER_OUTPUT_SETPOINT]: 'Heating power output Sollwert',
  [ETimeseriesMeaning.HEATING_POWER_OUTPUT_READING]: 'Heating power output Istwert',
  [ETimeseriesMeaning.HEATING_POWER_ENABLED]: 'Heating power enabled',
  [ETimeseriesMeaning.ELECTRICAL_POWER_SETPOINT]: 'Electrical power Sollwert',
  [ETimeseriesMeaning.ELECTRICAL_POWER_READING]: 'Electrical power Istwert',
  [ETimeseriesMeaning.TOTAL_HEATING_GAS_SUPPLY_FLOW_READING]: 'Total heating Gas supply Durchfluss Istwert',
  [ETimeseriesMeaning.COOLING_POWER_OUTPUT_SETPOINT]: 'Cooling power output Sollwert',
  [ETimeseriesMeaning.COOLING_POWER_OUTPUT_READING]: 'Cooling power output Istwert',
  [ETimeseriesMeaning.COOLING_POWER_ENABLED]: 'Cooling power enabled',
  [ETimeseriesMeaning.TOTAL_COOLING_GAS_SUPPLY_FLOW_READING]: 'Total cooling Gas supply Durchfluss Istwert',
  [ETimeseriesMeaning.RELATIVE_PRESSURE_SETPOINT]: 'Relative pressure Sollwert',
  [ETimeseriesMeaning.RELATIVE_PRESSURE_READING]: 'Relative pressure Istwert',
  [ETimeseriesMeaning.CHAMBER_TEMPERATURE_SETPOINT]: 'Kammertemperatur Sollwert',
  [ETimeseriesMeaning.CHAMBER_TEMPERATURE_READING]: 'Kammertemperatur Istwert',
  [ETimeseriesMeaning.ABSOLUTE_PRESSURE_SETPOINT]: 'Absolute pressure Sollwert',
  [ETimeseriesMeaning.ABSOLUTE_PRESSURE_READING]: 'Absolute pressure Istwert',
  [ETimeseriesMeaning.VACUUM_PRESSURE_SETPOINT]: 'Vacuum pressure Sollwert',
  [ETimeseriesMeaning.VACUUM_PRESSURE_READING]: 'Vacuum pressure Istwert',
  [ETimeseriesMeaning.ATMOSPHERIC_PRESSURE_SETPOINT]: 'Atmospheric pressure Sollwert',
  [ETimeseriesMeaning.ATMOSPHERIC_PRESSURE_READING]: 'Atmospheric pressure Istwert',
  [ETimeseriesMeaning.ROOM_TEMPERATURE_SETPOINT]: 'Room temperature Sollwert',
  [ETimeseriesMeaning.ROOM_TEMPERATURE_READING]: 'Room temperature Istwert',
  [ETimeseriesMeaning.ACTIVITY_AH_SETPOINT]: 'Aktivität aH Sollwert',
  [ETimeseriesMeaning.ACTIVITY_AH_CALCULATION]: 'Aktivität aH Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_HP_SETPOINT]: 'Potential HP Sollwert',
  [ETimeseriesMeaning.POTENTIAL_HP_CALCULATION]: 'Potential HP Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_KH_SETPOINT]: 'Potential kH Sollwert',
  [ETimeseriesMeaning.POTENTIAL_KH_CALCULATION]: 'Potential kH Berechnnung',
  [ETimeseriesMeaning.ACTIVITY_AO_SETPOINT]: 'Aktivität aO Sollwert',
  [ETimeseriesMeaning.ACTIVITY_AO_CALCULATION]: 'Aktivität aO Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_OP_SETPOINT]: 'Potential OP Sollwert',
  [ETimeseriesMeaning.POTENTIAL_OP_CALCULATION]: 'Potential OP Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_KO_SETPOINT]: 'Potential kO Sollwert',
  [ETimeseriesMeaning.POTENTIAL_KO_CALCULATION]: 'Potential kO Berechnnung',
  [ETimeseriesMeaning.ACTIVITY_AC_SETPOINT]: 'Aktivität aC Sollwert',
  [ETimeseriesMeaning.ACTIVITY_AC_CALCULATION]: 'Aktivität aC Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_CP_SETPOINT]: 'Potential CP Sollwert',
  [ETimeseriesMeaning.POTENTIAL_CP_CALCULATION]: 'Potential CP Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_KC_SETPOINT]: 'Potential kC Sollwert',
  [ETimeseriesMeaning.POTENTIAL_KC_CALCULATION]: 'Potential kC Berechnnung',
  [ETimeseriesMeaning.ACTIVITY_AN_SETPOINT]: 'Aktivität aN Sollwert',
  [ETimeseriesMeaning.ACTIVITY_AN_CALCULATION]: 'Aktivität aN Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_NP_SETPOINT]: 'Potential NP Sollwert',
  [ETimeseriesMeaning.POTENTIAL_NP_CALCULATION]: 'Potential NP Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_KN_SETPOINT]: 'Potential kN Sollwert',
  [ETimeseriesMeaning.POTENTIAL_KN_CALCULATION]: 'Potential kN Berechnnung',
  [ETimeseriesMeaning.ACTIVITY_AB_SETPOINT]: 'Aktivität aB Sollwert',
  [ETimeseriesMeaning.ACTIVITY_AB_CALCULATION]: 'Aktivität aB Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_BP_SETPOINT]: 'Potential BP Sollwert',
  [ETimeseriesMeaning.POTENTIAL_BP_CALCULATION]: 'Potential BP Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_KB_SETPOINT]: 'Potential kB Sollwert',
  [ETimeseriesMeaning.POTENTIAL_KB_CALCULATION]: 'Potential kB Berechnnung',
  [ETimeseriesMeaning.ACTIVITY_AS_SETPOINT]: 'Aktivität aS Sollwert',
  [ETimeseriesMeaning.ACTIVITY_AS_CALCULATION]: 'Aktivität aS Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_SP_SETPOINT]: 'Potential SP Sollwert',
  [ETimeseriesMeaning.POTENTIAL_SP_CALCULATION]: 'Potential SP Berechnnung',
  [ETimeseriesMeaning.POTENTIAL_KS_SETPOINT]: 'Potential kS Sollwert',
  [ETimeseriesMeaning.POTENTIAL_KS_CALCULATION]: 'Potential kS Berechnnung',
  [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Acetylen C2H2 Durchfluss Sollwert',
  [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_READING]: 'Acetylen C2H2 Durchfluss Istwert',
  [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Luft Durchfluss Sollwert',
  [ETimeseriesMeaning.AIR_FLOW_READING]: 'Luft Durchfluss Istwert',
  [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Ammoniak NH3 Durchfluss Sollwert',
  [ETimeseriesMeaning.AMMONIA_NH3_FLOW_READING]: 'Ammoniak NH3 Durchfluss Istwert',
  [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Argon Durchfluss Sollwert',
  [ETimeseriesMeaning.ARGON_FLOW_READING]: 'Argon Durchfluss Istwert',
  [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'Kohlenstoffdioxid CO2 Durchfluss Sollwert',
  [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_READING]: 'Kohlenstoffdioxid CO2 Durchfluss Istwert',
  [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'Kohlenstoffmonoxid CO Durchfluss Sollwert',
  [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_READING]: 'Kohlenstoffmonoxid CO Durchfluss Istwert',
  [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Dissoziiertes Ammoniak NH3 Durchfluss Sollwert',
  [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_READING]: 'Dissoziiertes Ammoniak NH3 Durchfluss Istwert',
  [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Endo Gas Erdgas Durchfluss Sollwert',
  [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_READING]: 'Endo Gas Erdgas Durchfluss Istwert',
  [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Endo Gas Propan Durchfluss Sollwert',
  [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_READING]: 'Endo Gas Propan Durchfluss Istwert',
  [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Ethene C2H4 Durchfluss Sollwert',
  [ETimeseriesMeaning.ETHENE_C2H4_FLOW_READING]: 'Ethene C2H4 Durchfluss Istwert',
  [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Helium H Durchfluss Sollwert',
  [ETimeseriesMeaning.HELIUM_FLOW_READING]: 'Helium H Durchfluss Istwert',
  [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Wasserstoff H2 Durchfluss Sollwert',
  [ETimeseriesMeaning.HYDROGEN_H2_FLOW_READING]: 'Wasserstoff H2 Durchfluss Istwert',
  [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Methanol CH3OH Durchfluss Sollwert',
  [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_READING]: 'Methanol CH3OH Durchfluss Istwert',
  [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Erdgas Durchfluss Sollwert',
  [ETimeseriesMeaning.NATURAL_GAS_FLOW_READING]: 'Erdgas Durchfluss Istwert',
  [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Stickstoff N Durchfluss Sollwert',
  [ETimeseriesMeaning.NITROGEN_FLOW_READING]: 'Stickstoff N Durchfluss Istwert',
  [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Propan C3H8 Durchfluss Sollwert',
  [ETimeseriesMeaning.PROPANE_C3H8_FLOW_READING]: 'Propan C3H8 Durchfluss Istwert',
  [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Wasser Durchfluss Sollwert',
  [ETimeseriesMeaning.WATER_FLOW_READING]: 'Wasser Durchfluss Istwert',
  [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'Lachgas N2O Durchfluss Sollwert',
  [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_READING]: 'Lachgas N2O Durchfluss Istwert',
  [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Methan CH4 Durchfluss Sollwert',
  [ETimeseriesMeaning.METHANE_CH4_FLOW_READING]: 'Methan CH4 Durchfluss Istwert',
  [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Sauerstoff O2 Durchfluss Sollwert',
  [ETimeseriesMeaning.OXYGEN_O2_FLOW_READING]: 'Sauerstoff O2 Durchfluss Istwert',
  [ETimeseriesMeaning.DISSOCIATION_SETPOINT]: 'Dissociation Sollwert',
  [ETimeseriesMeaning.DISSOCIATION_READING]: 'Dissociation Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_SETPOINT]: 'Partialdruck Acetylene C2H2 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_READING]: 'Partialdruck Acetylene C2H2 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_SETPOINT]: 'Partialdruck Luft Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_READING]: 'Partialdruck Luft Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_SETPOINT]: 'Partialdruck Ammonia NH3 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_READING]: 'Partialdruck Ammonia NH3 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_SETPOINT]: 'Partialdruck Argon Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_READING]: 'Partialdruck Argon Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_SETPOINT]: 'Partialdruck Kohlenstoffdioxid CO2 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_READING]: 'Partialdruck Kohlenstoffdioxid CO2 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_SETPOINT]: 'Partialdruck Kohlenstoffmonoxid CO Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_READING]: 'Partialdruck Kohlenstoffmonoxid CO Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_SETPOINT]: 'Partialdruck Dissoziiertes Ammoniak NH3 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_READING]: 'Partialdruck Dissoziiertes Ammoniak NH3 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_SETPOINT]: 'Partialdruck Endo Gas natural Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_READING]: 'Partialdruck Endo Gas natural Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_SETPOINT]: 'Partialdruck Endo Gas Propan Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_READING]: 'Partialdruck Endo Gas Propan Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_SETPOINT]: 'Partialdruck Ethene C2H4 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_READING]: 'Partialdruck Ethene C2H4 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_SETPOINT]: 'Partialdruck Helium Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_READING]: 'Partialdruck Helium Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_SETPOINT]: 'Partialdruck Wasserstoff H2 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_READING]: 'Partialdruck Wasserstoff H2 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_SETPOINT]: 'Partialdruck Methanol CH3OH Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_READING]: 'Partialdruck Methanol CH3OH Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_SETPOINT]: 'Partialdruck Erdgas Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_READING]: 'Partialdruck Erdgas Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_SETPOINT]: 'Partialdruck Stickstoff Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_READING]: 'Partialdruck Stickstoff Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_SETPOINT]: 'Partialdruck Lachgas N2O Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_READING]: 'Partialdruck Lachgas N2O Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_SETPOINT]: 'Partialdruck Propan C3H8 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_READING]: 'Partialdruck Propan C3H8 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_SETPOINT]: 'Partialdruck Wasser Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_READING]: 'Partialdruck Wasser Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_SETPOINT]: 'Partialdruck Acetylene C2H2 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ACETYLENE_C2H2_PERCENT_READING]: 'Partialdruck Acetylene C2H2 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_PERCENT_SETPOINT]: 'Partialdruck Luft Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AIR_PERCENT_READING]: 'Partialdruck Luft Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_SETPOINT]: 'Partialdruck Ammonia NH3 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_AMMONIA_NH3_PERCENT_READING]: 'Partialdruck Ammonia NH3 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_PERCENT_SETPOINT]: 'Partialdruck Argon Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ARGON_PERCENT_READING]: 'Partialdruck Argon Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_SETPOINT]: 'Partialdruck Kohlenstoffdioxid CO2 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_DIOXIDE_CO2_PERCENT_READING]: 'Partialdruck Kohlenstoffdioxid CO2 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_SETPOINT]: 'Partialdruck Kohlenstoffmonoxid CO Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_MONOXIDE_CO_PERCENT_READING]: 'Partialdruck Kohlenstoffmonoxid CO Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_SETPOINT]: 'Partialdruck Dissoziiertes Ammoniak NH3 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_DISSOCIATED_AMMONIA_NH3_PERCENT_READING]: 'Partialdruck Dissoziiertes Ammoniak NH3 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_SETPOINT]: 'Partialdruck Endo Gas natural Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_NATURAL_PERCENT_READING]: 'Partialdruck Endo Gas natural Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_SETPOINT]: 'Partialdruck Endo Gas Propan Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ENDO_GAS_PROPANE_PERCENT_READING]: 'Partialdruck Endo Gas Propan Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_SETPOINT]: 'Partialdruck Ethene C2H4 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHENE_C2H4_PERCENT_READING]: 'Partialdruck Ethene C2H4 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_PERCENT_SETPOINT]: 'Partialdruck helium Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HELIUM_PERCENT_READING]: 'Partialdruck helium Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_SETPOINT]: 'Partialdruck Wasserstoff H2 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_HYDROGEN_H2_PERCENT_READING]: 'Partialdruck Wasserstoff H2 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_SETPOINT]: 'Partialdruck Methanol CH3OH Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANOL_CH3OH_PERCENT_READING]: 'Partialdruck Methanol CH3OH Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_SETPOINT]: 'Partialdruck Erdgas Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NATURAL_GAS_PERCENT_READING]: 'Partialdruck Erdgas Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_PERCENT_SETPOINT]: 'Partialdruck Stickstoff Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROGEN_PERCENT_READING]: 'Partialdruck Stickstoff Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_SETPOINT]: 'Partialdruck Lachgas N2O Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_NITROUS_OXIDE_N2O_PERCENT_READING]: 'Partialdruck Lachgas N2O Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_SETPOINT]: 'Partialdruck Propan C3H8 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_PROPANE_C3H8_PERCENT_READING]: 'Partialdruck Propan C3H8 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_PERCENT_SETPOINT]: 'Partialdruck Wasser Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_WATER_PERCENT_READING]: 'Partialdruck Wasser Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_SETPOINT]: 'Partialdruck Sauerstoff Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_READING]: 'Partialdruck Sauerstoff Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_SETPOINT]: 'Partialdruck Kohlenstoff Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_READING]: 'Partialdruck Kohlenstoff Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_SETPOINT]: 'Partialdruck boron Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_READING]: 'Partialdruck boron Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_SETPOINT]: 'Partialdruck sulfur Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_READING]: 'Partialdruck sulfur Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_PERCENT_SETPOINT]: 'Partialdruck Sauerstoff Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_OXYGEN_PERCENT_READING]: 'Partialdruck Sauerstoff Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_PERCENT_SETPOINT]: 'Partialdruck Kohlenstoff Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_CARBON_PERCENT_READING]: 'Partialdruck Kohlenstoff Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_PERCENT_SETPOINT]: 'Partialdruck boron Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_BORON_PERCENT_READING]: 'Partialdruck boron Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_PERCENT_SETPOINT]: 'Partialdruck sulfur Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_SULFUR_PERCENT_READING]: 'Partialdruck sulfur Prozent Istwert',
  [ETimeseriesMeaning.ETHANE_C2H6_FLOW_SETPOINT]: 'Ethan C2H6 Durchfluss Sollwert',
  [ETimeseriesMeaning.ETHANE_C2H6_FLOW_READING]: 'Ethan C2H6 Durchfluss Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_SETPOINT]: 'Partialdruck Ethan C2H6 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_READING]: 'Partialdruck Ethan C2H6 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_SETPOINT]: 'Partialdruck Ethan C2H6 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_ETHANE_C2H6_PERCENT_READING]: 'Partialdruck Ethan C2H6 Prozent Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_SETPOINT]: 'Partialdruck Methan CH4 Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_READING]: 'Partialdruck Methan CH4 Istwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_SETPOINT]: 'Partialdruck Methan CH4 Prozent Sollwert',
  [ETimeseriesMeaning.PARTIAL_PRESSURE_METHANE_CH4_PERCENT_READING]: 'Partialdruck Methan CH4 Prozent Istwert',
  [ETimeseriesMeaning.HIGH_VACUUM_PRESSURE_SETPOINT]: 'Hochvakuumdruck Sollwert',
  [ETimeseriesMeaning.HIGH_VACUUM_PRESSURE_READING]: 'Hochvakuumdruck Istwert',
  [ETimeseriesMeaning.LOW_VACUUM_PRESSURE_SETPOINT]: 'Niedrigvakuumdruck Sollwert',
  [ETimeseriesMeaning.LOW_VACUUM_PRESSURE_READING]: 'Niedrigvakuumdruck Istwert',
  [ETimeseriesMeaning.LEAD_TIME_SETPOINT]: 'Durchlaufzeit Sollwert',
  [ETimeseriesMeaning.LEAD_TIME_READING]: 'Durchlaufzeit Istwert',
  [ETimeseriesMeaning.BELT_SPEED_SETPOINT]: 'Bandgeschwindigkeit Sollwert',
  [ETimeseriesMeaning.BELT_SPEED_READING]: 'Bandgeschwindigkeit Istwert',
  [ETimeseriesMeaning.LOAD_TEMPERATURE_SETPOINT]: 'Chargentemperatur Sollwert',
  [ETimeseriesMeaning.LOAD_TEMPERATURE_READING]: 'Chargentemperatur Istwert',
  [ETimeseriesMeaning.USER_DEFINED_001]: 'Benutzerdefinierte Zeitreihe 001',
  [ETimeseriesMeaning.USER_DEFINED_002]: 'Benutzerdefinierte Zeitreihe 002',
  [ETimeseriesMeaning.USER_DEFINED_003]: 'Benutzerdefinierte Zeitreihe 003',
  [ETimeseriesMeaning.USER_DEFINED_004]: 'Benutzerdefinierte Zeitreihe 004',
  [ETimeseriesMeaning.USER_DEFINED_005]: 'Benutzerdefinierte Zeitreihe 005',
  [ETimeseriesMeaning.USER_DEFINED_006]: 'Benutzerdefinierte Zeitreihe 006',
  [ETimeseriesMeaning.USER_DEFINED_007]: 'Benutzerdefinierte Zeitreihe 007',
  [ETimeseriesMeaning.USER_DEFINED_008]: 'Benutzerdefinierte Zeitreihe 008',
  [ETimeseriesMeaning.USER_DEFINED_009]: 'Benutzerdefinierte Zeitreihe 009',
  [ETimeseriesMeaning.USER_DEFINED_010]: 'Benutzerdefinierte Zeitreihe 010',
  [ETimeseriesMeaning.USER_DEFINED_011]: 'Benutzerdefinierte Zeitreihe 011',
  [ETimeseriesMeaning.USER_DEFINED_012]: 'Benutzerdefinierte Zeitreihe 012',
  [ETimeseriesMeaning.USER_DEFINED_013]: 'Benutzerdefinierte Zeitreihe 013',
  [ETimeseriesMeaning.USER_DEFINED_014]: 'Benutzerdefinierte Zeitreihe 014',
  [ETimeseriesMeaning.USER_DEFINED_015]: 'Benutzerdefinierte Zeitreihe 015',
  [ETimeseriesMeaning.USER_DEFINED_016]: 'Benutzerdefinierte Zeitreihe 016',
  [ETimeseriesMeaning.USER_DEFINED_017]: 'Benutzerdefinierte Zeitreihe 017',
  [ETimeseriesMeaning.USER_DEFINED_018]: 'Benutzerdefinierte Zeitreihe 018',
  [ETimeseriesMeaning.USER_DEFINED_019]: 'Benutzerdefinierte Zeitreihe 019',
  [ETimeseriesMeaning.USER_DEFINED_020]: 'Benutzerdefinierte Zeitreihe 020',
  [ETimeseriesMeaning.USER_DEFINED_021]: 'Benutzerdefinierte Zeitreihe 021',
  [ETimeseriesMeaning.USER_DEFINED_022]: 'Benutzerdefinierte Zeitreihe 022',
  [ETimeseriesMeaning.USER_DEFINED_023]: 'Benutzerdefinierte Zeitreihe 023',
  [ETimeseriesMeaning.USER_DEFINED_024]: 'Benutzerdefinierte Zeitreihe 024',
  [ETimeseriesMeaning.USER_DEFINED_025]: 'Benutzerdefinierte Zeitreihe 025',
  [ETimeseriesMeaning.USER_DEFINED_026]: 'Benutzerdefinierte Zeitreihe 026',
  [ETimeseriesMeaning.USER_DEFINED_027]: 'Benutzerdefinierte Zeitreihe 027',
  [ETimeseriesMeaning.USER_DEFINED_028]: 'Benutzerdefinierte Zeitreihe 028',
  [ETimeseriesMeaning.USER_DEFINED_029]: 'Benutzerdefinierte Zeitreihe 029',
  [ETimeseriesMeaning.USER_DEFINED_030]: 'Benutzerdefinierte Zeitreihe 030',
  [ETimeseriesMeaning.USER_DEFINED_031]: 'Benutzerdefinierte Zeitreihe 031',
  [ETimeseriesMeaning.USER_DEFINED_032]: 'Benutzerdefinierte Zeitreihe 032',
  [ETimeseriesMeaning.USER_DEFINED_033]: 'Benutzerdefinierte Zeitreihe 033',
  [ETimeseriesMeaning.USER_DEFINED_034]: 'Benutzerdefinierte Zeitreihe 034',
  [ETimeseriesMeaning.USER_DEFINED_035]: 'Benutzerdefinierte Zeitreihe 035',
  [ETimeseriesMeaning.USER_DEFINED_036]: 'Benutzerdefinierte Zeitreihe 036',
  [ETimeseriesMeaning.USER_DEFINED_037]: 'Benutzerdefinierte Zeitreihe 037',
  [ETimeseriesMeaning.USER_DEFINED_038]: 'Benutzerdefinierte Zeitreihe 038',
  [ETimeseriesMeaning.USER_DEFINED_039]: 'Benutzerdefinierte Zeitreihe 039',
  [ETimeseriesMeaning.USER_DEFINED_040]: 'Benutzerdefinierte Zeitreihe 040',
  [ETimeseriesMeaning.USER_DEFINED_041]: 'Benutzerdefinierte Zeitreihe 041',
  [ETimeseriesMeaning.USER_DEFINED_042]: 'Benutzerdefinierte Zeitreihe 042',
  [ETimeseriesMeaning.USER_DEFINED_043]: 'Benutzerdefinierte Zeitreihe 043',
  [ETimeseriesMeaning.USER_DEFINED_044]: 'Benutzerdefinierte Zeitreihe 044',
  [ETimeseriesMeaning.USER_DEFINED_045]: 'Benutzerdefinierte Zeitreihe 045',
  [ETimeseriesMeaning.USER_DEFINED_046]: 'Benutzerdefinierte Zeitreihe 046',
  [ETimeseriesMeaning.USER_DEFINED_047]: 'Benutzerdefinierte Zeitreihe 047',
  [ETimeseriesMeaning.USER_DEFINED_048]: 'Benutzerdefinierte Zeitreihe 048',
  [ETimeseriesMeaning.USER_DEFINED_049]: 'Benutzerdefinierte Zeitreihe 049',
  [ETimeseriesMeaning.USER_DEFINED_050]: 'Benutzerdefinierte Zeitreihe 050',
  [ETimeseriesMeaning.USER_DEFINED_051]: 'Benutzerdefinierte Zeitreihe 051',
  [ETimeseriesMeaning.USER_DEFINED_052]: 'Benutzerdefinierte Zeitreihe 052',
  [ETimeseriesMeaning.USER_DEFINED_053]: 'Benutzerdefinierte Zeitreihe 053',
  [ETimeseriesMeaning.USER_DEFINED_054]: 'Benutzerdefinierte Zeitreihe 054',
  [ETimeseriesMeaning.USER_DEFINED_055]: 'Benutzerdefinierte Zeitreihe 055',
  [ETimeseriesMeaning.USER_DEFINED_056]: 'Benutzerdefinierte Zeitreihe 056',
  [ETimeseriesMeaning.USER_DEFINED_057]: 'Benutzerdefinierte Zeitreihe 057',
  [ETimeseriesMeaning.USER_DEFINED_058]: 'Benutzerdefinierte Zeitreihe 058',
  [ETimeseriesMeaning.USER_DEFINED_059]: 'Benutzerdefinierte Zeitreihe 059',
  [ETimeseriesMeaning.USER_DEFINED_060]: 'Benutzerdefinierte Zeitreihe 060',
  [ETimeseriesMeaning.USER_DEFINED_061]: 'Benutzerdefinierte Zeitreihe 061',
  [ETimeseriesMeaning.USER_DEFINED_062]: 'Benutzerdefinierte Zeitreihe 062',
  [ETimeseriesMeaning.USER_DEFINED_063]: 'Benutzerdefinierte Zeitreihe 063',
  [ETimeseriesMeaning.USER_DEFINED_064]: 'Benutzerdefinierte Zeitreihe 064',
  [ETimeseriesMeaning.USER_DEFINED_065]: 'Benutzerdefinierte Zeitreihe 065',
  [ETimeseriesMeaning.USER_DEFINED_066]: 'Benutzerdefinierte Zeitreihe 066',
  [ETimeseriesMeaning.USER_DEFINED_067]: 'Benutzerdefinierte Zeitreihe 067',
  [ETimeseriesMeaning.USER_DEFINED_068]: 'Benutzerdefinierte Zeitreihe 068',
  [ETimeseriesMeaning.USER_DEFINED_069]: 'Benutzerdefinierte Zeitreihe 069',
  [ETimeseriesMeaning.USER_DEFINED_070]: 'Benutzerdefinierte Zeitreihe 070',
  [ETimeseriesMeaning.USER_DEFINED_071]: 'Benutzerdefinierte Zeitreihe 071',
  [ETimeseriesMeaning.USER_DEFINED_072]: 'Benutzerdefinierte Zeitreihe 072',
  [ETimeseriesMeaning.USER_DEFINED_073]: 'Benutzerdefinierte Zeitreihe 073',
  [ETimeseriesMeaning.USER_DEFINED_074]: 'Benutzerdefinierte Zeitreihe 074',
  [ETimeseriesMeaning.USER_DEFINED_075]: 'Benutzerdefinierte Zeitreihe 075',
  [ETimeseriesMeaning.USER_DEFINED_076]: 'Benutzerdefinierte Zeitreihe 076',
  [ETimeseriesMeaning.USER_DEFINED_077]: 'Benutzerdefinierte Zeitreihe 077',
  [ETimeseriesMeaning.USER_DEFINED_078]: 'Benutzerdefinierte Zeitreihe 078',
  [ETimeseriesMeaning.USER_DEFINED_079]: 'Benutzerdefinierte Zeitreihe 079',
  [ETimeseriesMeaning.USER_DEFINED_080]: 'Benutzerdefinierte Zeitreihe 080',
  [ETimeseriesMeaning.USER_DEFINED_081]: 'Benutzerdefinierte Zeitreihe 081',
  [ETimeseriesMeaning.USER_DEFINED_082]: 'Benutzerdefinierte Zeitreihe 082',
  [ETimeseriesMeaning.USER_DEFINED_083]: 'Benutzerdefinierte Zeitreihe 083',
  [ETimeseriesMeaning.USER_DEFINED_084]: 'Benutzerdefinierte Zeitreihe 084',
  [ETimeseriesMeaning.USER_DEFINED_085]: 'Benutzerdefinierte Zeitreihe 085',
  [ETimeseriesMeaning.USER_DEFINED_086]: 'Benutzerdefinierte Zeitreihe 086',
  [ETimeseriesMeaning.USER_DEFINED_087]: 'Benutzerdefinierte Zeitreihe 087',
  [ETimeseriesMeaning.USER_DEFINED_088]: 'Benutzerdefinierte Zeitreihe 088',
  [ETimeseriesMeaning.USER_DEFINED_089]: 'Benutzerdefinierte Zeitreihe 089',
  [ETimeseriesMeaning.USER_DEFINED_090]: 'Benutzerdefinierte Zeitreihe 090',
  [ETimeseriesMeaning.USER_DEFINED_091]: 'Benutzerdefinierte Zeitreihe 091',
  [ETimeseriesMeaning.USER_DEFINED_092]: 'Benutzerdefinierte Zeitreihe 092',
  [ETimeseriesMeaning.USER_DEFINED_093]: 'Benutzerdefinierte Zeitreihe 093',
  [ETimeseriesMeaning.USER_DEFINED_094]: 'Benutzerdefinierte Zeitreihe 094',
  [ETimeseriesMeaning.USER_DEFINED_095]: 'Benutzerdefinierte Zeitreihe 095',
  [ETimeseriesMeaning.USER_DEFINED_096]: 'Benutzerdefinierte Zeitreihe 096',
  [ETimeseriesMeaning.USER_DEFINED_097]: 'Benutzerdefinierte Zeitreihe 097',
  [ETimeseriesMeaning.USER_DEFINED_098]: 'Benutzerdefinierte Zeitreihe 098',
  [ETimeseriesMeaning.USER_DEFINED_099]: 'Benutzerdefinierte Zeitreihe 099',
  [ETimeseriesMeaning.USER_DEFINED_100]: 'Benutzerdefinierte Zeitreihe 100',
};

const PhysicalDimension: Record<EPhysicalDimension, string> = {
  [EPhysicalDimension.ACCELERATION]: 'Beschleunigung',
  [EPhysicalDimension.ADMITTANCE]: 'Admittanz',
  [EPhysicalDimension.AMOUNT]: 'Betrag',
  [EPhysicalDimension.ANGLE]: 'Winkel',
  [EPhysicalDimension.ANGULAR_ACCELERATION]: 'Winkelbeschleunigung',
  [EPhysicalDimension.ANGULAR_MOMENTUM]: 'Drehimpuls',
  [EPhysicalDimension.ANGULAR_VELOCITY]: 'Winkelgeschwindigkeit',
  [EPhysicalDimension.APPARENT_ELECTRICAL_POWER]: 'Elektrische Leistung',
  [EPhysicalDimension.AREA]: 'Fläche',
  [EPhysicalDimension.CAPACITANCE]: 'Kapazität',
  [EPhysicalDimension.COOLING_RATE]: 'Abkühlungsrate',
  [EPhysicalDimension.CURRENCY]: 'Währung',
  [EPhysicalDimension.DATETIME]: 'Zeitpunkt',
  [EPhysicalDimension.DATE]: 'Datum',
  [EPhysicalDimension.DENSITY]: 'Dichte',
  [EPhysicalDimension.DURATION]: 'Dauer',
  [EPhysicalDimension.ELECTRICAL_REACTIVE_POWER]: 'Elektrische Blindleistung',
  [EPhysicalDimension.ELECTRICAL_RESISTANCE]: 'Elektrischer Widerstand',
  [EPhysicalDimension.ELECTRIC_CHARGE]: 'Elektrische Ladung',
  [EPhysicalDimension.ELECTRIC_CURRENT]: 'Elektrischer Strom',
  [EPhysicalDimension.ENERGY]: 'Energie',
  [EPhysicalDimension.FLOW]: 'Durchfluss',
  [EPhysicalDimension.FLOW_PER_AREA]: 'Durchfluss pro Fläche',
  [EPhysicalDimension.FORCE]: 'Kraft',
  [EPhysicalDimension.FREQUENCY]: 'Frequenz',
  [EPhysicalDimension.HARDNESS]: 'Härte',
  [EPhysicalDimension.HEAT_FLUX]: 'Wärmestrom',
  [EPhysicalDimension.IDENTITY]: 'Identität',
  [EPhysicalDimension.INDUCTIVITY]: 'Induktivität',
  [EPhysicalDimension.INTERVAL]: 'Intervall',
  [EPhysicalDimension.LENGTH]: 'Länge',
  [EPhysicalDimension.MAGNETIC_FLUX]: 'Magnetischer Fluss',
  [EPhysicalDimension.MASS]: 'Masse',
  [EPhysicalDimension.MASS_FLOW_RATE]: 'Massendurchsatz',
  [EPhysicalDimension.MASS_FRACTION]: 'Massenanteil',
  [EPhysicalDimension.MODULUS_OF_ELASTICITY]: 'Elastizitätsmodul',
  [EPhysicalDimension.MOMENTUM]: 'Momentum',
  [EPhysicalDimension.MOMENT_OF_INERTIA]: 'Trägheitsmoment',
  [EPhysicalDimension.POWER]: 'Leistung',
  [EPhysicalDimension.POWER_DENSITY]: 'Leistungsdichte',
  [EPhysicalDimension.PRESSURE]: 'Druck',
  [EPhysicalDimension.PRESSURE_RATE]: 'Druckrate',
  [EPhysicalDimension.QUANTITY]: 'Menge',
  [EPhysicalDimension.RATIO]: 'Verhältnis',
  [EPhysicalDimension.ROTATIONAL_VELOCITY]: 'Rotationsgeschwindigkeit',
  [EPhysicalDimension.SOUND_PRESSURE]: 'Schalldruck',
  [EPhysicalDimension.STRESS]: 'Spannung',
  [EPhysicalDimension.SURFACE_TENSION]: 'Oberflächenspannung',
  [EPhysicalDimension.TEMPERATURE]: 'Temperatur',
  [EPhysicalDimension.THERMAL_EXPANSION]: 'Thermische Ausdehnung',
  [EPhysicalDimension.THERMAL_RESISTANCE]: 'Thermischer Widerstand',
  [EPhysicalDimension.TIME]: 'Zeit',
  [EPhysicalDimension.TORQUE]: 'Drehmoment',
  [EPhysicalDimension.VELOCITY]: 'Geschwindigkeit',
  [EPhysicalDimension.VISCOSITY]: 'Viskosität',
  [EPhysicalDimension.VOLTAGE]: 'Spannung',
  [EPhysicalDimension.VOLUME]: 'Volumen',
  [EPhysicalDimension.WORK]: 'Arbeit',
};

const CalendarEventType: Record<ECalendarEventType, string> = {
  [ECalendarEventType.ACTUAL_UNIT_DOWNTIME]: 'Ungeplanter Stillstand',
  [ECalendarEventType.PLANNED_UNIT_DOWNTIME]: 'Geplanter Stillstand',
  [ECalendarEventType.OPERATION_HOLD]: 'Betriebspause',
};

const MaintenanceEventType: Record<components['schemas']['MaintenanceEventType'], string> = {
  MAINTENANCE: 'Geplante Wartung (Wiederholend)',
};

const MaintenanceStateType: Record<components['schemas']['MaintenanceEventStateType'], string> = {
  PENDING: 'Ausstehend',
  ASSIGNED: 'Zugewiesen',
  IN_PROGRESS: 'In Bearbeitung',
  IN_REVIEW: 'In Prüfung',
  REJECTED: 'Abgelehnt',
  COMPLETED: 'Abgeschlossen',
};

const AssetHierarchyLevel: Record<EHierarchy, string> = {
  [EHierarchy.CHAMBER]: generalTerm.chamber,
  [EHierarchy.COMPANY]: generalTerm.company,
  [EHierarchy.COMPONENT]: generalTerm.component,
  [EHierarchy.EQUIPMENT]: generalTerm.equipment,
  [EHierarchy.EQUIPMENT_GROUP]: generalTerm.equipmentGroup,
  [EHierarchy.AUTOMAT]: generalTerm.automat,
  [EHierarchy.AREA]: generalTerm.area,
  [EHierarchy.LOCATION]: generalTerm.location,
  [EHierarchy.ROOT]: generalTerm.root,
};

export const NotificationChannelType: Record<ENotificationChannelType, string> = {
  [ENotificationChannelType.EMAIL]: generalTerm.email,
  [ENotificationChannelType.IN_APP]: 'In-App',
  [ENotificationChannelType.SMS]: generalTerm.sms,
};

const SimulationStep: TComponentKeys<'SimulationStep'> & {
  surfaceHardnessSetpoint: string,
  shortCaseDepth: string,
} = {
  runtimeSeconds: 'Laufzeit in Sekunden',
  stageIndex: 'Stufenindex',
  stageRuntimeSeconds: 'Stufenlaufzeit in Sekunden',
  temperature: 'Temperatur',
  carbonPotential: 'Kohlenstoffpotential',
  nitrogenPotential: 'Stickstoffpotential',
  massFlowHydroCarbon: 'Massenstrom',
  carburizingDepth: 'CHD',
  carburizingDepthSetPoint: 'Festgelegte Eindringtiefe der Aufkohlung',
  carburizingDepthCarbonContent: 'Kohlenstoffgehalt bei CHD',
  surfaceCarbon: 'Randkohlenstoff',
  surfaceNitrogen: 'Randstickstoff',
  surfaceCarbide: 'Oberflächencarbid',
  effectiveCaseHardness: 'Effektive Randschichthärte',
  carbideLimit: 'Carbidgrenze',
  surfaceHardnessSetpoint: 'Sollwert der Oberflächenhärte',
  sootLimit: 'Rußgrenze',
  caseDepth: 'Randschichttiefe',
  shortCaseDepth: 'RST',
  retainedAustenite: 'Restaustenit',
  compoundLayerThickness: 'Verbindungsschichtdicke',
  diffusionLayerThickness: 'Diffusionsschichtdicke',
  nitridedHardnessDepth: 'NHD + 50HV',
  totalDiffusionDepth: 'Gesamtnitriertiefe',
  carbonProfile: 'C-Profil',
  carbonProfileMin: 'C-Profil (min)',
  carbonProfileMax: 'C-Profil (max)',
  nitrogenProfile: 'N-Profil',
  nitrogenProfileMin: 'N-Profil (min)',
  nitrogenProfileMax: 'N-Profil (max)',
  carbideProfile: 'Carbidprofil',
  carbideProfileMin: 'Carbidprofil (min)',
  carbideProfileMax: 'Carbidprofil (max)',
  hardnessProfile: 'Härterverlaufsprofil',
  hardnessProfileMin: 'Härterverlaufsprofil (min)',
  hardnessProfileMax: 'Härterverlaufsprofil (max)',
  nSolubilityProfile: 'Aktuelle Nitridgrenze',
  nitrideProfile: 'Nitridprofil',
  nitrideProfileMin: 'Nitridprofil (min)',
  nitrideProfileMax: 'Nitridprofil (max)',
  profileXValues: 'Werte entlang des Profils von außen nach innen in mm',
};

const Asset: Partial<TComponentKeys<'Asset'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  hierarchyOrder: 'Position unter Geschwisteranlagen',
  hierarchyLevel: 'Anlagelevel',
  parentId: 'Übergeordnete Anlage',
};

const AssetCreate: Partial<TComponentKeys<'AssetCreate'>> = {
  ...Asset,
  permissionsRoleIds: 'Rollen die Zugriff erhalten',
};

const CompanyCreate: Partial<TComponentKeys<'CompanyCreate'>> = {
  ...Asset,
  timezone: 'Zeitzone',
};

const EquipmentCreate: Partial<TComponentKeys<'EquipmentCreate'>> = {
  ...Asset,
  equipmentPhysicalTypeId: 'Bauart',
  serialNumber: 'Serien-Nr.',
  manufacturer: 'Hersteller',
  model: 'Modell',
  ipAddress: 'IP-Adresse',
  chambers: 'Kammern',
  department: 'Abteilung',
};

const LocationCreate: Partial<TComponentKeys<'LocationCreate'>> = {
  ...CompanyCreate,
};

const ChamberCreate: Partial<TComponentKeys<'ChamberCreate'>> = {
  ...Asset,
  chamberType: 'Art der Kammer',
  heatingZoneCount: 'Anzahl der Heizzonen',
  concurrentJobLimit: 'Max. gleichzeitige Chargen',
  heatTreatmentCapabilities: 'Wärmebehandlungsfähigkeiten',
};

const Chamber: Partial<TComponentKeys<'ChamberCreate'>> = {
  ...ChamberCreate,
};

const CameraRegistryEntryCameraType: Record<components['schemas']['CameraRegistryEntry']['cameraType'], string> = {
  IP_CAMERA_SNAPSHOT: 'IP-Kamera-Snapshot',
  IP_CAMERA_STREAM: 'IP-Kamera-Stream',
};

const CameraRegistryEntry: TComponentKeys<'CameraRegistryEntry'> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  assetId: generalTerm.asset,
  maxAgeThresholdSeconds: 'Aufbewahrungsdauer von Bildern',
  staleThresholdSeconds: 'Schwellwert für veraltete Bilder',
  cameraId: 'Kamera',
  cameraType: 'Kameratyp',
  cameraUrl: 'Kamera-URL',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
};

const AssetProfileStatus: Record<EAssetProfileStatus, string> = {
  [EAssetProfileStatus.COMPLETE]: 'Vollständig',
  [EAssetProfileStatus.INCOMPLETE]: 'Unvollständig',
  [EAssetProfileStatus.INVALID]: 'Ungültig',
};

const AssetProfileCombinator: Record<EAssetProfileCombinator, string> = {
  [EAssetProfileCombinator.ANY_OF]: 'Beliebige',
  [EAssetProfileCombinator.ALL_OF]: 'Alle',
  [EAssetProfileCombinator.ONE_OF]: 'Einer',
};

const ObservationType: Record<EObservationType, string> = {
  [EObservationType.LOWER_CONTROL_LIMIT_EXCEEDED]: 'Untere Kontrollgrenze unterschritten',
  [EObservationType.UPPER_CONTROL_LIMIT_EXCEEDED]: 'Obere Kontrollgrenze überschritten',
  [EObservationType.NEGATIVE_TREND_DETECTED]: 'Negativer Trend erkannt',
  [EObservationType.POSITIVE_TREND_DETECTED]: 'Positiver Trend erkannt',
  [EObservationType.LOWER_CUSTOM_LIMIT_EXCEEDED]: 'Untere benutzerdefinierte Grenze überschritten',
  [EObservationType.UPPER_CUSTOM_LIMIT_EXCEEDED]: 'Obere benutzerdefinierte Grenze überschritten',
};

const ChamberType: Record<EChamberType, string> = {
  [EChamberType.HOT]: 'Heiß',
  [EChamberType.ISO_THERMAL]: 'Isotherm',
  [EChamberType.QUENCH]: 'Abschrecken',
  [EChamberType.VACUM]: 'Vakuum',
  [EChamberType.LOADING]: 'Beladen',
  [EChamberType.UNLOADING]: 'Entladen',
  [EChamberType.LOADING_UNLOADING]: 'Be-/Entladen',
};

const HeatTreatmentProcessType: Record<EHeatTreatmentProcessType, string> = {
  [EHeatTreatmentProcessType.ANNEALING]: 'Glühen',
  [EHeatTreatmentProcessType.QUENCHING]: 'Abschrecken',
  [EHeatTreatmentProcessType.NEUTRAL_HARDENING]: 'Neutralglühen',
  [EHeatTreatmentProcessType.CRYOGENIC_TREATMENT]: 'Kryogene Behandlung',
  [EHeatTreatmentProcessType.TEMPERING]: 'Anlassen',
  [EHeatTreatmentProcessType.AGING]: 'Altern',
  [EHeatTreatmentProcessType.CARBURIZING]: 'Vergüten',
  [EHeatTreatmentProcessType.CARBONITRIDING]: 'Carbonitrieren',
  [EHeatTreatmentProcessType.NITRIDING]: 'Nitrieren',
  [EHeatTreatmentProcessType.NITROCARBURIZING]: 'Nitrocarburieren',
  [EHeatTreatmentProcessType.PRE_OXIDATION]: 'Voroxidation',
  [EHeatTreatmentProcessType.POST_OXIDATION]: 'Nachoxidation',
  [EHeatTreatmentProcessType.ACTIVATION]: 'Aktivierung',
};

const AppGroupSemanticDepartment: Record<EAppGroupSemanticDepartment, string> = {
  [EAppGroupSemanticDepartment.PRODUCTION_PLANNING]: 'Arbeitsvorbereitung',
  [EAppGroupSemanticDepartment.OPERATIONS]: 'Fertigung',
  [EAppGroupSemanticDepartment.QUALITY_ASSURANCE]: 'Qualitätssicherung',
  [EAppGroupSemanticDepartment.ANALYTICS]: 'Auswertung',
  [EAppGroupSemanticDepartment.MASTER_DATA]: 'Stammdaten',
  [EAppGroupSemanticDepartment.ADMINISTRATION]: 'Administration',
};

const AppGroupSemanticTopic: Record<EAppGroupSemanticTopic, string> = {
  [EAppGroupSemanticTopic.OPERATION]: 'Betrieb',
  [EAppGroupSemanticTopic.DATA]: 'Daten/Analyse',
  [EAppGroupSemanticTopic.MAINTENANCE]: 'Wartung',
  [EAppGroupSemanticTopic.PROCESS]: 'Prozess',
  [EAppGroupSemanticTopic.CONFIGURATION]: 'Konfiguration',
};

const AppId: Record<EAppId, string> = {
  [EAppId.ALARM_STATISTICS]: 'Störungsstatistik',
  [EAppId.AI_ASSISTANT]: 'KI Assistent',
  [EAppId.ANOMALY_DETECTION]: 'Anomalieerkennung',
  [EAppId.ASSET_MANAGEMENT]: 'Anlagenmanagement',
  [EAppId.CERTIFICATION]: 'Audit & Qualität',
  [EAppId.CUSTOMER]: 'Kundendatenbank',
  [EAppId.DEPRECATED_MODEL_PROCESS]: 'Modellprozesse',
  [EAppId.GRAFANA]: 'Grafana',
  [EAppId.HISTORY_AND_TRENDS]: 'Historie & Trends',
  [EAppId.LIVE]: 'Live-App',
  [EAppId.MATERIALS]: 'Werkstoffdatenbank',
  [EAppId.MODEL_BUILDER]: 'Modellgenerator',
  [EAppId.OPERATION_CALENDAR]: 'Produktionskalender',
  [EAppId.ORDERS]: 'Aufträge',
  [EAppId.PARTS]: 'Teile',
  [EAppId.PERFORMANCE_DASHBOARD]: 'Auslastungsübersicht',
  [EAppId.PRODUCTION_LOGS]: 'Produktionshistorie',
  [EAppId.PRODUCTION_SCHEDULER]: 'Produktionsplaner',
  [EAppId.PRODUCTION_STATISTICS]: 'Produktionsstatistik',
  [EAppId.PT9800_CONFIGURATION]: 'Protherm Konfiguration',
  [EAppId.QMULUS_DASHBOARD]: 'Dashboards',
  [EAppId.RECIPE]: 'Rezepte',
  [EAppId.RECIPE_ON_DEMAND]: 'Rezeptsuche',
  [EAppId.REMOTE_CONTROL]: 'Fernsteuerung',
  [EAppId.SHOP_LAYOUT]: 'Hallenlayout',
  [EAppId.SIMULATOR]: 'Simulatoren',
  [EAppId.TENANT_SETTINGS]: 'Einstellungen',
  [EAppId.UPC_PRO]: 'UPC Werkzeugkasten',
  [EAppId.USER_MANAGEMENT]: 'Benutzerverwaltung',
};

const CarburizingSimulationPartShape: Record<ESimulatorSimulationPartShape, string> = {
  [ESimulatorSimulationPartShape.ROUND_STEEL]: 'Rundstahl',
  [ESimulatorSimulationPartShape.HEXAGON_OR_OCTAGON_STEEL]: 'Sechseck- oder Achteckstahl',
  [ESimulatorSimulationPartShape.SQUARE_OR_FLAT_STEEL]: 'Quadrat- oder Flachstahl',
  [ESimulatorSimulationPartShape.SOLID_STEEL]: 'Vollstahl oder Ring (Innendurchmesser < 200mm)',
  [ESimulatorSimulationPartShape.RING]: 'Ring (Innendurchmesser > 200 mm)',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_80]: 'Zyl. Hohlkörper Innendurchmesser < 80 mm',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_200]: 'Zyl. Hohlkörper Innendurchmesser < 200 mm',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_GREATER_200]: 'Zyl. Hohlkörper Innendurchmesser > 200 mm',
  [ESimulatorSimulationPartShape.HOLLOW_BODY_CLOSED]: 'Hohlkörper, geschlossen von einer oder zwei Seiten',
};

const CarburizingSimulationPartShapeSize: Record<ESimulatorSimulationPartShape, string> = {
  [ESimulatorSimulationPartShape.ROUND_STEEL]: 'Nenndurchmesser',
  [ESimulatorSimulationPartShape.HEXAGON_OR_OCTAGON_STEEL]: 'Nennabstand zwischen gegenüberl. Seiten',
  [ESimulatorSimulationPartShape.SQUARE_OR_FLAT_STEEL]: 'Kürzeste Kantenlänge',
  [ESimulatorSimulationPartShape.SOLID_STEEL]: 'Kürzeste Kantenlänge',
  [ESimulatorSimulationPartShape.RING]: 'Kürzeste Kantenlänge',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_80]: 'Wandstärke',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_LESS_200]: 'Wandstärke',
  [ESimulatorSimulationPartShape.CYL_HOLLOW_BODY_GREATER_200]: 'Wandstärke',
  [ESimulatorSimulationPartShape.HOLLOW_BODY_CLOSED]: 'Wandstärke',
};

const CarburizingSimulatorRecipeType: Record<ESimulatorRecipeType, string> = {
  [ESimulatorRecipeType.CARBURIZING]: 'Aufkohlen [C]',
  [ESimulatorRecipeType.CARBONITRIDING]: 'Carbonitrieren [CN]',
  [ESimulatorRecipeType.LOW_PRESSURE_CARBURIZING]: 'Niederdruckaufkohlen [LPC]',
  [ESimulatorRecipeType.NITRIDING]: 'Nitrieren [N]',
  [ESimulatorRecipeType.NITROCARBURIZING]: 'Nitrocarburieren [NC]',
};

const MaterialPropertySpectrum: Record<keyof components['schemas']['MaterialPropertySpectrum']['avg'], string> = {
  alloyFactorCAustenite: 'Legierungsfaktor [Carbon]',
  carbideLimit: 'Carbidgrenze [%aC=1]',
  alloyFactorNAustenite: 'Legierungsfaktor [Nitrogen]',
  nitrideLimit: 'Nitridgrenze [%FexNy]',
  coreHardness: 'Kernhärte (unbehandelt)',
  maxHardness: 'Maximale Martensit-Härte',
  ac1: 'AC1',
  ac3: 'AC3',
  ms: 'Martensit Start',
  mf: 'Martensit Finish',
};

const PartMaterialProperties: TComponentKeys<'PartMaterialProperties'> = {
  ...MaterialPropertySpectrum,
  surfaceCarbon: 'Randkohlenstoffgehalt',
  referenceCarbon: 'Grenzkohlenstoffgehalt',
};

const SimulatorRecipe: TComponentKeys<'SimulatorRecipe'> = {
  description: generalTerm.description, // Beschreibung
  displayName: generalTerm.name, // Anzeigename
  isGasCompositionConstant: 'Konstante Gaszusammensetzung',
  gasComposition: generalTerm.gasComposition,
  recipeType: 'Art der Wärmebehandlung',
  simulationValuesType: 'Art',
  simulatorRecipeId: '',
  simulatorPartId: '',
  materialCalculationModelId: 'Werkstoffberechnungsmodell',
  stages: '',
  userDefinedAlloyFactor: generalTerm.alloyFactor,
  userDefinedCarbideLimit: generalTerm.carbideLimit,
  userDefinedCarbonContent: generalTerm.carbonContent,
  userDefinedCarburizingDepth: generalTerm.carburizingDepth,
  userDefinedReferenceCarbon: generalTerm.referenceCarbon,
};

const NotificationTable: Record<ESubscriptionTreeLabel, Record<'event' | 'description', string>> = {
  [ESubscriptionTreeLabel.ALARMS]: {
    event: 'Störungen',
    description: 'Erhalte eine Benachrichtigung, sobald ein Störungsereignis beliebiger Schwere auftritt.',
  },
  [ESubscriptionTreeLabel.ALARM_OCCURRED]: {
    event: 'Störung',
    description: 'Benachrichtigung bei Störungsereignissen.',
  },
  [ESubscriptionTreeLabel.JOBS]: {
    event: 'Jobs',
    description: 'Erhalte eine Benachrichtigung, wenn sich der Status eines Jobs ändert.',
  },
  [ESubscriptionTreeLabel.JOB_FINISHED]: {
    event: 'Charge beendet',
    description: 'Eine Charge wurde beendet.',
  },
  [ESubscriptionTreeLabel.JOB_STARTED]: {
    event: 'Charge gestartet',
    description: 'Eine Charge wurde gestartet.',
  },
  [ESubscriptionTreeLabel.MONITORING]: {
    event: 'Überwachung',
    description: 'Erhalten eine Benachrichtigung über Qmulus-Überwachungsereignisse',
  },
  [ESubscriptionTreeLabel.TIMESERIES_LAG_DETECTED]: {
    event: 'Zeitreihen nicht mehr aktuell',
    description: 'Mindestens eine Zeitreihe eines Ofens hängt mehr als 30 Minuten hinterher.',
  },
  [ESubscriptionTreeLabel.NEW_QMULUS_RELEASE]: {
    event: 'Neue Qmulus Version',
    description: 'Eine neue Version von Qmulus wurde veröffentlicht.',
  },
  [ESubscriptionTreeLabel.ANOMALIES_AND_OBSERVATIONS]: {
    event: 'Anomalien & Beobachtungen',
    description: 'Erhalte eine Benachrichtigung, wenn eine Anomalie beobachtet wurde.',
  },
  [ESubscriptionTreeLabel.CONTROL_LEVEL_EXCEEDED]: {
    event: 'Kontrollniveau überschritten',
    description: 'Das Kontrollniveau des Sensors wurde überschritten.',
  },
  [ESubscriptionTreeLabel.TREND_DETECTED]: {
    event: 'Trend erkannt',
    description: 'Ein neuer Trend in den Sensormessungen wurde erkannt.',
  },
  [ESubscriptionTreeLabel.DRIFT_DETECTED]: {
    event: 'Drift erkannt',
    description: 'Die Sensormessungen weichen von den Referenzwerten ab.',
  },
  [ESubscriptionTreeLabel.OPERATION_MAINTENANCE]: {
    event: 'Betrieb & Wartung',
    description: 'Erhalte eine Benachrichtigung über Ereignisse zu Betrieb & Wartung.',
  },
  [ESubscriptionTreeLabel.MAINTENANCE_OCCURRENCE_STATE_CHANGED]: {
    event: 'Wartungsstatus geändert',
    description: 'Der aktuelle Status einer Wartungscharge wurde geändert.',
  },
};

const SubscriptionTreeNode: Partial<TComponentKeys<'SubscriptionTreeNode'>> = {
  notificationMetadataId: generalTerm.description,
  label: generalTerm.event,
  subscriptions: '',
};

const CarburizingRecipeSimulationValuesType: Record<ESimulatorRecipeSimulationValuesType, string> = {
  [ESimulatorRecipeSimulationValuesType.AVG_VAL]: 'Durchschnitt',
  [ESimulatorRecipeSimulationValuesType.COMP_VAL]: 'Mittlere Analyse',
  [ESimulatorRecipeSimulationValuesType.USER_VAL]: 'Benutzerdefiniert',
};

const DataFormat: Record<EDataFormat, string> = {
  [EDataFormat.TIME_SERIES]: 'Zeitreihe',
  [EDataFormat.JOB_AGGREGATE]: 'Jobwert',
  [EDataFormat.EVENT]: 'Ereignis',
  [EDataFormat.ALARM_EVENT]: 'Störung',
  [EDataFormat.BLOB]: 'Dateien/Binäre daten (BLOB)',
};

const ObservationTypeHeader: Record<string, string> = {
  name: 'Name des Beobachtungstyps',
};

const AssetUtilization: Partial<TComponentKeys<'AssetUtilization'>> = {
  assetDisplayName: generalTerm.asset,
  noProductionSeconds: 'Keine Produktion',
  actualProductionTimeSeconds: 'Produktive Zeit',
  plannedProductionTimeSeconds: 'Geplante produktive Zeit',
  actualUnitDowntimeSeconds: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  plannedDowntimeSeconds: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  operationHoldSeconds: CalendarEventType.OPERATION_HOLD,
  noProductionPercentage: 'Keine Produktion',
  actualProductionTimePercentage: 'Produktive Zeit',
  plannedProductionTimePercentage: 'Geplante produktive Zeit',
  actualUnitDowntimePercentage: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  plannedDowntimePercentage: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  operationHoldPercentage: CalendarEventType.OPERATION_HOLD,
  effectiveTargetProductivityPercentage: 'Zielproduktivität',
};

const AuditSpecificationResultOverview: TComponentKeys<'AuditSpecificationResultOverview'> = {
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  auditResultId: 'Bericht-Nr.',
  auditedAtUtc: generalTerm.date,
  auditedByUserName: 'Prüfer',
  jobName: generalTerm.jobDisplayName,
  recipeDisplayName: generalTerm.recipe,
  jobId: '',
};

const AuditEnvelopeNonPassedReasons: Record<components['schemas']['AuditEnvelopeNonPassedReasons'], string> = {
  no_start_point_found: 'Kein Startpunkt gefunden',
  values_out_of_band: 'Werte außerhalb des Bands',
  max_duration_exceeded: 'Maximale Dauer überschritten',
  min_duration_not_reached: 'Minimale Dauer nicht erreicht',
  job_is_running: 'Job ist nicht beendet',
  meanings_missing: 'Keine Signale mit Bedeutung vorhanden für die Anlage.',
  no_data: 'Keine Daten',
  data_ended_unexpected: 'Daten endeten unerwartet',
};

const Part: TComponentKeys<'Part'> = {
  hasAllRequiredAttributes: 'Alle Pflichtattribute ausgefüllt',
  displayName: generalTerm.part,
  description: 'Teilbeschreibung',
  materialId: generalTerm.material,
  customerId: generalTerm.customer,
  materialDisplayName: generalTerm.material,
  treatmentRemarks: 'Hinweis zur Behandlung',
  customerDisplayName: generalTerm.customer,
  drawingReference: 'Zeichnung',
  isOnetime: 'Einmaliges Teil',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  images: 'Bilder',
  partId: generalTerm.partId,
  attributes: 'Teile Attribute',
  partRevisionId: generalTerm.revision,
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
  lastModifiedByUserId: generalTerm.lastModifiedBy,
  lastModifiedByUserDisplayName: generalTerm.lastModifiedBy,
  createdAtUtc: generalTerm.createdAt,
  createdByUserId: generalTerm.createdBy,
  createdByUserDisplayName: generalTerm.createdBy,
  isDeleted: generalTerm.showDeleted,
};

const PartDetails: TComponentKeys<'PartDetails'> = {
  ...Part,
  assetQualifications: 'Qualifizierte Anlage & Rezepte',
};

const Customer: TComponentKeys<'Customer'> = {
  city: 'Stadt',
  contacts: 'Kontakte',
  country: 'Land',
  customerId: 'Unternehmen',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
  postalCode: 'Postleitzahl',
  state: 'Bundesland',
  street: 'Straße',
  displayName: 'Unternehmen',
  email: 'E-Mail',
  phone: 'Telefon',
  cellphone: 'Mobiltelefon',
  fax: 'Fax',
};

const Material: Partial<TComponentKeys<'Material'>> = {
  displayName: 'Werkstoffname',
  materialComposition: 'Werkstoffzusammensetzung',
  materialGroups: 'Benutzerdefinierte Gruppe',
  materialNames: 'Werkstoffnamen',
};

const MaterialNamingSystem: Partial<TComponentKeys<'MaterialNamingSystem'>> = {
  displayName: 'Name',
};

const ChartAssessmentTestRequirementType: Record<components['schemas']['ChartAssessmentTestRequirementType'], string> = {
  setpoints_greater_than_measures: 'Messwerte < Sollwerte',
  setpoints_greater_than_or_equal_to_measures: 'Messwerte ≤ Sollwerte',
  setpoints_less_than_measures: 'Messwerte > Sollwerte',
  setpoints_less_than_or_equal_to_measures: 'Messwerte ≥ Sollwerte',
};

const User: Partial<TComponentKeys<'User'>> = {
  displayName: generalTerm.displayName,
  firstName: 'Vorname',
  lastName: 'Nachname',
  email: 'E-Mail',
  rolesDisplayName: 'Rollen',
};

const OpenAPIPrimitiveType: Record<components['schemas']['OpenAPIPrimitiveType'], string> = {
  boolean: 'Boolean',
  integer: 'Ganzzahl',
  number: 'Fließkommazahl',
  string: 'Text',
};

const ProductionStatistics: Partial<TComponentKeys<'ProductionStatistics'>> = {
  displayName: generalTerm.name,
  earliestJobStartedAtUtc: 'Früheste Chargenstartzeit',
  latestJobFinishedAtUtc: 'Spätester Chargenendzeit',
  jobCnt: 'Chargenanzahl',
};

const CsvSeperator: Record<components['schemas']['CsvSeperator'], string> = {
  comma: 'Komma',
  semicolon: 'Semikolon',
  tab: 'Tabulator',
  pipe: 'Rohr',
  space: 'Leerzeichen',
};

const CsvDatetimeFormat: Record<components['schemas']['CsvDatetimeFormat'], string> = {
  iso_8601: 'ISO 8601',
  unix_timestamp: 'UNIX',
  us: 'US',
};

const CsvLineTerminator: Record<components['schemas']['CsvLineTerminator'], string> = {
  posix: 'POSIX',
  windows: 'Windows',
};

const CsvQuoteStyle: Record<components['schemas']['CsvQuoteStyle'], string> = {
  necessary: 'Erforderlich',
  always: 'Immer',
  non_numeric: 'Nicht numerisch',
  never: 'Nie',
};

const CsvQuoteStyleHint: Record<components['schemas']['CsvQuoteStyle'], string> = {
  necessary: 'Sie sind notwendig, wenn Felder ein Anführungszeichen, einen Separator oder einen Datensatzabschluss enthalten. Anführungszeichen sind auch notwendig, wenn ein leerer Datensatz geschrieben wird (der sich nicht von einem Datensatz mit einem leeren Feld unterscheidet). Dies ist der Standard.',
  always: 'Dies setzt Anführungszeichen um jedes Feld. Immer.',
  non_numeric: 'Dies setzt Anführungszeichen um alle Felder, die nicht numerisch sind. Nämlich, wenn ein Feld geschrieben wird, das nicht als gültige Fließkommazahl oder Ganzzahl geparst werden kann, dann werden Anführungszeichen verwendet, auch wenn sie nicht unbedingt erforderlich sind.',
  never: 'Dies setzt niemals Anführungszeichen um Felder, selbst wenn dies zu ungültigen CSV-Daten führt (z.B.: indem keine Anführungszeichen bei Zeichenfolgen gesetzt werden, die den Separator enthalten).',
};

const CsvQuoteCharacter: Record<components['schemas']['CsvQuoteCharacter'], string> = {
  single_quote: 'Einzelnes Zitatzeichen',
  double_quote: 'Doppeltes Zitatzeichen',
};

const PartAssetRecipeQualification: Partial<TComponentKeys<'PartAssetRecipeQualification'>> = {
  assetIds: generalTerm.asset,
  maxNumberOfPartsPerRost: 'Maximale Anzahl der Teile pro Rost',
  recipeDisplayName: generalTerm.recipe,
  recipeId: generalTerm.recipe,
  loadingSchemeId: 'Beladeplan',
};

const AuditSpecification: TComponentKeys<'AuditSpecification'> = {
  auditSpecificationId: 'Audit Spezifikations-Id',
  description: 'Beschreibung',
  displayName: 'Spezifikationsname',
  envelopes: 'Anzahl der Umschläge',
  revision: 'Revisions-Id',
  isOneTimeSpec: '',
};

const AlarmCollection: TComponentKeys<'AlarmCollection'> = {
  dataId: 'Data-Nr.',
  assetId: generalTerm.asset,
  displayName: generalTerm.displayName,
  eventType: 'Störungstyp',
  occurrenceData: generalTerm.date,
  severity: 'Schweregrad',
};

const AlarmEvent: TComponentKeys<'AlarmEvent'> = {
  occurredAtUtc: 'Start',
  clearedAtUtc: 'Ende',
  acknowledgedAtUtc: 'Quittiert',
  durationSeconds: 'Dauer',
};

const CounterAction: Partial<TComponentKeys<'CounterAction'>> = {
  displayName: 'Gegenmaßnahme',
  description: generalTerm.description,
};

const DataSource: TComponentKeys<'DataSource'> = {
  dataSourceId: generalTerm.dataSourceId,
  displayName: generalTerm.displayName,
  dataSourceType: 'Art der Datenquelle',
  description: generalTerm.description,
  timezone: 'Zeitzone',
  connectionDetails: 'Details',
  assetAssociations: 'Verbindungen zu Anlagen',
};

const DataSourceAssociation: TComponentKeys<'DataSourceAssociation'> = {
  assetId: generalTerm.assetId,
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  dataSourceType: DataSource.dataSourceType,
};

const Metadata: Partial<TComponentKeys<'Metadata'>> = {
  dataId: 'Data-Nr.',
  assetId: generalTerm.asset,
  dataSourceId: generalTerm.dataSourceId,
  color: generalTerm.color,
  dataFormat: 'Datenformat',
  dataSourceReference: generalTerm.dataSourceReference,
  dataSourceType: DataSource.dataSourceType,
  description: generalTerm.description,
  displayName: generalTerm.name,
  guaranteedColor: generalTerm.color,
  isHidden: 'Sichtbarkeit',
  meaningId: 'Bedeutung',
  meaningInstanceNumber: 'Instanz',
  significantDigits: 'Signifikante Ziffern',
  unitOfMeasureId: 'Einheit',
  scaleType: 'Achsentyp',
};

const MetadataUpdate: Partial<TComponentKeys<'MetadataUpdate'>> = {
  ...Metadata,
  significantDigitsOverwrite: Metadata.significantDigits,
};

const AlarmDefinition: Partial<TComponentKeys<'AlarmDefinition'>> = {
  ...Metadata,
  severity: AlarmCollection.severity,
};

const PartAttribute: Partial<TComponentKeys<'PartAttribute'>> & {
  value: string,
} = {
  displayName: 'Attribute',
  unitOfMeasureId: 'Einheit',
  value: 'Wert',
  valueType: 'Datentyp',
  isBuiltIn: 'Integriert',
  isRequired: 'Erforderlich',
  isVisible: Metadata.isHidden!,
};

const Sample: Partial<TComponentKeys<'Sample'>> = {
  displayName: 'Stichprobe',
};

const TableAssessmentTest: Partial<TComponentKeys<'TableAssessmentTest'>> = {
  displayName: generalTerm.name,
  isOverwritten: generalTerm.overwrite,
};

const TableAssessmentTestItem: Partial<Record<keyof components['schemas']['TableAssessmentTest']['items'][number], string>> = {
  displayName: generalTerm.name,
  requirement: generalTerm.requirement,
  result: generalTerm.result,
};

const AssessmentTestResultType = 'Testergebnis';

const ChartAssessmentTest: Partial<TComponentKeys<'ChartAssessmentTest'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  xUnitId: 'X-Einheit',
  yUnitId: 'Y-Einheit',
  requirement: 'Überprüfung',
  isOverwritten: generalTerm.overwrite,
};

const FileAssessmentTest: Partial<TComponentKeys<'FileAssessmentTest'>> = {
  displayName: generalTerm.name,
  result: 'Testergebnis',
  resultComment: 'Kommentar',
  isOverwritten: generalTerm.overwrite,
};

const ReportOverview: Partial<TComponentKeys<'ReportOverview'>> = {
  reportId: generalTerm.reportId,
  reporterDisplayName: generalTerm.reporter,
  jobId: generalTerm.jobId,
  jobDisplayName: generalTerm.job,
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  assetLocation: generalTerm.location,
  recipeDisplayName: generalTerm.recipe,
  partId: generalTerm.partId,
  partDisplayName: generalTerm.part,
  partCustomerDisplayName: generalTerm.customer,
  partMaterialDisplayName: generalTerm.material,
  partDrawingReference: generalTerm.drawingReference,
  orderNumbers: 'Auftrag',
  isAutogenerated: 'Importiert aus Fremdsystem',
  orderCreatedAtUtc: 'Auftragdatum',
  isDeleted: generalTerm.showDeleted,
};

const ReportBlockAlarmsTemplateVariable: Record<TComponentsSchema['ReportBlockAlarmsTemplateVariable'], string> = {
  alarms: 'Alarme',
};

const ReportBlockAuditEnvelopesTemplateVariable: Record<TComponentsSchema['ReportBlockAuditEnvelopesTemplateVariable'], string> = {
  envelopes: 'Grenzen',
};

const ReportBlockAuditReportHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockAuditReportHeaderTemplateVariable'], string> = {
  asset_name: 'Anlage',
  audit_specification_display_name: 'Spezifikation',
  audited_at: 'Geprüft am',
  auditor_display_name: 'Prüfer',
  auditor_remark: 'Bemerkung des Prüfers',
  description: 'Beschreibung',
  duration_seconds: 'Dauer',
  finished_at: 'Endzeitpunkt',
  load_number: 'Chargennummer',
  location_name: 'Standort',
  recipe_description: 'Rezeptbeschreibung',
  recipe_name: 'Rezept',
  report_number: 'Berichtsnummer',
  started_at: 'Startzeit',
};

const ReportBlockJobDetailsHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockJobDetailsHeaderTemplateVariable'], string> = {
  asset_name: 'Anlage',
  description: 'Beschreibung',
  duration_seconds: 'Dauer',
  finished_at: 'Endzeitpunkt',
  load_number: 'Chargennummer',
  location_name: 'Standort',
  recipe_description: 'Rezeptbeschreibung',
  recipe_name: 'Rezept',
  started_at: 'Startzeit',
};

const ReportBlockJobPartsField: Record<TComponentsSchema['ReportBlockJobPartsField'], string> = {
  customer: generalTerm.customer,
  description: generalTerm.description,
  displayName: generalTerm.part,
  drawingReference: generalTerm.drawingReference,
  material: generalTerm.material,
  order: 'Auftrag',
  partCount: 'Menge',
  treatmentRemarks: 'Behandlungshinweise',
};

const ReportBlockJobPartsTemplateVariable: Record<TComponentsSchema['ReportBlockJobPartsTemplateVariable'], string> = {
  attribute_totals: 'Summe je Attribut',
  attribute_unit_symbols: 'Einheit je Attribut',
  columns: 'Tabellenspalten',
  has_part_read_permission: 'Berechtigung zum Lesen von Teilen',
  part_attributes: 'Attribute der Teile',
  part_count_total: 'Gesamtmenge',
  parts: 'Teile',
};

const ReportBlockJobStagesTemplateVariable: Record<TComponentsSchema['ReportBlockJobStagesTemplateVariable'], string> = {
  has_stage_read_permission: 'Berechtigung zum Lesen der Schritte',
  stages: 'Schritte',
};

const ReportBlockQualityReportHeaderTemplateVariable: Record<TComponentsSchema['ReportBlockQualityReportHeaderTemplateVariable'], string> = {
  asset_name: 'Anlage',
  customer_display_name: generalTerm.customer,
  description: 'Beschreibung',
  duration_seconds: 'Dauer',
  finished_at: 'Endzeitpunkt',
  load_number: 'Chargennummer',
  location_name: 'Standort',
  material_display_name: generalTerm.material,
  part_display_name: generalTerm.part,
  part_drawing_reference: generalTerm.drawingReference,
  recipe_description: 'Rezeptbeschreibung',
  recipe_name: 'Rezept',
  report_number: 'Berichtsnummer',
  reporter_display_name: generalTerm.reporter,
  started_at: 'Startzeit',
};

const ReportBlockQualitySamplesTemplateVariable: Record<TComponentsSchema['ReportBlockQualitySamplesTemplateVariable'], string> = {
  samples: 'Proben',
};

const ReportBlockRecipeTemplateVariable: Record<TComponentsSchema['ReportBlockRecipeTemplateVariable'], string> = {
  does_include_setpoints: 'Jeder Sollwert in einer eigenen Zeile',
  does_include_signal_legend: 'Legende der Signale',
  specification: 'Behandlungsvorgabe',
};

const ReportBlockSignaturesTemplateVariable: Record<TComponentsSchema['ReportBlockSignaturesTemplateVariable'], string> = {
  titles: 'Titel der Unterschriftsfelder',
};

const ReportBlockId: Record<TReportBlockId, string> = {
  alarms: 'Alarme',
  auditEnvelopes: 'Grenzen des Audits',
  auditReportHeader: 'Kopf des Auditberichts',
  jobDetailsHeader: 'Chargendetails',
  jobImages: 'Chargenbilder',
  jobParts: 'Teile',
  jobStages: 'Rezeptschritte der Charge',
  qualityReportHeader: 'Kopf des Qualitätsberichts',
  qualitySamples: 'Proben und Prüfungen',
  recipe: 'Rezept',
  signatures: 'Unterschriften',
  timeseriesChart: 'Zeitreihendiagramm',
};

const ReportComponentIdType: Record<TComponentsSchema['ReportComponentIdType'], string> = {
  auditEnvelope: 'Audit: Grenze',
  auditEnvelopeSections: 'Audit: Abschnitte einer Grenze',
  auditResult: 'Audit: Ergebnis',
  formatQuantity: 'Wert mit Einheit',
  formatValue: 'Wert',
  keyValueGrid: 'Raster von Werten',
  keyValuePair: 'Beschrifteter Wert',
  prothermBatchRecipe: 'Protherm: Chargenrezept',
  prothermBeltRecipe: 'Protherm: Bandrezept',
  qualityChartTest: 'Qualität: Diagrammprüfung',
  qualityFileTest: 'Qualität: Dateiprüfung',
  qualityImageTest: 'Qualität: Bildprüfung',
  qualitySample: 'Qualität: Probe',
  qualityTableTest: 'Qualität: Tabellenprüfung',
  qualityTest: 'Qualität: Prüfung',
  qualityTestResult: 'Qualität: Prüfergebnis',
  table: 'Tabelle',
  warning: 'Warnung',
};

const ReportComponentIdTypeDescription: Record<TComponentsSchema['ReportComponentIdType'], string> = {
  auditEnvelope: 'Zeigt eine Grenze eines Audits mit ihren Sensoren, ihrem Band und ihrer Dauer, dem Diagramm ihrer Sensoren, ihren Abschnitten und ihrer Bewertung.',
  auditEnvelopeSections: 'Listet die Abschnitte einer Grenze, in denen die Werte im Band blieben, mit ihren Zeiten, Werten und ihrem Ergebnis.',
  auditResult: 'Zeigt, ob eine Grenze bestanden, nicht bestanden oder nicht eindeutig ist.',
  formatQuantity: 'Formatiert eine Zahl mit ihrer Einheit, z. B. 1575 °C. Einheiten wie °C, %, bar oder m³/h werden wie im Druck gesetzt; andere Einheiten folgen der Zahl als Text. Die Zahl wird auf die Nachkommastellen gerundet, auf zwei, wenn keine angegeben sind.',
  formatValue: 'Formatiert einen einzelnen Wert: Datum und Uhrzeit, Zahlen, Ja oder Nein sowie Text. Tabellen und beschriftete Werte formatieren damit jeden Wert.',
  keyValueGrid: 'Ordnet beschriftete Werte in Zeilen an, z. B. die Details einer Charge. Jede Zeile enthält Paare aus Schlüssel und Wert.',
  keyValuePair: 'Zeigt einen Wert unter seiner Beschriftung. Der Wert wird von der Komponente für Werte formatiert.',
  prothermBatchRecipe: 'Zeigt die Schritte eines Protherm-Chargenrezepts mit Sollwerten, digitalen Ausgängen, Endebedingungen und Dauer, und auf Wunsch die Namen der digitalen Ausgänge und Bedingungen.',
  prothermBeltRecipe: 'Zeigt die Zonen eines Protherm-Bandrezepts mit ihrer Position im Ofen, und auf Wunsch ihre Sollwerte mit Toleranzen und Alarmzeit.',
  qualityChartTest: 'Zeigt das Diagramm der Soll- und Messwerte einer Diagrammprüfung und eine Tabelle ihrer Werte.',
  qualityFileTest: 'Zeigt den Namen der Datei einer Dateiprüfung.',
  qualityImageTest: 'Zeigt das Bild einer Bildprüfung.',
  qualitySample: 'Zeigt eine Probe eines Qualitätsberichts mit ihren benutzerdefinierten Feldern und ihren Prüfungen.',
  qualityTableTest: 'Zeigt die Merkmale einer Tabellenprüfung mit ihrer Anforderung und dem gemessenen Ergebnis.',
  qualityTest: 'Zeigt eine Prüfung mit Name, Ergebnis und Kommentar sowie den Details ihrer Prüfungsart.',
  qualityTestResult: 'Zeigt, ob eine Prüfung bestanden wurde oder nicht.',
  table: 'Zeigt eine Tabelle, die über mehrere Seiten fortgesetzt wird. Jede Spalte hat eine Überschrift, optional eine Einheit, einen Anteil an der Breite, ihre Zellen und optional eine Summe.',
  warning: 'Hebt eine Meldung hervor, z. B. dass Daten nicht geladen werden konnten.',
};

const ReportComponentAuditEnvelopeParameter: Record<TComponentsSchema['ReportComponentAuditEnvelopeParameter'], string> = {
  envelope: 'Grenze',
};

const ReportComponentAuditEnvelopeSectionsParameter: Record<TComponentsSchema['ReportComponentAuditEnvelopeSectionsParameter'], string> = {
  envelope: 'Grenze',
};

const ReportComponentAuditResultParameter: Record<TComponentsSchema['ReportComponentAuditResultParameter'], string> = {
  result: 'Ergebnis',
};

const ReportComponentFormatQuantityParameter: Record<TComponentsSchema['ReportComponentFormatQuantityParameter'], string> = {
  decimal_places: 'Nachkommastellen',
  unit: 'Einheit',
  value: 'Wert',
};

const ReportComponentFormatValueParameter: Record<TComponentsSchema['ReportComponentFormatValueParameter'], string> = {
  value: 'Wert',
};

const ReportComponentKeyValueGridParameter: Record<TComponentsSchema['ReportComponentKeyValueGridParameter'], string> = {
  rows: 'Zeilen',
};

const ReportComponentKeyValuePairParameter: Record<TComponentsSchema['ReportComponentKeyValuePairParameter'], string> = {
  key: 'Beschriftung',
  value: 'Wert',
};

const ReportComponentProthermBatchRecipeParameter: Record<TComponentsSchema['ReportComponentProthermBatchRecipeParameter'], string> = {
  does_include_setpoints: 'Jeder Sollwert in einer eigenen Zeile',
  does_include_signal_legend: 'Legende der Signale',
  specification: 'Rezept',
};

const ReportComponentProthermBeltRecipeParameter: Record<TComponentsSchema['ReportComponentProthermBeltRecipeParameter'], string> = {
  does_include_setpoints: 'Jeder Sollwert in einer eigenen Zeile',
  does_include_signal_legend: 'Legende der Signale',
  specification: 'Rezept',
};

const ReportComponentQualityChartTestParameter: Record<TComponentsSchema['ReportComponentQualityChartTestParameter'], string> = {
  test: 'Prüfung',
};

const ReportComponentQualityFileTestParameter: Record<TComponentsSchema['ReportComponentQualityFileTestParameter'], string> = {
  test: 'Prüfung',
};

const ReportComponentQualityImageTestParameter: Record<TComponentsSchema['ReportComponentQualityImageTestParameter'], string> = {
  test: 'Prüfung',
};

const ReportComponentQualitySampleParameter: Record<TComponentsSchema['ReportComponentQualitySampleParameter'], string> = {
  sample: 'Probe',
};

const ReportComponentQualityTableTestParameter: Record<TComponentsSchema['ReportComponentQualityTableTestParameter'], string> = {
  test: 'Prüfung',
};

const ReportComponentQualityTestParameter: Record<TComponentsSchema['ReportComponentQualityTestParameter'], string> = {
  test: 'Prüfung',
};

const ReportComponentQualityTestResultParameter: Record<TComponentsSchema['ReportComponentQualityTestResultParameter'], string> = {
  result: 'Ergebnis',
};

const ReportComponentTableParameter: Record<TComponentsSchema['ReportComponentTableParameter'], string> = {
  columns: 'Spalten',
};

const ReportComponentWarningParameter: Record<TComponentsSchema['ReportComponentWarningParameter'], string> = {
  message: 'Meldung',
};

const ReportScopeType: Record<TComponentsSchema['ReportScopeType'], string> = {
  assetTimeRange: 'Zeitraum einer Anlage',
  auditResultReference: 'Audit',
  jobReference: 'Charge',
  qualityReportReference: 'Qualitätsbericht',
};

const ReportTemplateIdType: Record<TComponentsSchema['ReportTemplateIdType'], string> = {
  '__base__': 'Basislayout',
  'qmulus.audit': 'Auditbericht',
  'qmulus.quality': 'Qualitätsbericht',
  'qmulus.report_builder.heat_treatment_report': 'Wärmebehandlungsbericht',
};

const ReportTemplateKind: Record<TComponentsSchema['ReportTemplateKind'], string> = {
  blocks: 'Blöcke',
  template: 'Vorlage',
};

const ReportTemplateUpsert: Partial<TComponentKeys<'ReportTemplateUpsert'>> = {
  header: 'Kopfzeile',
  footer: 'Fußzeile',
  content: 'Inhalt',
  blocks: 'Blöcke',
};

const ReportTemplateHeader: Partial<TComponentKeys<'ReportTemplateHeader-Input'>> = {
  height: 'Höhe',
  contentLeft: 'Inhalt links',
  contentCenter: 'Inhalt mittig',
  contentRight: 'Inhalt rechts',
};

const ReportTemplateFooter: Partial<TComponentKeys<'ReportTemplateFooter-Input'>> = {
  contentLeft: 'Inhalt links',
  contentCenter: 'Inhalt mittig',
  contentRight: 'Inhalt rechts',
};

const Job: TComponentKeys<'Job'> = {
  assetId: generalTerm.asset,
  displayName: generalTerm.jobDisplayName,
  durationSeconds: 'Dauer',
  description: generalTerm.description,
  startedAtUtc: generalTerm.jobStartedAtUtc,
  finishedAtUtc: generalTerm.jobFinishedAtUtc,
  finishedOverflowAtUtc: 'Tatsächliche Endzeit',
  treatmentSpecificationDisplayName: generalTerm.recipe,
  hasAlarms: 'Störungen',
  jobVerdict: 'Bewertung',
  parentJobId: 'Prozessablauf',
  isProductive: 'Als Produktiv markieren',
  status: 'Status',
  plannedStartAtUtc: 'Geplanter Startzeitpunkt',
  plannedFinishAtUtc: 'Geplanter Endzeitpunkt',
  plannedDurationSeconds: 'Geplanter Dauer',
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  startedBy: 'Gestartet von',
  treatmentSpecificationId: generalTerm.recipe,
  treatmentSpecificationRevision: generalTerm.recipe,
  jobId: generalTerm.jobId,
  assetHierarchyLevel: generalTerm.asset,
  previousJobId: 'Vorheriger Auftrag',
  nextJobId: 'Nächster Auftrag',
  parentAssetId: generalTerm.asset,
  startedOrPlannedTimeAtUtc: generalTerm.jobStartedAtUtc,
  finishedOrPlannedTimeAtUtc: generalTerm.jobFinishedAtUtc,
  plannedOrActualDurationSeconds: 'Dauer',
  isActive: 'Aktiv',
};

const MaterialCalculationModelOutput: Partial<TComponentKeys<'MaterialCalculationModel'>> = {
  displayName: 'Name',
  description: 'Beschreibung',
};

const JobStage: Partial<TComponentKeys<'JobStage'>> | Record<'state', string> = {
  assetId: generalTerm.asset,
  displayName: generalTerm.recipeStep,
  state: 'Status',
  jobDisplayName: generalTerm.jobDisplayName,
  startedAtUtc: generalTerm.jobStartedAtUtc,
  finishedAtUtc: generalTerm.jobFinishedAtUtc,
  durationSeconds: 'Dauer',
  stageNo: 'Nr',
};

const JobStatus: Record<TJobStatus, string> = {
  PLANNED: 'Geplant',
  STARTED: 'Gestartet',
  FINISHED: 'Abgeschlossen',
};

const OrderStatus: Record<TOrderStatus, string> = {
  CREATED: 'Erstellt',
  RELEASED: 'Freigegeben',
  STARTED: 'Gestartet',
  PAUSED: 'Pausiert',
  FULFILLED: 'Erfüllt',
  CANCELLED: 'Storniert',
};

const AlarmSeverity: Record<components['schemas']['AlarmSeverity'], string> = {
  0: 'Info',
  1: 'Warnung',
  2: 'Alarm',
  3: 'Notfall',
};

const AlarmEventSeverity: Record<components['schemas']['AlarmEventSeverity'], string> = {
  warning: 'Warnung',
  alarm: 'Alarm',
  emergency: 'Notfall',
};

const HandlingState: Record<components['schemas']['HandlingState'], string> = {
  [-99]: 'Defekt',
  [-10]: 'Fehlgeschlagen',
  [-5]: 'Fehlgeschlagen (erwartet)',
  [0]: 'Registriert',
  [10]: 'In Bearbeitung',
  [20]: 'Abgeschlossen',
};

const LogFileState: Partial<TComponentKeys<'LogFileState'>> = {
  filePathId: 'Dateipfad',
  state: 'Status',
  jobId: generalTerm.job,
  discoveredAtUtc: 'Entdeckt am',
  lastModifiedAtUtc: generalTerm.lastModifiedAt,
};

const RootCauseGroup: Partial<TComponentKeys<'RootCauseGroup'>> = {
  displayName: 'Name der Ursachengruppe',
  description: generalTerm.description,
};

const EquipmentProfile: Partial<TComponentKeys<'ProfileTreeNode'>> = {
  label: 'Profilname',
  combinator: 'Kombination Typ',
};

const TimeBucket_AssetUtilization_: Partial<TComponentKeys<'TimeBucket_AssetUtilization_'>> = {
  earliestDateUtc: 'Start date',
  latestDateUtc: 'End date',
};

const EquipmentProfileMeaning: Partial<TComponentKeys<'Meaning'>> = {
  label: 'Bedeutung des Namens',
  description: 'Beschreibung',
  physicalDimensionId: 'Physikalische Größe',
  preferredUnitId: 'Bevorzugte Einheit',
};

const EquipmentTreeNavigation: Partial<TComponentKeys<'AssetTreeNode'>> = {
  displayName: 'Name',
};

const MaterialGroup: Partial<TComponentKeys<'MaterialGroup'>> = {
  displayName: generalTerm.name,
  description: generalTerm.description,
  materialIds: 'Werkstoffe',
};

const MaterialCalculationModelElementCoefficient: Partial<TComponentKeys<'MaterialCalculationModelElementCoefficient'>> = {
  alloyingElementId: 'Element',
  description: generalTerm.description,
  eJcAustenite: 'eJC (Austenit)',
  eJcFerrite: 'eJC (Ferrit)',
  eJnAustenite: 'eJN (Austenit)',
  eJnFerrite: 'eJN (Ferrit)',
  rJjcAustenite: 'rJJC (Austenit)',
  rJjcFerrite: 'rJJC (Ferrit)',
  rJjnAustenite: 'rJJN (Austenit)',
  rJjnFerrite: 'rJJN (Ferrit)',
};

const MaintenanceState: Record<EMaintenanceState, string> = {
  [EMaintenanceState.PENDING]: 'Ausstehend',
  [EMaintenanceState.ACCEPTED]: 'Angenommen',
  [EMaintenanceState.IN_PROGRESS]: 'In Bearbeitung',
  [EMaintenanceState.DONE]: 'Erledigt',
  [EMaintenanceState.VALIDATED]: 'Validiert',
};

const MaintenancePriority: Record<EMaintenancePriority, string> = {
  [EMaintenancePriority.LOW]: 'Niedrig',
  [EMaintenancePriority.NORMAL]: 'Normal',
  [EMaintenancePriority.HIGH]: 'Hoch',
};

const MaintenancePeriod: Record<ECalendarPeriod, string> = {
  [ECalendarPeriod.WEEK]: 'Woche',
  [ECalendarPeriod.MONTH]: 'Monat',
  [ECalendarPeriod.YEAR]: 'Jahr',
};

const MaintenanceRecurrenceMonthDayValue: Record<EMaintenanceRecurrenceMonthDayValue, string> = {
  [EMaintenanceRecurrenceMonthDayValue.FIRST]: 'Erster',
  [EMaintenanceRecurrenceMonthDayValue.SECOND]: 'Zweiter',
  [EMaintenanceRecurrenceMonthDayValue.THIRD]: 'Dritter',
  [EMaintenanceRecurrenceMonthDayValue.FOURTH]: 'Vierter',
  [EMaintenanceRecurrenceMonthDayValue.LAST]: 'Letzter',
};

const MaintenanceRecurrenceMonthDayDimension: Record<EMaintenanceRecurrenceMonthDayDimension, string> = {
  [EMaintenanceRecurrenceMonthDayDimension.WEEKDAY]: 'Wochentag',
  [EMaintenanceRecurrenceMonthDayDimension.WEEKEND_DAY]: 'Wochenendtag',
  [EMaintenanceRecurrenceMonthDayDimension.DAY]: 'Kalendertag | Kalendertage',
};

const MaintenanceRecurrenceEndCondition: Record<EMaintenanceRecurrenceEndCondition, string> = {
  [EMaintenanceRecurrenceEndCondition.AFTER]: 'nach',
  [EMaintenanceRecurrenceEndCondition.ON_THIS_DAY]: 'an diesem Tag',
};

const MetadataWithChartConfigInput: Partial<TComponentKeys<'MetadataWithChartConfig'>> = {
  ...Metadata,
};

const RootCause: Partial<TComponentKeys<'RootCause'> & {
  count: string,
}> = {
  displayName: 'Ursache',
  description: generalTerm.description,
  count: 'Anzahl',
};

const JobValueData: Partial<TComponentKeys<'JobValueData'>> = {
  jobId: generalTerm.jobId,
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartUtc: generalTerm.jobStartedAtUtc,
  jobEndUtc: generalTerm.jobFinishedAtUtc,
  value: 'Wert',
  recipeId: 'Rezept-Nr.',
  recipeRevision: 'Rezept-Revision',
  recipeDisplayName: generalTerm.recipe,
};

const QualityPreset: Partial<TComponentKeys<'QualityPreset'>> = {
  displayName: generalTerm.name,
};

const Observation: Partial<TComponentKeys<'Observation'>> = {
  assetDisplayName: generalTerm.asset,
  displayName: 'Beobachtung',
  observationTimeAtUtc: 'Zeitpunkt der Beobachtung',
  jobFeatureDisplayName: 'Chargenbeschreibung',
  observationType: 'Beobachtungstyp',
  jobValue: 'Wert',
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartedAtUtc: generalTerm.jobStartedAtUtc,
  jobFinishedAtUtc: generalTerm.jobFinishedAtUtc,
};

const QuenchingMedia: Partial<TComponentKeys<'QuenchingMedia'>> = {
  displayName: generalTerm.name,
  maxIntensity: 'Maximale Intensität',
  minIntensity: 'Minimale Intensität',
  quenchingMediaId: 'Id',
};

const expressionFunctionCategories: Record<TFunctionCategory, string> & {
  all: string,
} = {
  all: 'Alle',
  arithmetic: 'Arithmetische Funktionen',
  cumulative: 'Kumulative Funktionen',
  custom: 'Benutzerdefinierte Funktionen',
  exponential: 'Exponential Funktionen',
  generative: 'Generative Funktionen',
  imputation: 'Imputations Funktionen',
  logical: 'Logische Funktionen',
  numerical: 'Numerische Funktionen',
  operator: 'Operatoren',
  other: 'Andere Funktionen',
  sections: 'Bereichsfunktionen',
  statistical: 'Statistische Funktionen',
  trigonometric: 'Trigonometrische Funktionen',
};

const expressionFunctionsExplanations: {
  functions: Record<TFunctionDefinition['functionType'], string>,
  operators: Record<TOperatorDefinition['operatorType'], string>,
} = {
  functions: {
    abs: 'Diese Funktion berechnet die absoluten Werte eines Signals elementweise.',
    acos: 'Diese Funktion berechnet den Arkuskosinus jedes Elements eines Signals.',
    add: 'Addition. Addiert zwei Operanden zusammen.',
    asin: 'Diese Funktion berechnet den Arkussinus jedes Elements eines Signals.',
    atan2: 'Diese Funktion berechnet den Arkustangens von a/b für jedes entsprechende Elementenpaar.',
    atan: 'Diese Funktion berechnet den Arkustangens jedes Elements eines Signals.',
    avg: 'Wenn mehrere Argumente gegeben sind, berechnet diese Funktion den Durchschnitt über jedes Signal. Andernfalls berechnet sie den Durchschnitt aller Proben.',
    avg_windowed: 'Diese Funktion berechnet den Mittelwert der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    bfill: 'Füllt NaN-Werte, indem der nächste gültige Wert verwendet wird, um die Lücke zu füllen.',
    ceil: 'Diese Funktion rundet jedes Element eines Signals auf die nächste Ganzzahl auf.',
    clamp: 'Diese Funktion klemmt die Werte eines Signals innerhalb eines angegebenen Bereichs ab.',
    cos: 'Diese Funktion berechnet den Kosinus jedes Elements eines Signals.',
    count_samples: 'Accumulates the number of samples where the input signal evaluates to true.',
    count_seconds: 'Accumulates the seconds where the input signal evaluates to true.',
    counter: 'Diese Funktion erhöht einen Zähler für jeden Zeitschritt um 1.',
    cumulative_avg: 'Gibt den kumulierten Durchschnitt des Signals zurück.',
    cumulative_integrate: 'Gibt das kumulierte Integral (unter Verwendung der Trapezregel über Sekunden) zurück.',
    cumulative_max: 'Gibt das kumulierte Maximum des Signals zurück.',
    cumulative_mean: 'Gibt den kumulierten Mittelwert des Signals zurück.',
    cumulative_median: 'Gibt den kumulierten Median des Signals zurück.',
    cumulative_min: 'Gibt das kumulierte Minimum des Signals zurück.',
    cumulative_range: 'Gibt den kumulierten Bereich des Signals zurück.',
    cumulative_std: 'Gibt die kumulierte Standardabweichung des Signals zurück.',
    cumulative_sum: 'Gibt die kumulierte Summe des Signals zurück, wobei "Not a Number" (NaN) als Null behandelt wird. Die kumulierte Summe ändert sich nicht, wenn NaNs auftreten, und führende NaNs werden durch Nullen ersetzt. Für Signale, die nur NaN oder leer sind, werden Nullen zurückgegeben.',
    cumulative_var: 'Gibt die kumulierte Varianz des Signals zurück.',
    degrees: 'Diese Funktion wandelt jedes Element eines Signals von Radianten in Grad um.',
    denormalize: 'Diese Funktion denormalisiert die Werte eines Signals vom Bereich [0, 1] in den angegebenen Bereich.',
    diff: 'Diese Funktion berechnet die Differenz zwischen aufeinanderfolgenden Elementen eines Signals.',
    divide: 'Division. Teilt den ersten Operanden durch den zweiten.',
    do_nothing: 'Diese Funktion gibt das Eingangssignal zurück.',
    elapsed_seconds: 'Diese Funktion gibt die Anzahl der vergangenen Sekunden seit Beginn des Zeitrahmens zurück.',
    equals: 'Gleichheit. Gibt true zurück, wenn die Operanden gleich sind, ansonsten false.',
    exp: 'Diese Funktion berechnet die Exponentialfunktion jedes Elements eines Signals.',
    fill: 'Füllt NaN-Werte in Signal a mit Werten von Signal b.',
    ffill: 'Füllt NaN-Werte, indem der letzte gültige Wert zum nächsten gültigen propagiert wird.',
    floor: 'Diese Funktion rundet jedes Element eines Signals auf die nächste Ganzzahl ab.',
    floor_divide: 'Ganzzahlige Division. Gibt den Quotienten der Division zurück und verwirft dabei den Rest.',
    gradient: 'Numerische Ableitung des Eingangssignals pro Sekunde (1/s).',
    gradient_windowed: 'Numerische Ableitung über ein gegebenes dx (in Sekunden). Falls dx negativ oder null ist, ist der Rückgabewert NaN. Der parameter dx wird als Konstante interpretiert.',
    greater_than: 'Größer als. Gibt true zurück, wenn der erste Operand größer als der zweite ist.',
    greater_than_or_equal: 'Größer als oder gleich. Gibt true zurück, wenn der erste Operand größer oder gleich dem zweiten ist.',
    if: 'Diese Funktion führt eine bedingte Vorgang elementweise basierend auf einer Bedingung durch.',
    integrate: 'Diese Funktion integriert das Signal über Sekunden unter Verwendung der Trapezregel.',
    interpfill: 'Füllt NaN-Werte, indem linear zwischen dem letzten und dem nächsten gültigen Wert interpoliert wird.',
    is_finite: 'Diese Funktion überprüft, ob die Elemente eines Signals elementweise endlich sind.',
    less_than: 'Kleiner als. Gibt true zurück, wenn der erste Operand kleiner als der zweite ist.',
    less_than_or_equal: 'Kleiner als oder gleich. Gibt true zurück, wenn der erste Operand kleiner oder gleich dem zweiten ist.',
    ln: 'Diese Funktion berechnet den natürlichen Logarithmus jedes Elements eines Signals.',
    log10: 'Diese Funktion berechnet den Logarithmus zur Basis 10 jedes Elements eines Signals.',
    log2: 'Diese Funktion berechnet den Logarithmus zur Basis 2 jedes Elements eines Signals.',
    log: 'Diese Funktion berechnet den Logarithmus jedes Elements eines Signals zur Basis b.',
    logical_and: 'Logisches UND. Gibt true zurück, wenn beide Operanden true sind, ansonsten false.',
    logical_not: 'Unäres logisches NICHT. Gibt true zurück, wenn das Operand falsch ist, und false, wenn das Operand wahr ist.',
    logical_or: 'Logisches ODER. Gibt true zurück, wenn mindestens einer der Operanden true ist, ansonsten false.',
    logical_xor: 'Logisches XOR. Gibt false zurück, wenn beide Operatoren den gleichen logischen Zustand haben, ansonsten true.',
    max: 'Wenn mehrere Argumente gegeben sind, findet diese Funktion den maximalen Wert unter den Signalen. Andernfalls findet sie den maximalen Wert unter allen Proben.',
    max_windowed: 'Diese Funktion berechnet das Maximum der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    mean: 'Wenn mehrere Argumente gegeben sind, berechnet diese Funktion den Durchschnitt über jedes Signal. Andernfalls berechnet sie den Durchschnitt aller Proben.',
    mean_windowed: 'Diese Funktion berechnet den Mittelwert der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    median: 'Wenn mehrere Argumente gegeben sind, findet diese Funktion den Medianwert unter den Signalen. Andernfalls findet sie den Medianwert unter allen Proben.',
    median_windowed: 'Diese Funktion berechnet den Median der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    min: 'Wenn mehrere Argumente gegeben sind, findet diese Funktion den maximalen Wert unter den Signalen. Andernfalls findet sie den maximalen Wert unter allen Proben.',
    min_windowed: 'Diese Funktion berechnet das Minimum der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    modulo: 'Modulus. Gibt den Rest der Division des ersten Operanden durch den zweiten zurück.',
    multiply: 'Multiplikation. Multipliziert zwei Operanden miteinander.',
    negate: 'Diese Funktion negiert jedes Element eines Signals.',
    normalize: 'Diese Funktion normalisiert die Werte eines Signals auf den Bereich [0, 1].',
    not_equals: 'Ungleichheit. Gibt true zurück, wenn die Operanden ungleich sind, ansonsten false.',
    percentile: 'Diese Funktion berechnet das Perzentil der Signalwerte.',
    power: 'Exponentialfunktion. Erhöht den ersten Operanden zur Potenz des zweiten Operanden.',
    radians: 'Diese Funktion wandelt jedes Element eines Signals von Grad in Radianten um.',
    ramp: 'Diese Funktion gibt eine lineare Rampe von 0 bis 1 zurück.',
    random: 'Diese Funktion gibt für jeden Zeitschritt eine Zufallszahl zwischen 0 und 1 zurück.',
    range: 'Wenn mehrere Argumente gegeben sind, berechnet diese Funktion den Bereich (max - min) über jedes Signal. Andernfalls berechnet sie den Bereich aller Proben.',
    range_windowed: 'Diese Funktion berechnet den Bereich der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    relative_error: 'Berechnet den relativen Fehler in % an Stellen wo reference != 0.',
    round: 'Diese Funktion rundet jedes Element eines Signals auf die nächste Ganzzahl.',
    sections_join: 'Kombiniert alle Abschnitte deren Abstand kürzer als die gewählte Grenze ist.',
    sections_first: 'Gibt den ersten verfügbaren Abschnitt zurück. Kann keinen Abschnitt zurückgeben, wenn keiner existiert.',
    sections_last: 'Gibt den letzten verfügbaren Abschnitt zurück. Kann keinen Abschnitt zurückgeben, wenn keiner existiert.',
    sections_nth: 'Gibt den n-ten Abschnitt zurück, falls existent. Um von hinten zu addressieren, kann negative Indizierung verwendet werden. Die Indizierung beginnt bei 0.',
    sections_shortest: 'Gibt die kürzeste(n) Abschnitt(e) zurück. Kann mehrere Abschnitte zurückgeben, falls es mehrere Abschnitte mit minimaler Länge gibt.',
    sections_median_duration: 'Gibt den/die Abschnitt(e) mit der mittleren Dauer zurück. Kann mehrere Abschnitte zurückgeben, wenn es mehrere Abschnitte mit der mittleren Länge gibt.',
    sections_longest: 'Gibt den/die längsten Abschnitt(e) zurück. Kann mehrere Abschnitte zurückgeben, wenn es mehrere Abschnitte mit maximaler Länge gibt.',
    sections_max_value: 'Gibt den/die Abschnitt(e) mit dem größten Wert zurück. Kann mehrere Abschnitte zurückgeben, wenn mehrere Abschnitte mit dem absoluten Maximalwert vorhanden sind.',
    sections_median_value: 'Gibt den Abschnitt zurück, der den Medianwert enthält. Kann mehrere Abschnitte zurückgeben, wenn mehrere Abschnitte mit dem Medianwert vorhanden sind.',
    sections_min_value: 'Gibt den/die Abschnitt(e) mit dem kleinsten Wert zurück. Kann mehrere Abschnitte zurückgeben, wenn mehrere Abschnitte mit dem absoluten Minimalwert vorhanden sind.',
    sign: 'Diese Funktion gibt das Vorzeichen jedes Elements eines Signals zurück.',
    sin: 'Diese Funktion berechnet den Sinus jedes Elements eines Signals.',
    sqrt: 'Diese Funktion berechnet die Quadratwurzel jedes Elements eines Signals.',
    stage_number: 'Liefert die Nummer des aktuellen Rezeptschrittes, sofern ein job aktiv ist. Ansonsten NaN.',
    std: 'Wenn mehrere Argumente gegeben sind, berechnet diese Funktion die Standardabweichung über jedes Signal. Andernfalls berechnet sie die Standardabweichung aller Proben.',
    std_windowed: 'Diese Funktion berechnet die Standardabweichung der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    steady_state_detection: 'Diese Funktion erlaubt es Bereiche mit nahezu Konstanten werten zu finden. Die Windowlength erlaubt das Glätten des Eingangssignals und der Grenzwert erlaubt es eine maximale Steigung zu definieren bei das Signal noch als Konstant angenommen wird.',
    subtract: 'Subtraktion. Subtrahiert den zweiten Operanden vom ersten.',
    sum: 'Wenn mehrere Argumente gegeben sind, berechnet diese Funktion die Summe über jedes Signal. Andernfalls berechnet sie die Summe aller Proben.',
    sum_windowed: 'Diese Funktion berechnet die Summe der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    tan: 'Diese Funktion berechnet den Tangens jedes Elements eines Signals.',
    timestamp: 'Diese Funktion gibt die Millisekunden seit der Epoche (1970-01-01 00:00:00 UTC) zurück.',
    total_seconds: 'Gibt die Gesamtzeit des angezeigen Zeitraum in Sekunden als Konstante zurück.',
    trunc: 'Diese Funktion kürzt jedes Element eines Signals in Richtung Null ab.',
    var: 'Wenn mehrere Argumente gegeben sind, berechnet diese Funktion die Varianz über jedes Signal. Andernfalls berechnet sie die Varianz aller Proben.',
    var_windowed: 'Diese Funktion berechnet die Varianz der Signalwerte über die angegebene Fensterlänge in Sekunden. Eine negative Fensterlänge wird in eine Fensterlänge von 0 umgewandelt, was zu NaN führt.',
    first: 'Diese Funktion gibt eine Konstante des ersten Samples zurück.',
    last: 'Diese Funktion gibt eine Konstante des letzten Samples zurück.',
    shift: 'Verschiebt das Signal um die angegebene Anzahl von Samples. Positive Werte verschieben nach rechts und negative Werte nach links. Die neuen Werte werden mit NaN aufgefüllt.',

  },
  operators: {
    '!': 'Logisches NICHT. Gibt true zurück, wenn das Operand falsch ist, und false, wenn das Operand wahr ist.',
    '-': 'Subtraktion. Subtrahiert den zweiten Operanden vom ersten.',
    '+': 'Addition. Addiert zwei Operanden zusammen.',
    '^': 'Bitweise XOR. Gibt an jeder Bitposition eine 1 zurück, an der die entsprechenden Bits beider Operanden unterschiedlich sind.',
    '**': 'Exponentialfunktion. Erhöht den ersten Operanden zur Potenz des zweiten Operanden.',
    '*': 'Multiplikation. Multipliziert zwei Operanden miteinander.',
    '//': 'Ganzzahlige Division. Gibt den Quotienten der Division zurück und verwirft dabei den Rest.',
    '/': 'Division. Teilt den ersten Operanden durch den zweiten.',
    '%': 'Modulo. Gibt den Rest der Division des ersten Operanden durch den zweiten zurück.',
    '==': 'Gleichheit. Gibt true zurück, wenn die Operanden gleich sind, ansonsten false.',
    '!=': 'Ungleichheit. Gibt true zurück, wenn die Operanden ungleich sind, ansonsten false.',
    '<': 'Kleiner als. Gibt true zurück, wenn der erste Operand kleiner als der zweite ist.',
    '<=': 'Kleiner als oder gleich. Gibt true zurück, wenn der erste Operand kleiner oder gleich dem zweiten ist.',
    '>': 'Größer als. Gibt true zurück, wenn der erste Operand größer als der zweite ist.',
    '>=': 'Größer als oder gleich. Gibt true zurück, wenn der erste Operand größer oder gleich dem zweiten ist.',
    '&&': 'Logisches UND. Gibt true zurück, wenn beide Operanden true sind, ansonsten false.',
    '||': 'Logisches ODER. Gibt true zurück, wenn mindestens einer der Operanden true ist, ansonsten false.',
  },
};

const DataMigrationState: TComponentKeys<'DataMigrationState'> = {
  assetId: generalTerm.assetId,
  displayName: generalTerm.displayName,
  assetDisplayName: generalTerm.asset,
  lastTimestampAtUtc: 'Neuste Daten',
  lastEtlRunAtUtc: 'Letzte Migration',
  etlLagSeconds: 'Migration lag',
  dataId: 'Identifizierer',
  dataFormat: 'Typ',
  dataSourceReference: generalTerm.dataSourceReference,
};

const JobMigrationState: TComponentKeys<'JobMigrationState'> = {
  assetId: generalTerm.assetId,
  assetDisplayName: generalTerm.asset,
  lastTimestampAtUtc: 'Neuste Daten',
  lastEtlRunAtUtc: 'Letzte Migration',
  etlLagSeconds: 'Migration lag',
};

const QmulusDriver = {
  PT9800: {
    DataMigrationState,
    JobMigrationState,
  },
};

const TableTestData: Partial<TComponentKeys<'TableTestData'>> = {
  jobDisplayName: generalTerm.jobDisplayName,
  jobStartUtc: generalTerm.jobStartedAtUtc,
  jobEndUtc: generalTerm.jobFinishedAtUtc,
  value: 'Wert',
  recipeDisplayName: generalTerm.recipe,
  partDisplayName: generalTerm.part,
  sampleDisplayName: 'Stichprobe',
};

const ModelBuilderModelOverview: Partial<TComponentKeys<'ModelBuilderModelOverview'>> = {
  accuracy: 'Genauigkeit',
  assetId: generalTerm.asset,
  createdAtUtc: generalTerm.createdAt,
  createdByDisplayName: 'Autor',
  displayName: 'Modell',
  inputDisplayNames: 'Eingabesignale',
  lifecycleState: 'Status',
  modelRevision: 'Version',
  modelType: 'Modelltyp',
  outputDisplayName: 'Ausgabesignal',
  revisionCount: 'Versionen',
};

const ModelLifecycleState: Record<EModelLifecycleState, string> = {
  [EModelLifecycleState.REGISTERED]: 'Registriert',
  [EModelLifecycleState.LOADING_DATA]: 'Daten werden geladen',
  [EModelLifecycleState.PREPARING_DATA]: 'Trainings daten werden vorbereitet',
  [EModelLifecycleState.TRAINING]: 'Training läuft',
  [EModelLifecycleState.SAVING_MODEL]: 'Model wird gespeichert',
  [EModelLifecycleState.CREATING_METADATA]: 'Signal definitionen werden erstellen',
  [EModelLifecycleState.READY]: 'Bereit',
};

const ModelBuilderModelType: Record<components['schemas']['ModelBuilderModelType'], string> = {
  LINEAR_REGRESSION: 'Lineare Regression',
  SMART_REGRESSION: 'Smart Regression',
};

const ModelBuilderTrainingRequest: Partial<TComponentKeys<'ModelBuilderTrainingRequest'>> = {
  displayName: 'Modelname',
  testSize: 'Anteil Testdaten',
};

const TimeBucketType: Record<TComponentValue<'TimeBucketType'>, string> = {
  day: 'Tage',
  week: 'Woche',
  month: 'Monat',
  quarter: 'Quartal',
  year: 'Jahr',
  full: 'Gesamtzeitraum',
  auto: 'Automatisch',
};

const AlarmStatistics: Partial<TComponentKeys<'AlarmStatistics'>> = {
  displayName: generalTerm.displayName,
  severity: AlarmDefinition.severity,
};

const AssetBlobIndex: Partial<TComponentKeys<'AssetBlobIndex'>> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  semanticMeaningId: 'Kategorie',
  ownerDisplayName: 'Hochgelanden von',
};

const BlobMeaning: Record<TComponentValue<'BlobMeaning'>, string> = {
  [EBlobMeaning.MANUAL]: 'Anleitung/Handbuch',
  [EBlobMeaning.DRAWING]: 'Zeichnung',
  [EBlobMeaning.CONFIGURATION]: 'Konfiguration',
  [EBlobMeaning.BILL_OF_MATERIALS]: 'Stückliste',
  [EBlobMeaning.TENANT_LOGO]: 'Logo',
  [EBlobMeaning.PART_IMAGE]: 'Bauteilbild',
};

const Meaning: Partial<TComponentKeys<'Meaning'>> = {
  meaningId: 'Bedeutung',
  userDefinedDisplayName: 'Benutzerdefinierter Name',
  userDefinedDescription: generalTerm.description,
  physicalDimensionId: 'Physikalische Größe',
  preferredUnitId: 'Bevorzugte Einheit',
  defaultColor: generalTerm.color,
};

const MaintenanceEventOverview: TComponentKeys<'MaintenanceEventOverview'> = {
  createdByDisplayName: generalTerm.createdBy,
  createdById: generalTerm.createdBy,
  assetDisplayName: generalTerm.asset,
  assetId: generalTerm.asset,
  assetLocation: generalTerm.location,
  assigneeDisplayName: 'Bearbeiter',
  assigneeId: 'Bearbeiter',
  calendarEventType: 'Eventtyp',
  color: generalTerm.color,
  displayName: generalTerm.displayName,
  endAtUtc: generalTerm.jobFinishedAtUtc,
  maintenanceSeriesId: 'Serie',
  occurrenceId: 'Ereignis',
  priority: 'Priorität',
  startAtUtc: generalTerm.jobStartedAtUtc,
  state: 'Status',
};

const MaintenanceEventDetails: Partial<TComponentKeys<'MaintenanceEventDetails'>> = {
  ...MaintenanceEventOverview,
  description: generalTerm.description,
  seriesRecurrence: 'Wiederholung',
  seriesAttachments: 'Anhänge',
  overheadSeconds: 'Zusätzlicher Zeitaufwand',
  taskInstances: 'Wartungsarbeiten',
  assetBlobs: 'Anlagendokumentation',
};

const MaintenanceSeriesCreate: Partial<TComponentKeys<'MaintenanceSeriesCreate'>> = {
  ...MaintenanceEventOverview,
  description: generalTerm.description,
  recurrence: MaintenanceEventDetails.seriesRecurrence,
  overheadSeconds: MaintenanceEventDetails.overheadSeconds,
  taskTemplates: MaintenanceEventDetails.taskInstances,
  attachments: MaintenanceEventDetails.seriesAttachments,
};

const CalendarEventTag: TComponentKeys<'CalendarEventTag'> = {
  calendarEventTagId: 'Id',
  calendarEventType: MaintenanceEventOverview.calendarEventType,
  color: generalTerm.color,
  createdAtUtc: generalTerm.createdAt,
  createdByDisplayName: generalTerm.createdBy,
  createdByUserId: generalTerm.createdBy,
  deletedAtUtc: generalTerm.deletedAt,
  deletedBy: generalTerm.deletedBy,
  deletedByDisplayName: generalTerm.deletedBy,
  description: generalTerm.description,
  displayName: generalTerm.displayName,
  isDeleted: generalTerm.deleted,
};

const OperationEventOverview: TComponentKeys<'OperationEventOverview'> = {
  assetLocation: generalTerm.location,
  calendarEventId: 'Id',
  createdByDisplayName: generalTerm.createdBy,
  createdById: generalTerm.createdBy,
  displayName: generalTerm.displayName,
  startAtUtc: generalTerm.jobStartedAtUtc,
  endAtUtc: generalTerm.jobFinishedAtUtc,
  assetId: generalTerm.asset,
  assetDisplayName: generalTerm.asset,
  calendarEventType: MaintenanceEventOverview.calendarEventType,
  calendarEventTag: 'Event tag',
};

const OperationEventDetails: TComponentKeys<'OperationEventDetails'> = {
  ...OperationEventOverview,
  description: generalTerm.description,
};

const MaintenanceTaskResultType: Record<TComponentValue<'MaintenanceTaskResultType'>, string> = {
  WORK_DONE: 'Fertig',
  OMITTED: 'Nicht ausgeführt',
};

const MaintenanceTaskReviewType: Record<TComponentValue<'MaintenanceTaskReviewType'>, string> = {
  APPROVED: 'Akzeptiert',
  REJECTED: 'Abgelehnt',
};

const TaskInstance: Partial<TComponentKeys<'TaskInstance'>> = {
  description: generalTerm.description,
  durationSeconds: 'Vorgesehene Dauer',
  attachments: 'Anhänge',
  workResult: 'Status',
  reviewResult: 'Genehmigung',
};

const Role: Partial<TComponentKeys<'Role'>> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  isDefault: 'Standardrolle',
};

const RoleWithPermissions: Partial<TComponentKeys<'RoleWithPermissions'>> = {
  ...Role,
};

const SimulatorAMS275910CompoundLayerClass: Record<ESimulatorAMS275910CompoundLayerClass, string> = {
  [ESimulatorAMS275910CompoundLayerClass.CLASS_0]: 'Klasse 0',
  [ESimulatorAMS275910CompoundLayerClass.CLASS_1]: 'Klasse 1',
  [ESimulatorAMS275910CompoundLayerClass.CLASS_2]: 'Klasse 2',
};

const SimulatorAMS275910SteelClass: Record<ESimulatorAMS275910SteelClass, string> = {
  [ESimulatorAMS275910SteelClass.NITRALLOY_135M]: 'Nitralloy 135M',
  [ESimulatorAMS275910SteelClass.NITRALLOY_EZ]: 'Nitralloy EZ',
  [ESimulatorAMS275910SteelClass.NITRALLOY_N]: 'Nitralloy N',

  [ESimulatorAMS275910SteelClass._4140_4340]: '4140 / 4340',
  [ESimulatorAMS275910SteelClass.DC_D6AC]: 'DC / D6AC',
  [ESimulatorAMS275910SteelClass.H11]: 'H11',
  [ESimulatorAMS275910SteelClass.STAINLESS]: 'Edelstahl',
  [ESimulatorAMS275910SteelClass.CARBON]: 'Kohlenstoffstahl',
};

const SimulatorAMS275912PorosityClass: Record<ESimulatorAMS275912PorosityClass, string> = {
  [ESimulatorAMS275912PorosityClass.CLASS_1]: 'Klasse 1',
  [ESimulatorAMS275912PorosityClass.CLASS_2]: 'Klasse 2',
};

const SimulatorAMS275912SteelClass: Record<ESimulatorAMS275912SteelClass, string> = {
  [ESimulatorAMS275912SteelClass.GROUP_0]: 'Gruppe 0',
  [ESimulatorAMS275912SteelClass.GROUP_1]: 'Gruppe 1',
  [ESimulatorAMS275912SteelClass.GROUP_2]: 'Gruppe 2',
};

const SimulatorPreTreatmentType: Record<ESimulatorPreTreatmentType, string> = {
  [ESimulatorPreTreatmentType.NORMALIZED]: 'Normalisiert',
  [ESimulatorPreTreatmentType.QUENCHED]: 'Abgehärtet',
  [ESimulatorPreTreatmentType.TEMPERED]: 'Vergütet',
};

const SimulatorSimulationPart: Partial<TComponentKeys<'SimulatorSimulationPart'>> = {
  setpointMinSurfaceHardness: 'Min. Oberflächenhärte',
  setpointCaseDepth: 'Aufkohlungstiefe (CHD)',
  setpointCaseDepthHardness: generalTerm.requiredHardness,
  grainSize: generalTerm.grainSize,
  doesUseShape: 'Formsimulationstyp',
  shape: 'Teilegeometrie (DIN 17211)',
  quenchingMediaId: 'Abeschreckmedium',
  quenchingIntensity: generalTerm.quenchingIntensity,
  endTemperature: generalTerm.endTemperature,
  nitridingMinSurfaceHardness: 'Min. Oberflächenhärte',
  nitridingHardnessDepth: generalTerm.nitridingDepth,
  nitridingHardness: 'Grenzhärte',
  nitridingSteelClass: 'Stahlgruppe (AMS 2759-10)',
  nitridingCompoundLayerClass: 'Verbindungsschicht Klasse (AMS 2759-10)',
  nitrocarburizingPorosityClass: 'Porosität (AMS 2759-12)',
  nitrocarburizingSteelClass: 'Stahlgruppe (AMS 2759-12)',
};

const AssetStatusJobInfo: Partial<TComponentKeys<'AssetStatusJobInfo'>> = {
  jobDisplayName: generalTerm.jobDisplayName,
  treatmentSpecificationDisplayName: generalTerm.recipe,
  stageDisplayName: generalTerm.recipeStep,
  stageNo: 'Schritt-Nr.',
};

const AssetStatusOverview: Partial<TComponentKeys<'AssetStatusOverview'>> = {
  activeAlarmCount: 'Aktive Störungen',
  state: 'Status',
};

const AssetState: Record<TComponentValue<'AssetState'>, string> = {
  RUNNING: 'In Betrieb',
  OFFLINE: 'Aus',
  IDLE: 'Leerlauf',
  PLANNED_UNIT_DOWNTIME: CalendarEventType.PLANNED_UNIT_DOWNTIME,
  ACTUAL_UNIT_DOWNTIME: CalendarEventType.ACTUAL_UNIT_DOWNTIME,
  OPERATION_HOLD: CalendarEventType.OPERATION_HOLD,
};

const QmulusLicenseViolationReason: Record<TComponentValue<'QmulusLicenseViolationReason'>, string> = {
  NO_LICENSE_KEY: 'Lizenz fehlt',
  ASSET_LIMIT_VIOLATED: 'Zuviele Anlagen konfiguriert',
  EXPIRED: 'Lizenz abgelaufen',
  EXPIRED_GRACE_PERIOD: 'Lizenz abgelaufen',
  INVALID: 'Ungültige Lizenz',
  USER_LIMIT_VIOLATED: 'Zuviele Benutzer konfiguriert',
  OTHER: 'Lizenzverletzung',
};

const AuditEnvelope: Partial<TComponentKeys<'AuditEnvelope'>> = {
  valueSensorMeanings: 'Sensoren',
  durationMeanings: 'Zeitsensoren',
  stabilizationCombinationType: 'Stabilizierung',
  stabilizationStagesCnt: 'Rezeptschritte',
  stabilizationPeriodSeconds: 'Dauer',
};

const EnvelopeThreshold: TComponentKeys<'EnvelopeThreshold_int_'> = {
  lowerOffset: 'Limit -',
  setpoint: 'Sollwert',
  upperOffset: 'Limit +',
};

const AuditEnvelopStabilizationCombinationType: Record<TComponentValue<'AuditEnvelopStabilizationCombinationType'>, string> = {
  0: 'Addiert',
  1: 'Kürzeste',
  2: 'Längeste',
};

const ScaleType: Record<TComponentValue<'ScaleType'>, string> = {
  linear: 'Linear',
  logarithmic: 'Logarithmisch',
};

const ChemicalElementInSteel: Record<TComponentValue<'ChemicalElementInSteel'>, string> = {
  Fe: 'Eisen',
  C: 'Kohlenstoff',
  Mn: 'Mangan',
  P: 'Phosphor',
  S: 'Schwefel',
  Si: 'Silizium',
  Cr: 'Chrom',
  V: 'Vanadium',
  W: 'Wolfram',
  Mo: 'Molybdän',
  Co: 'Kobalt',
  Ni: 'Nickel',
  Cu: 'Kupfer',
  Al: 'Aluminium',
  Ti: 'Titan',
  Nb: 'Niob',
  B: 'Bor',
  Pb: 'Blei',
  Zr: 'Zirkonium',
  Ta: 'Tantal',
  N: 'Stickstoff',
  Se: 'Selen',
};

const EquipmentPhysicalType: Record<TComponentValue<'EquipmentPhysicalType'>, string> = {
  1: 'Schachtofen',
  2: 'Schachtofen mit Retorte',
  10: 'Glockenofen',
  20: 'Horizontalofen',
  21: 'Horizontalofen mit Retorte',
  30: 'Kastenofen',
  40: 'Mehrkammerofen',
  50: 'Drehtellerofen',
  51: 'Pusher',
  52: 'Ringherdofen',
  100: 'Bandofen',
  110: 'Drehretortenofen',
  120: 'Rüttelherdofen',
  200: 'Lager',
  201: 'Beladeplatz',
  202: 'Entladeplatz',
  203: 'Be-/Entladeplatz',
};

const DataSourceType: Record<TComponentValue<'DataSourceType'>, string> = {
  ...NonTranslatable.dataSourceName,
  production_statistics: AppId[EAppId.PRODUCTION_STATISTICS],
  model_builder: AppId[EAppId.MODEL_BUILDER],
  ml_model: `${AppId[EAppId.MODEL_BUILDER]} (Legacy)`,
  generic: 'Generische Datenquelle / Node-RED',
};

// Do not translate, related to NPM
const NpmSystemIdentifier: TComponentKeys<'NpmSystemIdentifier'> = {
  customerCode: 'customerCode',
  location: 'location',
  systemId: 'systemId',
  subSystemId: 'subSystemId',
};

const PtDatabaseIdentifier: TComponentKeys<'PtDatabaseIdentifier'> = {
  type: 'Type',
  ptDbId: 'PE-Nr.',
};

const WorkplaceIdentifier: TComponentKeys<'WorkplaceIdentifier'> = {
  ...PtDatabaseIdentifier,
  workplaceId: 'Arbeitsplatz-Nr.',
  chamberId: 'Kammer-Nr.',
};

const AutomatIdentifier: TComponentKeys<'AutomatIdentifier'> = {
  ...WorkplaceIdentifier,
  automatNo: 'Automat-Nr.',
};

const AssetParameter: Partial<TComponentKeys<'AssetParameter'>> = {
  namespace: 'Namensraum',
  category: 'Kategorie',
  parameterName: 'Parameter',
  sourceAssetId: 'Quelle',
  value: 'Wert',
};

const AssetParameterDefinition: Partial<TComponentKeys<'AssetParameterDefinition'>> = {
  namespace: AssetParameter.namespace,
  category: AssetParameter.category,
  parameterName: AssetParameter.parameterName,
  physicalDimensionId: Meaning.physicalDimensionId,
  description: generalTerm.description,
  defaultValue: 'Standardwert',
  allowedMin: 'Erlaubter Minimalwert',
  allowedMax: 'Erlaubter Maximalwert',
};

const ExpressionType: Record<Exclude<TValidateExpressionRouteResponse['expression']['type'], undefined> | typeof sinkExpression | typeof sinkVariable, string> = {
  ExpressionConstant: 'Konstante',
  ExpressionOperator: 'Operator',
  ExpressionUnaryOperator: 'Operator',
  ExpressionDataIdentifier: 'Signal',
  ExpressionMeaningIdentifier: 'Bedeutung',
  ExpressionFunction: 'Funktion',
  ExpressionGenerativeFunction: expressionFunctionCategories.generative,
  ExpressionVariableIdentifier: 'Variable',
  ExpressionAssetParameterIdentifier: 'Anlagenparameter',
  SinkExpression: 'Expression',
  SinkVariable: 'Variable Definition',
};

const ExpressionNamedConstant: Record<TComponentValue<'ExpressionNamedConstant'>, string> = {
  pi: 'Kreiszahl π',
  e: 'Eulersche Zahl',
  inf: 'Unendlichkeit',
  nan: 'keine Zahl',
  true: 'wahr',
  false: 'falsch',
  null: 'null',
  millisecond: 'Eine Millisekunde als Sekunden',
  minute: 'Eine Minute als Sekunden',
  hour: 'Eine Stunde als Sekunden',
};

const ExpressionOperatorType: Record<TComponentValue<'ExpressionOperatorType'>, string> = {
  '**': 'Potenzierung',
  '*': 'Multiplikation',
  '//': 'Ganzzahl-Division',
  '/': 'Division',
  '%': 'Modulo',
  '+': 'Addition',
  '-': 'Subtraktion',
  '==': 'gleich',
  '!=': 'ungleich',
  '<': 'kleiner als',
  '<=': 'kleiner oder gleich',
  '>': 'größer als',
  '>=': 'größer oder gleich',
  '^': 'bitweises XOR',
  '&&': 'logisches UND',
  '||': 'logisches ODER',
};

const ExpressionUnaryOperatorType: Record<TComponentValue<'ExpressionUnaryOperatorType'>, string> = {
  '!': 'logisches NICHT',
  '-': 'Negation',
  '+': 'Identität',
};

const ExpressionFunctionTypeAggregation: Record<TComponentValue<'ExpressionFunctionTypeAggregation'>, string> = {
  min: 'Minimum',
  max: 'Maximum',
  range: 'Spannweite',
  sum: 'Summe',
  mean: 'Mittelwert',
  avg: 'Durchschnitt',
  std: 'Standardabweichung',
  var: 'Varianz',
  median: 'Median',
};

const QmulusDashboardWidget: Record<keyof TQmulusDashboardWidgetSharedProps, string> = {
  displayName: generalTerm.displayName,
  widgetType: 'Typ',
  size: 'Größe',
};

const QmulusDashboardWidgetType: Record<TQmulusDashboardWidgetType, string> = {
  anomaly_detection_observations: `${AppId[EAppId.ANOMALY_DETECTION]}: Beobachtungen`,
  camera: 'Kamera',
  gauge: 'Messwert',
  job_features: DataFormat.job_aggregate,
  timeseries: DataFormat.time_series,
};

const QmulusDashboardWidgetObservations: Pick<TComponentKeys<'QmulusDashboardWidgetObservations'>, 'variant'> = {
  variant: 'Variante',
};

const QmulusDashboardWidgetObservationsVariant: Record<components['schemas']['QmulusDashboardWidgetObservations']['variant'], string> = {
  details: 'Details',
  frequency: 'Häufigkeit',
};

const QmulusDashboardMixinData: Pick<TComponentKeys<'QmulusDashboardWidgetGauge'>, 'assetId' | 'dataFormat' | 'data' | 'lookback'> = {
  assetId: generalTerm.assetId,
  dataFormat: Metadata.dataFormat!,
  data: 'Signal',
  lookback: 'Zeitraum',
};

const QmulusDashboardMixinLimits: Pick<TComponentKeys<'QmulusDashboardWidgetGauge'>, 'limitMin' | 'limitMax'> = {
  limitMin: 'Minimum',
  limitMax: 'Maximum',
};

const QmulusDashboardWidgetGauge: Partial<TComponentKeys<'QmulusDashboardWidgetGauge'>> = {
  ...QmulusDashboardWidget,
  ...QmulusDashboardMixinData,
  ...QmulusDashboardMixinLimits,
  appearance: 'Visuelle Erscheinung',
  thresholds: 'Grenzwerte',
};

const QmulusDashboardWidgetGaugeAppearance: Record<TComponentValue<'QmulusDashboardWidgetGauge'>['appearance'], string> = {
  180: 'Rund - 180°',
  270: 'Rund - 270°',
  horizontal: 'Linear - Horizontal',
  vertical: 'Linear - Vertical',
};

const QmulusDashboardNamedLimit: Record<TComponentValue<'QmulusDashboardNamedLimit'>, string> = {
  standard_deviation: 'Mittelwert + b * Standardabweichung',
  lower_whisker: 'Q1 - 1.5 * IQR',
  upper_whisker: 'Q3 + 1.5 * IQR',
  q1: 'Unteres Quartil (Q1)',
  q3: 'Oberes Quartil (Q3)',
};

const QmulusDashboardThreshold: TComponentKeys<'QmulusDashboardThreshold'> = {
  value: 'Wert',
  color: generalTerm.color,
};

const QmulusDashboard: Partial<TComponentKeys<'QmulusDashboard'>> = {
  displayName: generalTerm.displayName,
};

const SimulatorRecipeSetpointType: Record<TComponentValue<'SimulatorRecipeSetpoint'>['simulatorSetpointMeaningId'], string> = {
  RUN_TIME: 'Laufzeit',
  TEMPERATURE: 'Temperatur',
  CARBURIZING_DEPTH_RELATIVE: 'Aufkohlungstiefe (relativ)',
  CARBURIZING_DEPTH_ABSOLUTE: 'Aufkohlungstiefe (absolut)',
  CARBON_POTENTIAL_CP: 'Kohlenstoffpotential CP',
  SURFACE_CARBON: PartMaterialProperties.surfaceCarbon,
  AUTO_BOOST_C: 'Auto C (100-x)',
  SOOT_LIMIT: 'Rußgrenze',
  NITROGEN_POTENTIAL_NP: 'Stickstoffpotential NP',
  NITRIDING_DEPTH_RELATIVE: 'Nitriertiefe (relativ)',
  NITRIDING_DEPTH_ABSOLUTE: 'Nitriertiefe (absolut)',
  NITRIDING_POTENTIAL_KN: 'Nitrierkennzahl kN',
  SURFACE_NITROGEN: 'Randstickstoff',
  AUTO_BOOST_N: 'Auto N (100-x)',
  CARBURIZING_POTENTIAL_KC_B: 'Kohlungskennzahl kC nach Bodouard',
  CARBURIZING_POTENTIAL_KC_W: 'Kohlungskennzahl kC über das het. Wassergas',
  TOTAL_GAS_FLOW: 'Volumenstrom (konstant)',
  TOTAL_GAS_FLOW_AUTO: 'Volumenstrom (Auto-Puls)',
  CO_CONTENT: generalTerm.carbonMonoxide,
  H2_CONTENT: generalTerm.hydrogen,
  N2_CONTENT: 'Nitrogen N₂',
  NH3_CONTENT: 'Ammonia NH₃',
  COX_CONTENT: 'COₓ',
  C2H2_CONTENT: 'Acetylene C₂H₂',
  C3H8_CONTENT: 'Propane C₃H₈',
  WAIT_RELATIVE: 'Wait',
};

const SimulatorRecipeStep: TComponentKeys<'SimulatorRecipeStepMutation'> = {
  stageId: generalTerm.recipeStage,
  displayName: generalTerm.displayName,
  setpoints: generalTerm.setpoint,
};

const SimulatorRecipeSetpoint: TComponentKeys<'SimulatorRecipeSetpoint'> = {
  simulatorSetpointMeaningId: generalTerm.setpoint,
  unitOfMeasurementId: generalTerm.unit,
  control: 'Sollwertsteuerung',
  endControl: 'Abbruchbedingung',
};

const PT9800RecipeSimulationInput: TComponentKeys<'PT9800RecipeSimulationInput'> = {
  recipe: generalTerm.recipe,
  recipeMetadata: '',
  intervalSeconds: '',
  dataSourceId: generalTerm.dataSourceId,
  startTemperature: 'Starttemperatur',
  heatingGradient: 'Heizgradient',
  coolingGradient: 'Abkühlgradient',
  assetFactor: 'Ofenfaktor',
  coContent: 'CO',
  h2Content: 'H2',
};

const CCTPhaseBoundaryType: Record<TComponentValue<'CCTBoundaryType'>, string> = {
  PEARLITE: 'Perlit',
  FERRITE: 'Ferrit',
  BAINITE: 'Bainit',
  CARBIDE: 'Karbid',
};

const JobConsumption: TComponentKeys<'JobConsumption'> = {
  dataId: 'Verbrauchsmaterial',
  meaningId: 'Bedeutung',
  value: 'Menge',
  unitOfMeasureId: generalTerm.unit,
};

const JobValueComparison: TComponentKeys<'JobValueComparison'> = {
  dataId: DataFormat.job_aggregate,
  jobId: generalTerm.job,
  value: 'Wert',
  comparison: 'Vergleich',
};

const JobValueComparisonPeriod: TComponentKeys<'JobValueComparisonPeriod'> = {
  previous30D: 'Vergleich zu vorhergegangen 30 Tagen',
  previous90D: 'Vergleich zu vorhergegangen 90 Tagen',
  previousYear: 'Vergleich zu vorhergegangen Jahr',
};

const JobValueBaselineMetrics: TComponentKeys<'JobValueBaselineMetrics'> = {
  isOutlier: 'Statistischer Ausreißer',
  delta: 'Absolute Änderung',
  deltaPercent: 'Relative Änderung',
  standardDeviation: 'Standard Abweichung',
  baseline: 'Mittelwert',
  earliestDateUtc: 'Startzeit des Zeitraums',
  latestDateUtc: 'Ende des Zeitraums',
};

const AuditTrailSettings: TComponentKeys<'AuditTrailSettings'> = {
  isEnabled: 'Aktiviert',
  retentionPeriodDays: 'Aufbewahrungszeitraum',
};

const AuditTrailEvent: TComponentKeys<'AuditTrailEvent'> = {
  timestampUtc: 'Zeitstempel',
  userId: 'Benutzer',
  userDisplayName: 'Benutzer',
  endpointMethod: 'Methode',
  endpoint: 'Endpoint',
  params: 'Parameter',
  appKey: 'API',
  returnStatus: 'Status',
};

const AuditSpecificationResultDetail: Partial<TComponentKeys<'AuditSpecificationResultDetail'>> = {
  auditorRemark: 'Bemerkung des Prüfers',
};

const Order: TComponentKeys<'Order'> = {
  orderId: 'Auftrag',
  createdAtUtc: generalTerm.createdAt,
  createdBy: generalTerm.createdBy,
  customerDisplayName: Customer.displayName,
  customerId: Customer.customerId,
  dataSourceId: generalTerm.dataSourceId,
  dataSourceReference: generalTerm.dataSourceReference,
  partDisplayName: Part.displayName,
  partId: Part.partId,
  revisedAtUtc: generalTerm.lastModifiedAt,
  revisedBy: generalTerm.lastModifiedBy,
  revisionId: generalTerm.revision,
  partRevisionId: 'Teilerevision',
  attributes: 'Auftragsattribute',
  status: 'Status',
  target: 'Ziel',
};

const OperationOrder: Partial<TComponentKeys<'OperationOrder'>> = {
  displayName: Order.orderId,
  customerId: generalTerm.customer,
};

const PartInstance: TComponentKeys<'PartInstance'> = {
  partInstanceId: Part.displayName,
  displayName: Part.displayName,
  partId: Part.partId,
  jobId: Job.jobId,
  jobDisplayName: Job.displayName,
  orderId: Order.orderId,
  orderDisplayName: Order.orderId,
  partCount: 'Menge',
  partRevisionId: Part.partRevisionId,
};

const OrderAttribute: TComponentKeys<'OrderAttribute'> = {
  createdAtUtc: generalTerm.createdAt,
  createdBy: generalTerm.createdBy,
  displayName: generalTerm.displayName,
  isBuiltIn: 'Integriert',
  isRequired: 'Erforderlich',
  isVisible: Metadata.isHidden!,
  orderAttributeId: 'Attribut-ID',
  physicalDimension: Meaning.physicalDimensionId!,
  revisedAtUtc: generalTerm.lastModifiedAt,
  revisedBy: generalTerm.lastModifiedBy,
  revisionId: generalTerm.revision,
  unitOfMeasureId: generalTerm.unit,
  valueType: 'Typ',
};

const OrderAttributeNumber: TComponentKeys<'OrderAttributeNumber'> = {
  ...OrderAttribute,
  minValue: 'Mindestwert',
  maxValue: 'Maximalwert',
  isMaxExclusive: 'Exklusives Maximum',
  isMinExclusive: 'Exklusives Minimum',
};

const OrderAttributeString: TComponentKeys<'OrderAttributeString'> = {
  ...OrderAttribute,
  minLength: 'Mindestlänge',
  maxLength: 'Maximale Länge',
  pattern: 'Muster',
};

const OrderAttributeValueType: Record<NonNullable<TComponentValue<'OrderAttribute'>['valueType']>, string> = {
  'string': OpenAPIPrimitiveType.string,
  'integer': OpenAPIPrimitiveType.integer,
  'number': OpenAPIPrimitiveType.number,
  'boolean': OpenAPIPrimitiveType.boolean,
  'date-time': 'Zeitstempel',
};

const BuiltInOrderAttribute: Record<TComponentValue<'BuiltInOrderAttribute'>, string> = {
  DUE_DATE: 'Fälligkeitsdatum',
  PRIORITY: 'Priorität',
  REMARK: 'Bemerkung',
  START_NOT_BEFORE: 'Frühestmöglicher Start',
  WORK_ORDER_NUMBER: 'Auftrags-Nr.',
};

const OperationCalendarStateStart: TComponentKeys<'OperationCalendarStateStart'> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  calendarEventType: OperationEventDetails.calendarEventType,
  startAtUtc: OperationEventDetails.startAtUtc,
  calendarEventTagId: CalendarEventTag.displayName,
};

const OperationCalendarStateFinish: TComponentKeys<'OperationCalendarStateFinish'> = {
  displayName: generalTerm.displayName,
  description: generalTerm.description,
  finishedAtUtc: OperationEventDetails.endAtUtc,
};

const ScaleOptions: Record<EScaleOption, string> = {
  [EScaleOption.FIT_SCREEN]: 'An Bildschirm anpassen',
  [EScaleOption.FULL_SIZE]: '100%',
};

export default {
  AlarmCollection,
  AlarmDefinition,
  AlarmEvent,
  AlarmEventSeverity,
  AlarmSeverity,
  AlarmStatistics,
  AppGroupCommercial: NonTranslatable.AppGroupCommercial,
  AppGroupSemanticDepartment,
  AppGroupSemanticTopic,
  AppId,
  AssessmentTestResultType,
  Asset,
  AssetBlobIndex,
  AssetCreate,
  AssetHierarchyLevel,
  AssetParameter,
  AssetParameterDefinition,
  AssetProfileCombinator,
  AssetProfileStatus,
  AssetState,
  AssetStatusJobInfo,
  AssetStatusOverview,
  AssetUtilization,
  AuditEnvelopStabilizationCombinationType,
  AuditEnvelope,
  AuditEnvelopeNonPassedReasons,
  AuditResultType,
  AuditSpecification,
  AuditSpecificationResultDetail,
  AuditSpecificationResultOverview,
  AuditTrailEvent,
  AuditTrailSettings,
  AutomatIdentifier,
  BlobMeaning,
  BuiltInOrderAttribute,
  CCTPhaseBoundaryType,
  CalendarEventType,
  CameraRegistryEntry,
  CameraRegistryEntryCameraType,
  CarburizingRecipeSimulationValuesType,
  CarburizingSimulationPartShape,
  CarburizingSimulationPartShapeSize,
  CarburizingSimulatorRecipeType,
  Chamber,
  ChamberCreate,
  ChamberType,
  ChartAssessmentTest,
  ChartAssessmentTestRequirementType,
  ChemicalElementInSteel,
  CompanyCreate,
  CounterAction,
  CsvDatetimeFormat,
  CsvLineTerminator,
  CsvQuoteCharacter,
  CsvQuoteStyle,
  CsvQuoteStyleHint,
  CsvSeperator,
  Customer,
  DataFormat,
  DataSource,
  DataSourceAssociation,
  DataSourceType,
  EnvelopeThreshold,
  EquipmentCreate,
  EquipmentPhysicalType,
  EquipmentProfile,
  EquipmentProfileMeaning,
  EquipmentTreeNavigation,
  ExpressionFunctionTypeAggregation,
  ExpressionNamedConstant,
  ExpressionOperatorType,
  ExpressionType,
  ExpressionUnaryOperatorType,
  FileAssessmentTest,
  HandlingState,
  HeatTreatmentCapability,
  HeatTreatmentProcessType,
  Job,
  JobStage,
  JobStatus,
  JobValueBaselineMetrics,
  JobConsumption,
  JobValueComparison,
  JobValueComparisonPeriod,
  JobValueData,
  LocationCreate,
  LogFileState,
  MaintenanceEventDetails,
  MaintenanceEventOverview,
  MaintenanceEventType,
  MaintenancePeriod,
  MaintenancePriority,
  MaintenanceRecurrenceEndCondition,
  MaintenanceRecurrenceMonthDayDimension,
  MaintenanceRecurrenceMonthDayValue,
  MaintenanceSeriesCreate,
  MaintenanceState,
  MaintenanceStateType,
  MaintenanceTaskResultType,
  MaintenanceTaskReviewType,
  Material,
  MaterialCalculationModelElementCoefficient,
  MaterialCalculationModelOutput,
  MaterialGroup,
  MaterialNamingSystem,
  MaterialPropertySpectrum,
  Meaning,
  Metadata,
  MetadataUpdate,
  MetadataWithChartConfigInput,
  ModelBuilderModelOverview,
  ModelBuilderModelType,
  ModelBuilderTrainingRequest,
  ModelLifecycleState,
  NotificationChannelType,
  NotificationTable,
  NpmSystemIdentifier,
  Observation,
  ObservationType,
  ObservationTypeHeader,
  OpenAPIPrimitiveType,
  OperationCalendarStateFinish,
  OperationCalendarStateStart,
  CalendarEventTag,
  OperationEventDetails,
  OperationEventOverview,
  Order,
  OrderAttribute,
  OrderAttributeNumber,
  OrderAttributeString,
  OrderAttributeValueType,
  OrderStatus,
  PT9800RecipeSimulationInput,
  Part,
  PartAssetRecipeQualification,
  PartAttribute,
  PartDetails,
  PartInstance,
  OperationOrder,
  PartMaterialProperties,
  PhysicalDimension,
  ProductionStatistics,
  PtDatabaseIdentifier,
  QmulusDashboard,
  QmulusDashboardMixinData,
  QmulusDashboardMixinLimits,
  QmulusDashboardNamedLimit,
  QmulusDashboardThreshold,
  QmulusDashboardWidget,
  QmulusDashboardWidgetGauge,
  QmulusDashboardWidgetGaugeAppearance,
  QmulusDashboardWidgetObservations,
  QmulusDashboardWidgetObservationsVariant,
  QmulusDashboardWidgetType,
  QmulusDriver,
  QmulusLicenseViolationReason,
  QualityPreset,
  QuenchingMedia,
  ReportBlockAlarmsTemplateVariable,
  ReportBlockAuditEnvelopesTemplateVariable,
  ReportBlockAuditReportHeaderTemplateVariable,
  ReportBlockJobDetailsHeaderTemplateVariable,
  ReportBlockId,
  ReportBlockJobPartsField,
  ReportBlockJobPartsTemplateVariable,
  ReportBlockJobStagesTemplateVariable,
  ReportBlockQualityReportHeaderTemplateVariable,
  ReportBlockQualitySamplesTemplateVariable,
  ReportBlockRecipeTemplateVariable,
  ReportBlockSignaturesTemplateVariable,
  ReportComponentAuditEnvelopeParameter,
  ReportComponentAuditEnvelopeSectionsParameter,
  ReportComponentAuditResultParameter,
  ReportComponentFormatQuantityParameter,
  ReportComponentFormatValueParameter,
  ReportComponentIdType,
  ReportComponentIdTypeDescription,
  ReportComponentKeyValueGridParameter,
  ReportComponentKeyValuePairParameter,
  ReportComponentProthermBatchRecipeParameter,
  ReportComponentProthermBeltRecipeParameter,
  ReportComponentQualityChartTestParameter,
  ReportComponentQualityFileTestParameter,
  ReportComponentQualityImageTestParameter,
  ReportComponentQualitySampleParameter,
  ReportComponentQualityTableTestParameter,
  ReportComponentQualityTestParameter,
  ReportComponentQualityTestResultParameter,
  ReportComponentTableParameter,
  ReportComponentWarningParameter,
  ReportOverview,
  ReportScopeType,
  ReportTemplateIdType,
  ReportTemplateKind,
  ReportTemplateFooter,
  ReportTemplateHeader,
  ReportTemplateUpsert,
  Role,
  RoleWithPermissions,
  RootCause,
  RootCauseGroup,
  Sample,
  ScaleType,
  ScaleOptions,
  SimulationStep,
  SimulatorAMS275910CompoundLayerClass,
  SimulatorAMS275910SteelClass,
  SimulatorAMS275912PorosityClass,
  SimulatorAMS275912SteelClass,
  SimulatorPreTreatmentType,
  SimulatorRecipe,
  SimulatorRecipeSetpoint,
  SimulatorRecipeSetpointType,
  SimulatorRecipeStep,
  SimulatorSimulationPart,
  SparePart,
  SubscriptionTreeNode,
  TableAssessmentTest,
  TableAssessmentTestItem,
  TableTestData,
  TaskInstance,
  TimeBucketType,
  TimeBucket_AssetUtilization_,
  UnitOfMeasurementDisplayName,
  UnitOfMeasurementSymbol,
  User,
  WorkplaceIdentifier,
  expressionFunctionCategories,
  expressionFunctionsExplanations,

  util: {
    timeBucketName: 'Zeitraum',
  },

  meaningId: {
    ...TimeseriesMeaning,
    ...ConsumptionMeaning,
    ...JobFeatureMeaning,
    ...PhysicalPropertyMeaning,
  },
  npm: npmApiTranslations,
  pt9800: pt9800ApiTranslations,
};
